import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import { ApiEndPoint } from '@/services/apiUttiLities';
import { computeScheduledActionSlaFromMeta, formatElapsedTime, parseTimestamp, resolveAssigneeDisplayName, resolvePlanOwnerDisplayName } from '@/uitilites/utility';
import { JwtService } from '@/services/jwtapi';
import { Bell, Building, Users, CheckCircle, ChevronDown, ClipboardList, Clock, Eye, FileText, Info, Lightbulb, Link2, MapPin, Package, Paperclip, Pencil, Plus, Search, Target, Upload, Video, AlertCircle, Check, X as XIcon, Calendar, Trash2, HandHelping, Zap, UserCircle2 } from 'lucide-react';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isWebAdmin } from '@/auth/webAccess';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import AuthenticatedMedia, { downloadAuthenticatedMedia } from './AuthenticatedMedia';
import { openAssignPersonnel } from './AssignPersonnelModal';
import CardDiscussion from './CardDiscussion';
import SectionTooltip from './SectionTooltip';
import ResourceFormModal from './ResourceFormModal';
import IncidentReadinessLog from './IncidentReadinessLog';
import RoleAssignmentPanel, { cardIsFullyAssigned, cardHasRoles } from './RoleAssignmentPanel';
import { cleanRoles as cleanScenarioRoles } from './ScenarioRoleField';
import { conditionalChipText } from './conditionalInject';
import {
  TIMELINE_FOCUS_EVENT,
  TIMELINE_RESPONDER_ACTIVITY_EVENT,
  normalizeFocusGroupKey,
  queueLatestActivity,
  responderActivityFromSync,
  timelineFocusTop,
} from './timelineFocusController';
import {
  AVAILABILITY as RESOURCE_AVAILABILITY, RESOURCE_GROUP, num as resourceNum, money as resourceMoney,
  unitLabel as resourceUnitLabel, blankResource, resourceToTimelinePayload, buildResourceFullItem, timelineItemToResource,
} from './resourceInject';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SAUDI_RIYAL_MARK = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB0AAAAgCAYAAADud3N8AAAEnElEQVRIDe1WWUxcVRj+ZoNhGRgoDDt0gOks1LIkLdKHWgypD31pXFJi6vJgfFFj4oOJibUxaqxRU21i2getlZemraAvWtsYExObWm1dSmFgYBgYkAJShp2BWfD/z3Cv3BmWGUh88p9k7rnnP+f/zr9996iWSfAfi3oreKP3x7ayTd6TEGgwGMR7n32AUy2nZQNbGcQN6l9axJtn3sbZC2cRIvBo+aX9Fr776Vr09JrvcYPe6W5H29U2pCSnQKVSKYx98+MVPPf68+jq61LMr/cSN2ggFIBGo4mxwx4e//gE/P4F6HRJMfq1JuIGVUHpnWTMPdiHef/cmgeS1kQ/4waN3ii9a9QaqFWJmUlstYS0zef/oNsM4MbbtRurE9NG9y/vnpjywfOXB1Mz02jc95Do8W2Dald6lwH9i370DXmIJLrR0dsJp9uJgeEBDI8No6mhCQ/XHxRexA262gu1Wo3wchij42ME0g9+12l1aCXGuvTtJczMzQi91E7M2Q01DwpA/osbdJ4YhyWJWOd35x946tVn4b3nJYBZMce6uflZhMIhHgrhw2QajKgprsXBFS9ZoVrre8rkziHp6e9Fh5vD1AW3txc+yg97HA6HEQwFxZiJgelxmX46bRIKcvNRUVqJqko77BV2lJeYYco2EYH8y2gKTxcJ7OSn7+P23dsUulHMLswRQAgalUYYlrg3AqCDaUcetFothkeHsLi0hObDzXj52IvQJ+sjrq7zryCHdtddXKSceAY9xKfzwMqlQq1RQ6+PGAoEA6i2VeP8yXO4/NEFPHPkaQHIF5CM9IwYQLbT5enGjT9vkrnIJUXhKSsWKHc5WTnIyzFRmCrgqHBgt6UK475xvHH6BEKhEIpMRai27onxIxAIYIQi5BroQWevU1Sv2+umCvbiwN4DaKiuF3sUoFbzLpx67UM4Kh0ozitCqj5FNnzzzq/ySVcXi7SAQ9p27St8ebWVcj8hcs755mLiVtq3Z6+0VFm9h/Y3yYqNBlxI0cIFNjk9QdUblg/Hc6n6VOwyW+Ue5X0KT6MNSe98Ug4bCxdRXVWtpBIgrGdhb3OpuMxFO2Gn6q2iiFmokgvzCpG86gMfA8pejPn+pmLqRye1CzML54VBuU0aavfjsUOPyqD5O0w4evgo6hy1sJFHJQUlMBoyZf1aA7lPvfcGca71PBWBC0MjQxSqSXClSnnhwxjSDfji3c/BuY9XAsRGI+Mj8E378IBlt5J7L165jJavW5CWkhYhAKI5SThsRVRYLx17YVNAJniuVidd0jp6OuDq74FnqA/1VLmfHI9cXUV4mRuv/3ZdEAA3e7YxG2WFZbCV20S7WMosKC0oRnJSsnQOxfMWkcn3P/8Al8clACcm74NZjTmICYVzXueok/cI0PnFBXGSJx55XLRLWWEpsjOz5EUbDdj4W2feob7sFIfidDAJSKTHHwLOc+Nm3LsRSLSOWaz5lScFH+t0OhgzjCjOL4Z1pxVVRCq2civKCkphSEuXt8ZUr6yJczA5M0VeNKLWUUPsZYeZCT4rV5DCeibk6l1vwWbz/F3lkCYi2wZNBExa+w+GP9aNUh2pmAAAAABJRU5ErkJggg==';

let lastLocationId;
// Cache of resolved attachment previews keyed by incident + attachment signature, so the report
// card's attachment thumbnails don't flicker/disappear on each timeline poll/re-render.
const reportPreviewCache = {};

// Shared, app-wide cache of locationId -> readable name. Populated once and reused by every card
// so the "Impacted Areas / Departments" chips never flicker (raw UUID → name) on each poll/render.
const locationNamesCache = {};
let locationNamesPromise = null;
const ensureLocationNames = async () => {
  if (Object.keys(locationNamesCache).length > 0) return locationNamesCache;
  if (!locationNamesPromise) {
    locationNamesPromise = (async () => {
      try {
        const response = await CommanServices.getlocationszonesdata();
        if (response?.status) {
          const allLocations = await response.data;
          const walk = (locs, parentPath = '') => {
            if (!Array.isArray(locs)) return;
            locs.forEach((loc) => {
              if (!loc || !loc.id) return;
              const displayName = loc.name || loc.zone_name || loc.label || 'Unknown';
              const currentPath = parentPath ? `${parentPath} > ${displayName}` : displayName;
              locationNamesCache[loc.id] = currentPath;
              if (loc.children?.length) walk(loc.children, currentPath);
              if (loc.subzones?.length) walk(loc.subzones, currentPath);
            });
          };
          walk(allLocations);
        }
      } catch (e) {
        // best-effort; chips fall back to a neutral label
      } finally {
        // allow a later retry if nothing resolved (e.g. transient failure)
        if (Object.keys(locationNamesCache).length === 0) locationNamesPromise = null;
      }
      return locationNamesCache;
    })();
  }
  return locationNamesPromise;
};

// Format a scenario role with the count rule: hide "1×" when only one is needed.
const formatRoleLabel = (r) => `${Number(r.count) > 1 ? `${r.count}× ` : ''}${r.role}`;

const normalizeTimelineSearch = (value) => String(value || '').trim().toLocaleLowerCase();
const TIMELINE_SEARCH_IGNORED_KEYS = new Set(['base64', 'blob', 'bytes', 'content', 'filedata', 'previewdata', 'thumbnail']);

// Timeline cards expose important labels inside actor, chips, assignments, recipients,
// roles and task metadata. Build the search text from the complete display model so a
// term the user can see is also a term the search box can find.
const timelineSearchText = (value) => {
  const parts = [];
  const seen = new WeakSet();

  const collect = (entry, depth = 0) => {
    if (entry === null || entry === undefined || depth > 6) return;
    if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
      parts.push(String(entry));
      return;
    }
    if (entry instanceof Date) {
      parts.push(entry.toISOString());
      return;
    }
    if (typeof entry !== 'object' || seen.has(entry)) return;
    seen.add(entry);

    if (Array.isArray(entry)) {
      entry.slice(0, 150).forEach((child) => collect(child, depth + 1));
      return;
    }

    Object.entries(entry).forEach(([key, child]) => {
      if (TIMELINE_SEARCH_IGNORED_KEYS.has(String(key).replace(/[_-]/g, '').toLocaleLowerCase())) return;
      collect(child, depth + 1);
    });
  };

  collect(value);
  return normalizeTimelineSearch(parts.join(' '));
};

const timelineItemMatchesSearch = (item, query, groupTitle = '') => {
  const normalizedQuery = normalizeTimelineSearch(query);
  if (!normalizedQuery) return true;
  const searchable = `${normalizeTimelineSearch(groupTitle)} ${timelineSearchText(item)}`;
  return normalizedQuery.split(/\s+/).every((token) => searchable.includes(token));
};

const normalizeTimelineGroupKey = (groupKey) => (
  groupKey === 'status_update_2'
    ? 'status_update'
    : groupKey === 'pre_incident_action'
      ? 'response_action'
      : String(groupKey || '')
);

// Pre-activation role chips (replaces the real assignee on inject cards while planning).
const planningStage = (incident) =>
  !!incident?.is_scenario || String(incident?.status || '').toLowerCase() === 'requested';

// The exercise's reference time is the activation moment (Report & Detection time). Every inject's
// fire time = activation + its "Fire at T+X" offset. activated_at is pause-adjusted by the backend.
const exerciseBaseTime = (incident) => {
  // Only exercises (and plan-managed cards, which present a virtual exercise
  // incident with activated_at injected) have an activation clock. A live card
  // on a real incident must count from its OWN notifiedAt — never from the
  // incident's creation time.
  const raw = incident?.activated_at || (incident?.is_exercise ? incident?.created_at : null);
  if (!raw) return null;
  try { const d = new Date(raw); return isNaN(d.getTime()) ? null : d; } catch (_) { return null; }
};
const cardFireTime = (item, incident) => {
  const base = exerciseBaseTime(incident);
  if (!base) return null;
  const offset = Number(item?.meta?.fireOffsetMinutes) || 0;
  return new Date(base.getTime() + offset * 60000);
};

const sourceConditionalWindow = (cond, source) => {
  const sourceMeta = source?.meta || {};
  const taskIndex = cond?.sourceTaskIndex;
  if (taskIndex !== undefined && taskIndex !== null && taskIndex !== '') {
    const task = (sourceMeta.tasks || [])[Number(taskIndex)] || {};
    const taskSla = typeof task.sla === 'object' ? task.sla?.withinMinutes : task.sla;
    if (taskSla !== undefined && taskSla !== null && taskSla !== '' && Number.isFinite(Number(taskSla))) {
      return Math.max(0, Number(taskSla));
    }
  }
  const sourceSla = sourceMeta?.sla?.withinMinutes;
  return sourceSla !== undefined && sourceSla !== null && sourceSla !== '' && Number.isFinite(Number(sourceSla))
    ? Math.max(0, Number(sourceSla))
    : 0;
};

// A 'timeout' conditional follow-up is suppressed (won't fire) when its source card — or the
// specific source task — was completed at/before the condition threshold. Mirrors the backend so
// the UI reflects it the instant the user checks the source task (before the next runtime sweep).
const conditionalSuppressed = (child, allItems, incident) => {
  if (child?.meta?.suppressed) return true;
  const cond = child?.meta?.conditional;
  if (!cond) return false;
  if ((cond.triggerType || 'timeout') !== 'timeout') return false;
  const src = (allItems || []).find((x) => x.id === cond.sourceItemId);
  if (!src) return false;
  const sm = src.meta || {};
  const fireT = cardFireTime(src, incident) || (sm.notifiedAt ? parseTimestamp(sm.notifiedAt) : null);
  const win = sourceConditionalWindow(cond, src);
  const threshold = fireT ? new Date(fireT.getTime() + Number(win) * 60000) : null;
  const taskDoneInTime = (task) => {
    if (!task) return false;
    if (!(task.isCompleted || task.completed || task.done)) return false;
    const cAt = task.completedAt ? parseTimestamp(task.completedAt) : null;
    if (cAt && threshold) return cAt <= threshold;
    return true;
  };
  const idx = cond.sourceTaskIndex;
  if (idx !== undefined && idx !== null && idx !== '') {
    return taskDoneInTime((sm.tasks || [])[Number(idx)]);
  }
  // Whole-card completion.
  let done = false; let cAt = null;
  if (src.group === 'acknowledgment') {
    done = ['Confirmed', 'False Alarm', 'Acknowledged', 'Verified'].includes(sm.verificationStatus);
    cAt = sm.verifiedAt ? parseTimestamp(sm.verifiedAt) : null;
  } else if (src.group === 'response_action') {
    if (sm.performedAt || sm.completedAt) { done = true; cAt = parseTimestamp(sm.performedAt || sm.completedAt); }
    else { const tasks = sm.tasks || []; done = tasks.length > 0 && tasks.every((t) => t.isCompleted || t.completed || t.done); }
  } else {
    return false;
  }
  if (!done) return false;
  if (cAt && threshold) return cAt <= threshold;
  return true;
};

// Personnel assigned to this card's roles in the Requested stage, grouped as "Role: name1, name2".
const cardAssignedByRole = (item) => {
  const ra = item?.meta?.roleAssignments;
  if (!ra || typeof ra !== 'object') return [];
  const out = [];
  Object.entries(ra).forEach(([role, list]) => {
    const names = (Array.isArray(list) ? list : []).map((a) => a?.name).filter(Boolean);
    if (names.length) out.push({ role, names });
  });
  return out;
};
const cardAssignedFlatNames = (item) => {
  const seen = new Set(); const names = [];
  cardAssignedByRole(item).forEach((g) => g.names.forEach((n) => { if (!seen.has(n)) { seen.add(n); names.push(n); } }));
  return names;
};

// Shows a live countdown to a card's fire time ("Fires in …"); once the fire time passes it switches
// to the actual "Notified at" time. Freezes while the exercise is paused.
const FireTimeCountdown = ({ item, incident, paused, pausedAt }) => {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (paused) return; // frozen while paused
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, [paused]);
  // Real live cards have no exercise clock — show their own send time instead.
  const ft = cardFireTime(item, incident) || (item?.meta?.notifiedAt ? parseTimestamp(item.meta.notifiedAt) : null);
  if (!ft || isNaN(ft.getTime?.() ? ft.getTime() : NaN)) return null;
  const fmt = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  const fmtDate = (d) => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  let now;
  try { now = (paused && pausedAt) ? new Date(pausedAt) : new Date(); } catch (_) { now = new Date(); }
  const diffMs = ft.getTime() - now.getTime();
  if (diffMs > 0) {
    const totalSec = Math.ceil(diffMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const label = h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
    return (
      <div className="text-xs font-mono text-amber-600 font-semibold mb-1 flex items-center gap-1" data-testid={`fire-countdown-${item.id}`}>
        <Clock className="h-3 w-3" /> Fires in {label} <span className="text-gray-400 font-normal">(at {fmt(ft)})</span>
        {paused && <span className="text-amber-700">· paused</span>}
      </div>
    );
  }
  return (
    <div className="text-xs font-mono text-gray-500 mb-1" data-testid={`fired-at-${item.id}`}>
      Notified at: {fmt(ft)} · {fmtDate(ft)}
    </div>
  );
};

// Acknowledgment status for Notification cards with a live SLA countdown.
// - Not yet fired: dormant pill.
// - Fired, pending: live "SLA in Xm Ys" countdown → turns red "Overdue (late Xm)" past the SLA.
// - Acknowledged: green "ACK (within …)" if on time, red "ACK (late Xm)" if past the SLA.
const NotificationAckStatus = ({ item, incident, simActivated, paused, pausedAt }) => {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (paused) return;
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, [paused]);

  // Number(null) === 0, so guard explicitly: null/undefined/'' = no SLA entered.
  const rawSlaMinutes = item?.meta?.sla?.withinMinutes;
  const slaMinutes = Number(rawSlaMinutes);
  const hasSla = rawSlaMinutes != null && rawSlaMinutes !== '' && Number.isFinite(slaMinutes);
  const base = cardFireTime(item, incident)
    || (item?.meta?.notifiedAt ? parseTimestamp(item.meta.notifiedAt) : parseTimestamp(incident.created_at));

  // Acknowledged
  if (item?.meta?.acknowledgedAt) {
    const ackAt = parseTimestamp(item.meta.acknowledgedAt);
    const elapsedMs = ackAt - base;
    const elapsedMin = elapsedMs / 60000;
    if (hasSla && elapsedMin > slaMinutes) {
      const lateMin = Math.max(1, Math.round(elapsedMin - slaMinutes));
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-testid={`notif-ack-${item.id}`}>
          🔴 ACK (late {lateMin}m)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-testid={`notif-ack-${item.id}`}>
        🟢 ACK (within {formatElapsedTime(Math.max(0, elapsedMs))})
      </span>
    );
  }

  // Pre-activation: dormant
  if (!simActivated) {
    const off = item?.meta?.fireOffsetMinutes;
    const hasOff = Number.isFinite(Number(off));
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200" data-testid={`notif-ack-${item.id}`}>
        ⏳ Starts on activation{hasOff ? ` · fires T+${off}m` : ''}{hasSla ? ` · SLA ${slaMinutes}m` : ''}
      </span>
    );
  }

  // Activated, pending
  let now;
  try { now = (paused && pausedAt) ? new Date(pausedAt) : new Date(); } catch (_) { now = new Date(); }

  // Not fired yet → SLA clock hasn't started.
  if (base && now < base) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-testid={`notif-ack-${item.id}`}>
        🟡 Pending
      </span>
    );
  }

  if (!hasSla) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-testid={`notif-ack-${item.id}`}>
        🟡 Pending
      </span>
    );
  }

  const deadline = new Date(base.getTime() + slaMinutes * 60000);
  const remainingMs = deadline.getTime() - now.getTime();
  if (remainingMs > 0) {
    const totalSec = Math.ceil(remainingMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 font-mono" data-testid={`notif-ack-${item.id}`}>
        🟡 Pending · SLA in {m}m {s}s{paused ? ' · paused' : ''}
      </span>
    );
  }
  const lateMin = Math.max(1, Math.ceil(Math.abs(remainingMs) / 60000));
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-testid={`notif-ack-${item.id}`}>
      🔴 Overdue (late {lateMin}m)
    </span>
  );
};

const CardAssignedPeople = ({ item, incident }) => {
  if (planningStage(incident)) return null; // requested/scenario use the assignment panel instead
  const groups = cardAssignedByRole(item);
  const srcGroups = item?.meta?.sourceGroups || (item?.meta?.sourceGroup ? [item.meta.sourceGroup] : []);
  if (!groups.length && !srcGroups.length) return null;
  return (
    <div className="mt-2" data-testid={`card-assigned-${item.id}`}>
      {srcGroups.length > 0 && (
        <div className="mb-1 flex flex-wrap items-center gap-2">
          {srcGroups.map((g, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700">
              {g.type === 'department' ? <Building className="h-3.5 w-3.5 text-blue-500" /> : <Users className="h-3.5 w-3.5 text-indigo-500" />}
              {g.name}
            </span>
          ))}
        </div>
      )}
      {groups.length > 0 && (
      <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Assigned:</span>
      {groups.map((g, gi) => (
        <span key={gi} className="inline-flex items-center gap-1 rounded-full bg-violet-100 border border-violet-200 px-2 py-0.5 text-[11px] font-medium text-violet-700">
          <UserCircle2 className="h-3 w-3" /> {g.role}: {g.names.join(', ')}
        </span>
      ))}
      </div>
      )}
    </div>
  );
};

const RoleChips = ({ roles, note = '' }) => {
  const clean = cleanScenarioRoles(roles);
  const [showNote, setShowNote] = React.useState(false);
  if (!clean.length) return null;
  return (
    <div className="space-y-1" data-testid="role-chips-inline">
      <div className="flex flex-wrap items-center gap-1.5">
        {clean.map((r, i) => (
          <button
            type="button"
            key={i}
            onClick={note ? (e) => { e.stopPropagation(); setShowNote((v) => !v); } : undefined}
            title={note ? 'Click to view assignment instructions' : undefined}
            className={`inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 px-2.5 py-0.5 text-xs font-semibold text-violet-700 ${note ? 'cursor-pointer hover:bg-violet-100' : 'cursor-default'}`}
          >
            {formatRoleLabel(r)}{note ? ' 📝' : ''}
          </button>
        ))}
      </div>
      {showNote && note && (
        <p className="text-[11px] text-violet-700 italic bg-violet-50 border border-violet-200 rounded-md px-2 py-1 inline-block" data-testid="role-note-popover">📝 {note}</p>
      )}
    </div>
  );
};

// Assignment instructions are surfaced by clicking the role chip (not shown inline on the timeline).
const ScenarioRoleChips = () => null;


const toDatetimeLocalValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Classify a notification card into a merge bucket: individual user / department / crew.
const notificationBucket = (item) => {
  const m = item?.meta || {};
  if ((m.crewIds && m.crewIds.length) || m.crewId || m.crew_id || m.crewName || m.crew_name) return 'crew';
  if ((m.departmentIds && m.departmentIds.length) || m.department_id || m.departmentId || m.department || (item.chips || []).includes('Department Alert')) return 'department';
  return 'user';
};
const NOTIFICATION_BUCKET_LABEL = { user: 'user', department: 'department', crew: 'crew' };

// Resolve the assignee/target name for a card (for "Take over" on-behalf actions).
const getCardAssigneeName = (item) => {
  const m = item?.meta || {};
  if (m.acknowledgedBy?.userName) return m.acknowledgedBy.userName;
  if (Array.isArray(m.assignedUsers) && m.assignedUsers.length) {
    return m.assignedUsers[0]?.userName || m.assignedUsers[0]?.name || `${m.assignedUsers.length} assignee(s)`;
  }
  if (m.notifiedUser?.userName) return m.notifiedUser.userName;
  if (item?.actor?.name && !['System', 'Scenario'].includes(item.actor.name)) return item.actor.name;
  return 'assigned user';
};

// Takeover button: replaces the old per-card hover "edit" pencil. Lets an admin
// act on behalf of the assigned user (opens the card's action modal) and records
// the takeover in the audit trail.
const TakeoverButton = ({ item, incident, onAct, className, canManage = false }) => {
  if (!canManage || incident?.is_scenario || !onAct || item?.isVirtual || item?.meta?.superseded) return null;
  const assignee = getCardAssigneeName(item);
  const handle = async () => {
    try {
      await CommanServices.addIncidentAuditLog(incident.id, {
        action: 'Takeover',
        detail: `Admin took over "${item.title || 'card'}" on behalf of ${assignee}`,
      });
      window.dispatchEvent(new Event('timelineRefresh'));
    } catch (e) { /* best-effort */ }
    onAct(item);
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      className={className || 'opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 hover:bg-violet-100 rounded'}
      onClick={handle}
      title={`Take over — do it for ${assignee}`}
      data-testid={`takeover-${item.id}`}
    >
      <HandHelping className="h-3.5 w-3.5 text-violet-600" />
    </Button>
  );
};

// Discuss/approve (+ optional takeover) anchored to the top-right corner of any card.
// Top-right "send reminder now" bell — REAL incidents only. Nudges the card's
// pending assignees with a REMINDER push + in-app notification.
const ReminderButton = ({ incident, item, canManage = false }) => {
  const [state, setState] = React.useState('idle'); // idle | sending | sent | none
  const handleRemind = async (e) => {
    e?.stopPropagation?.();
    if (!canManage || state === 'sending') return;
    setState('sending');
    try {
      const res = await CommanServices.remindTimelineCard(incident.id, item.id);
      setState(res?.status && res?.data?.reminded > 0 ? 'sent' : 'none');
    } catch (_) {
      setState('none');
    }
    setTimeout(() => setState('idle'), 2500);
  };
  if (!canManage) return null;
  return (
    <button
      onClick={handleRemind}
      title={state === 'sent' ? 'Reminder sent' : state === 'none' ? 'No pending assignees' : 'Send reminder to assignees'}
      data-testid={`remind-card-${item.id}`}
      className={`h-7 w-7 p-0 rounded flex items-center justify-center transition-colors ${
        state === 'sent' ? 'text-emerald-600 bg-emerald-50' : state === 'none' ? 'text-gray-300' : 'text-amber-500 hover:bg-amber-50'
      }`}
    >
      {state === 'sent' ? <Check className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
    </button>
  );
};

const CardCornerActions = ({ item, incident, currentUser, onTakeover, withTakeover = false, isAdmin = false }) => {
  if (incident?.__planVirtual) return null;
  // REAL incidents: the corner hosts the manual Reminder bell on every card,
  // plus the observation-capture icon (parity with drills) so admins can raise
  // corrective actions straight from the card that exposed the gap.
  if (!incident?.is_exercise && !incident?.is_scenario) {
    if (!item?.id || item?.isVirtual) return null;
    return (
      <div className="absolute top-3 right-3 flex items-center gap-1 z-0">
        {isAdmin && (
          <CardDiscussion incident={incident} itemId={item.id} currentUser={currentUser} alwaysVisible addedLive={false} />
        )}
        <ReminderButton incident={incident} item={item} canManage={isAdmin} />
      </div>
    );
  }
  if (!incident?.is_exercise) return null;
  // Exercise cards keep their exercise-only affordances (discussion/approve,
  // takeover, observation & corrective-action capture).
  return (
    <div className="absolute top-3 right-3 flex items-center gap-1 z-0">
      {withTakeover && <TakeoverButton item={item} incident={incident} onAct={onTakeover} canManage={isAdmin} className="h-7 w-7 p-0 hover:bg-violet-100 rounded" />}
      <CardDiscussion incident={incident} itemId={item.id} currentUser={currentUser} alwaysVisible addedLive={item?.meta?.addedDuringActive === true} />
    </div>
  );
};



// Per-card admin controls for Exercise drills at the "Requested" stage:
// set an absolute Fire time, edit, or delete the card. Defined at module scope so
// its local state (the fire-time input) is preserved across parent re-renders.
const leaderOwnedCardIsPending = (item, currentUser) => {
  const userId = String(currentUser?.id || currentUser?._id || '');
  const meta = item?.meta && typeof item.meta === 'object' ? item.meta : {};
  const group = String(item?.group || '').toLowerCase();
  if (!userId || !['notification', 'verification', 'acknowledgment', 'response_action', 'action'].includes(group)) return false;
  if (String(meta.createdByUserId || '') !== userId) return false;
  if (meta.fired === true || meta.push_sent === true) return false;
  if (['firedAt', 'notifiedAt', 'sentAt', 'dispatchedAt', 'push_sent_at'].some((field) => meta[field])) return false;
  if (String(item?.plan_state || '').toLowerCase() === 'planned') return true;
  if (meta.fired === false || meta.push_sent === false) return true;
  if (['fireOffsetMinutes', 'scheduledAt', 'fireAt'].some((field) => ![undefined, null, '', 0, '0'].includes(meta[field]))) return true;
  return false;
};

const ExerciseCardControls = ({ item, onEdit, incident, canEdit, creatorMode = false, onRefresh, allItems = [] }) => {
  if (!canEdit) return null;
  if (!item || item.isVirtual) return null;
  // On a real incident only the scenario-planned cards are editable injects;
  // live items (report, notifications already sent, manual cards) stay as-is.
  if (!creatorMode && !incident?.is_exercise && item?.plan_state !== 'planned') return null;
  // A fired card is live — its inject setup (fire time, role editing) is over.
  if (!creatorMode && item?.plan_state === 'active') return null;

  const [fireOffset, setFireOffset] = React.useState(
    item?.meta?.fireOffsetMinutes !== undefined && item?.meta?.fireOffsetMinutes !== null ? String(item.meta.fireOffsetMinutes) : ''
  );
  const [firing, setFiring] = React.useState(false);
  const firingRef = React.useRef(false);
  const [saving, setSaving] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);
  const [lockMsg, setLockMsg] = React.useState(false);
  const autoSavedRef = React.useRef('');

  // For a conditional follow-up, its clock can only start once the source's condition is met.
  // baseOffset = source's fire offset + the condition window (timeout/completed minutes).
  const cond = item?.meta?.conditional;
  const baseOffset = React.useMemo(() => {
    if (!cond) return 0;
    const src = (allItems || []).find((x) => x.id === cond.sourceItemId);
    const srcOffset = Number(src?.meta?.fireOffsetMinutes) || 0;
    const window = sourceConditionalWindow(cond, src);
    return Math.max(0, srcOffset + (Number.isFinite(window) ? window : 0));
  }, [cond, allItems]);

  React.useEffect(() => {
    setFireOffset(item?.meta?.fireOffsetMinutes !== undefined && item?.meta?.fireOffsetMinutes !== null ? String(item.meta.fireOffsetMinutes) : '');
  }, [item?.meta?.fireOffsetMinutes]);

  // Conditional cards: the fire time is auto-managed (= when the condition is met). Persist it
  // silently whenever the stored value drifts from the computed base.
  React.useEffect(() => {
    if (!cond || !canEdit) return;
    const stored = Number(item?.meta?.fireOffsetMinutes);
    const source = (allItems || []).find((x) => x.id === cond.sourceItemId);
    const inheritedWindow = sourceConditionalWindow(cond, source);
    const needsSync = !Number.isFinite(stored)
      || stored !== baseOffset
      || Number(cond.withinMinutes) !== inheritedWindow
      || cond.inheritsSourceSla !== true;
    const signature = `${baseOffset}:${inheritedWindow}`;
    if (needsSync && autoSavedRef.current !== signature) {
      autoSavedRef.current = signature;
      CommanServices.updateIncidentTimeline(incident.id, item.id, {
        meta: {
          fireOffsetMinutes: baseOffset,
          conditional: { ...cond, withinMinutes: inheritedWindow, inheritsSourceSla: true },
        },
      })
        .then((res) => { if (res?.status) { onRefresh && onRefresh(); window.dispatchEvent(new Event('timelineRefresh')); } })
        .catch(() => { autoSavedRef.current = ''; });
    }
  }, [cond, baseOffset, item?.meta?.fireOffsetMinutes, allItems, canEdit, incident.id, item.id, onRefresh]);

  const showLockReason = () => {
    setLockMsg(true);
    setTimeout(() => setLockMsg(false), 2600);
  };

  const handleSaveFireTime = async () => {
    if (fireOffset === '' || isNaN(Number(fireOffset))) return;
    setSaving(true);
    try {
      // Clamp conditional cards so they never fire before the condition is met.
      const requested = Math.max(0, parseInt(fireOffset, 10));
      const finalOffset = cond ? Math.max(baseOffset, requested) : requested;
      if (finalOffset !== requested) setFireOffset(String(finalOffset));
      const res = await CommanServices.updateIncidentTimeline(incident.id, item.id, { meta: { fireOffsetMinutes: finalOffset } });
      if (res.status) {
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 1500);
        onRefresh && onRefresh();
        window.dispatchEvent(new Event('timelineRefresh'));
      }
    } catch (e) {
      console.error('Failed to save fire offset', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // Cascade: collect all conditional follow-ups (recursively) that depend on this card.
    const collectDescendants = (rootId) => {
      const found = [];
      const stack = [rootId];
      while (stack.length) {
        const pid = stack.pop();
        (allItems || []).forEach((it) => {
          if (it?.meta?.conditional?.sourceItemId === pid && !found.includes(it.id)) {
            found.push(it.id);
            stack.push(it.id);
          }
        });
      }
      return found;
    };
    const childIds = collectDescendants(item.id);
    // Only warn when there are conditional follow-ups that will also be removed.
    if (childIds.length > 0) {
      const msg = `Delete this card and its ${childIds.length} conditional follow-up${childIds.length > 1 ? 's' : ''}? This cannot be undone.`;
      if (!window.confirm(msg)) return;
    }
    try {
      const res = await CommanServices.deleteTimelineItem(incident.id, item.id);
      for (const cid of childIds) {
        try { await CommanServices.deleteTimelineItem(incident.id, cid); } catch (e) { /* best effort */ }
      }
      if (res.status) {
        onRefresh && onRefresh();
        window.dispatchEvent(new Event('timelineRefresh'));
      }
    } catch (e) {
      console.error('Failed to delete timeline item', e);
    }
  };

  return (
    <React.Fragment>
    <div
      data-testid={`exercise-card-controls-${item.id}`}
      className="mt-3 rounded-md border border-dashed border-indigo-300 bg-indigo-50/60 px-2.5 py-2"
    >
      <div className="flex min-w-0 flex-nowrap items-center gap-1.5 whitespace-nowrap">
      {item?.plan_state === 'planned' ? (
        (() => {
          const roleAssignments = item?.meta?.roleAssignments || {};
          const missingRole = cleanScenarioRoles(item?.meta?.scenarioRoles).find((role) => {
            const assigned = Array.isArray(roleAssignments[role.role]) ? roleAssignments[role.role].length : 0;
            return assigned < role.count;
          });
          const staffed = !missingRole;
          return (
            <button
              type="button"
              data-testid={`fire-now-${item.id}`}
              disabled={firing}
              aria-busy={firing}
              title={staffed
                ? 'Fire this card now without activating the full response plan'
                : `Assign personnel to ${missingRole.role} before firing this card`}
              className={`inline-flex shrink-0 items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide transition-colors disabled:cursor-wait disabled:opacity-60 ${staffed ? 'bg-red-600 text-white hover:bg-red-700' : 'border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
              onClick={async () => {
                if (missingRole) {
                  openAssignPersonnel({ itemId: item.id, role: missingRole.role, variant: 'light', item });
                  return;
                }
                if (firingRef.current) return;
                firingRef.current = true;
                setFiring(true);
                try {
                  const incidentId = item.incident_id || incident?.id;
                  const response = await JwtService.commanApi({}, `${ApiEndPoint}/api/incidents/${incidentId}/timeline/${item.id}/fire-now`, "POST");
                  if (!response || response.status === false) {
                    toast.error(response?.message || 'Unable to fire this card. Refresh the timeline and try again.');
                    return;
                  }
                  toast.success('Card fired now without launching the full response plan.');
                  if (onRefresh) {
                    try {
                      await onRefresh();
                    } catch (refreshError) {
                      console.error('fire-now refresh failed', refreshError);
                    }
                  }
                  window.dispatchEvent(new CustomEvent('timelineRefresh', { detail: { incident_id: incidentId } }));
                } catch (err) {
                  console.error('fire-now failed', err);
                  toast.error(err?.message || 'Unable to fire this card. Refresh the timeline and try again.');
                } finally {
                  firingRef.current = false;
                  setFiring(false);
                }
              }}
            >
              {staffed ? <Zap className="h-3 w-3" /> : <Users className="h-3 w-3" />}
              {staffed ? (firing ? 'Firing…' : 'Fire now') : 'Assign to fire'}
            </button>
          );
        })()
      ) : (
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-indigo-600">Inject Setup</span>
      )}
      <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
        <Clock className="h-3.5 w-3.5 text-indigo-500" />
        <span className="text-xs font-medium text-gray-600">Fires at T+</span>
        <input
          type="number"
          min={cond ? baseOffset : 0}
          data-testid={`fire-offset-input-${item.id}`}
          value={cond ? String(baseOffset) : fireOffset}
          onChange={(e) => { if (!cond) setFireOffset(e.target.value); }}
          readOnly={!!cond}
          onMouseDown={cond ? (e) => { e.preventDefault(); showLockReason(); } : undefined}
          onFocus={cond ? showLockReason : undefined}
          placeholder={cond ? String(baseOffset) : 'min'}
          title={cond ? 'Set automatically to when the condition is met' : undefined}
          className={`h-7 w-14 shrink-0 rounded border px-1.5 text-xs focus:outline-none no-number-spinner ${cond ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300 focus:border-indigo-400'}`}
        />
        <span className="text-xs font-medium text-gray-600">min after activation</span>
        {!cond && (
          <Button
            size="sm"
            data-testid={`fire-time-save-${item.id}`}
            className="h-7 shrink-0 px-2 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
            disabled={fireOffset === '' || saving}
            onClick={handleSaveFireTime}
          >
            {justSaved ? 'Saved' : saving ? 'Saving…' : 'Set'}
          </Button>
        )}
      </div>
      {onEdit && (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`exercise-edit-${item.id}`}
          className="h-7 w-7 shrink-0 p-0 hover:bg-indigo-100 rounded"
          title="Edit card"
          onClick={() => onEdit(item)}
        >
          <Pencil className="h-3.5 w-3.5 text-indigo-600" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        data-testid={`exercise-delete-${item.id}`}
        className={`${incident?.__planVirtual ? 'absolute right-3 top-3 z-10' : 'ml-auto shrink-0'} h-7 w-7 p-0 hover:bg-red-100 rounded`}
        title="Delete card"
        onClick={handleDelete}
      >
        <Trash2 className="h-3.5 w-3.5 text-red-500" />
      </Button>
      </div>
      {cond && lockMsg && (
        <div className="mt-1 text-[10px] font-medium text-amber-700" data-testid={`conditional-lock-msg-${item.id}`}>
          ⚡ Auto-set to when the condition is met — it can't be changed.
        </div>
      )}
    </div>
    {cardHasRoles(item) && !incident?.is_scenario && (String(incident?.status || '').toLowerCase() === 'requested' || item?.plan_state === 'planned') && (
      <RoleAssignmentPanel item={item} incident={incident} onRefresh={onRefresh} allItems={allItems} />
    )}
    </React.Fragment>
  );
};

const INVALID_REPORT_LOCATION_LABELS = new Set([
  'unknown',
  'unknown location',
  'not specified',
  'n/a',
  'na',
  'null',
  'undefined',
]);

const normalizeReportLocationLabel = (value) => String(value ?? '').trim();

const isValidReportLocationLabel = (value) => {
  const label = normalizeReportLocationLabel(value);
  if (!label) return false;
  return !INVALID_REPORT_LOCATION_LABELS.has(label.toLowerCase());
};

const findReportLocationLabelById = (locationId, locationNodes = []) => {
  if (!locationId || locationId === 'unknown') return null;

  for (const node of locationNodes) {
    if (!node) continue;
    if (String(node.id) === String(locationId) || String(node._id) === String(locationId)) {
      const label = node.label || node.name || node.title;
      return isValidReportLocationLabel(label) ? normalizeReportLocationLabel(label) : null;
    }

    const nested = [
      ...(Array.isArray(node.children) ? node.children : []),
      ...(Array.isArray(node.subzones) ? node.subzones : []),
    ];
    if (nested.length) {
      const nestedMatch = findReportLocationLabelById(locationId, nested);
      if (nestedMatch) return nestedMatch;
    }
  }

  return null;
};

const resolveReportLocationEntry = (entry, locationNodes = []) => {
  if (entry == null || entry === 'unknown') return null;
  const resolvedById = findReportLocationLabelById(entry, locationNodes);
  if (resolvedById) return resolvedById;
  return isValidReportLocationLabel(entry) ? normalizeReportLocationLabel(entry) : null;
};

const collectReportSummaryLocations = (incident, itemMeta = {}, locationNodes = []) => {
  const results = [];
  const seen = new Set();

  const addLocation = (value) => {
    const label = typeof value === 'string'
      ? (isValidReportLocationLabel(value) ? normalizeReportLocationLabel(value) : null)
      : resolveReportLocationEntry(value, locationNodes);
    if (!label) return;

    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    results.push(label);
  };

  const addFromEntries = (entries) => {
    (Array.isArray(entries) ? entries : entries ? [entries] : []).forEach((entry) => {
      addLocation(entry);
    });
  };

  if (incident?.is_unknown_location) {
    addFromEntries(incident?.location_names);
    return results;
  }

  addFromEntries(incident?.location_names);
  if (results.length) return results;

  addFromEntries(itemMeta?.locationPath);
  if (results.length) return results;

  addFromEntries(incident?.location);
  return results;
};

const EscalationCountdown = ({ targetTime }) => {
  const [countdown, setCountdown] = React.useState('');

  React.useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const target = new Date(targetTime);
      const remainingMs = target - now;

      if (remainingMs <= 0) {
        setCountdown('now');
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // Format: "in Xh Ym Zs" or "in Ym Zs" or "in Zs"
      let parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);

      setCountdown(`in ${parts.join(' ')}`);
    };

    updateCountdown(); // Initial update
    const interval = setInterval(updateCountdown, 1000); // Update every second

    return () => clearInterval(interval);
  }, [targetTime]);

  return <span>{countdown}</span>;
};

const computeTasksWithState = (tasks, notifiedAtStr, currentTime) => {
  const sortedTasks = [...tasks].sort((a, b) => (a.order || 0) - (b.order || 0));
  let currentStart = notifiedAtStr ? parseTimestamp(notifiedAtStr) : new Date();
  if (!currentStart) currentStart = new Date();

  let foundActive = false;

  return sortedTasks.map((task) => {
    const durationMs = (Number(task.timerMinutes) || 5) * 60 * 1000;
    const deadline = new Date(currentStart.getTime() + durationMs);

    let state = 'queued'; // 'completed', 'active', 'failed', 'queued'
    let remainingMs = deadline.getTime() - currentTime.getTime();

    if (task.isCompleted) {
      state = 'completed';
      currentStart = task.completedAt ? parseTimestamp(task.completedAt) : deadline;
      if (!currentStart) currentStart = deadline;
    } else {
      if (currentTime.getTime() > deadline.getTime()) {
        state = 'failed';
        currentStart = deadline;
      } else {
        if (!foundActive) {
          state = 'active';
          foundActive = true;
        } else {
          state = 'queued';
        }
        currentStart = deadline;
      }
    }

    return {
      ...task,
      state,
      deadline,
      remainingMs,
    };
  });
};

const StatusUpdateReplies = ({ incidentId, timelineItemId, replies, readOnly = false }) => {
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [replyTo, setReplyTo] = React.useState(null); // { id, userName }
  const [localReplies, setLocalReplies] = React.useState(() => (Array.isArray(replies) ? replies : []));

  React.useEffect(() => {
    setLocalReplies(Array.isArray(replies) ? replies : []);
  }, [replies]);

  const getCurrentUser = () => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const handleSend = async () => {
    const trimmed = String(message || '').trim();
    if (readOnly || !trimmed || sending) return;

    const user = getCurrentUser();
    const reply = {
      id: `reply-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      userId: user?.id || user?.user_id || 'unknown',
      userName: user?.full_name || user?.name || user?.user_account || user?.email || 'User',
      userRole: user?.role || user?.access_level || user?.user_type || '',
      parentReplyId: replyTo?.id || null,
      replyToName: replyTo?.userName || '',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };

    const nextReplies = [...(Array.isArray(localReplies) ? localReplies : []), reply];
    setLocalReplies(nextReplies);
    setMessage('');
    setReplyTo(null);
    setSending(true);

    try {
      const res = await CommanServices.updateIncidentTimeline(incidentId, timelineItemId, {
        meta: { replies: nextReplies },
      });
      if (!res?.status) {
        throw new Error(res?.message || 'Failed to send reply');
      }
      // Avoid full timeline refetch for the sender; realtime will update others.
      window.__lastStatusUpdateReplySent = {
        incidentId: String(incidentId),
        timelineItemId: String(timelineItemId),
        ts: Date.now(),
      };
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const normalizedReplies = Array.isArray(localReplies) ? localReplies : [];
  const replyChildrenByParent = normalizedReplies.reduce((acc, r) => {
    const key = r?.parentReplyId ? String(r.parentReplyId) : '__root__';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const sortReplies = (arr) =>
    [...arr].sort((a, b) => new Date(a?.timestamp || 0).getTime() - new Date(b?.timestamp || 0).getTime());

  const renderThread = (parentId, depth = 0) => {
    const list = sortReplies(replyChildrenByParent[parentId] || []);
    return list.map((r, idx) => (
      <div
        key={r?.id || `${parentId}-${idx}`}
        className={`bg-white border border-slate-200 rounded-md p-2.5 ${depth > 0 ? 'ml-6' : ''}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-slate-700">
            <span className="font-semibold">{r?.userName || 'User'}</span>
            {r?.userRole ? <span className="text-slate-400"> • {r.userRole}</span> : null}
            {r?.replyToName ? <span className="text-slate-400"> • replying to {r.replyToName}</span> : null}
          </div>
          <div className="text-[11px] text-slate-400">
            {r?.timestamp ? new Date(r.timestamp).toLocaleString() : ''}
          </div>
        </div>
        <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{r?.content || ''}</div>
        {!readOnly && <div className="mt-1.5">
          <button
            type="button"
            className="text-xs text-blue-600 hover:text-blue-800"
            onClick={() => setReplyTo({ id: r?.id, userName: r?.userName || 'User' })}
          >
            Reply
          </button>
        </div>}
        {renderThread(String(r?.id), depth + 1)}
      </div>
    ));
  };

  const replyScrollRef = React.useRef(null);

  React.useEffect(() => {
    if (replyScrollRef.current) {
      replyScrollRef.current.scrollTop = replyScrollRef.current.scrollHeight;
    }
  }, [localReplies.length]);

  return (
    <div className="p-4 border-b border-gray-100 bg-slate-50">
      <div className="text-xs font-semibold text-slate-700 mb-2">Replies</div>

      {normalizedReplies.length > 0 ? (
        <div
          ref={replyScrollRef}
          className="space-y-2 max-h-56 overflow-y-auto pr-1"
        >
          {renderThread('__root__', 0)}
        </div>
      ) : (
        <div className="text-xs text-slate-500">No replies yet.</div>
      )}

      {!readOnly ? <>
      <div className="mt-3 flex items-center gap-2">
        {replyTo ? (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="px-2 py-1 rounded bg-slate-200">Replying to {replyTo.userName}</span>
            <button
              type="button"
              className="text-slate-500 hover:text-slate-700"
              onClick={() => setReplyTo(null)}
              disabled={sending}
            >
              Cancel
            </button>
          </div>
        ) : null}
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Write a reply…"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={sending}
        />
        <Button type="button" onClick={handleSend} disabled={sending || !String(message || '').trim()}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
      <div className="mt-1 text-[11px] text-slate-400">Press Enter to send.</div>
      </> : (
        <div className="mt-3 text-xs text-slate-500">Incident resolved — replies are read-only.</div>
      )}
    </div>
  );
};

const getAcknowledgmentUserId = (item) => {
  if (!item || item.group !== 'acknowledgment') return null;
  const ackData = item.meta?.acknowledgedBy || item.meta?.ackEntity || {};
  return ackData.userId || item.actor?.id || null;
};

const isAcknowledgmentTimelineGroup = (item) =>
  ['acknowledgment', 'acknowledgement', 'verification', 'ack'].includes(
    String(item?.group || '').trim().toLowerCase()
  );

const isFinalVerificationStatus = (status) => {
  const v = String(status || '').trim();
  return v === 'Confirmed' || v === 'False Alarm' || v === 'False alert';
};

/** E3: any-one confirms → other pending verifications show auto-escalation superseded (incl. virtual crew-esc). */
const applyVerificationSupersedeState = (timelineItems) => {
  const acks = (timelineItems || []).filter(isAcknowledgmentTimelineGroup);
  const finalized = acks.find(
    (item) =>
      isFinalVerificationStatus(item?.meta?.verificationStatus) && !item?.meta?.superseded
  );
  if (!finalized) return timelineItems;

  const supersededBy = {
    cardId: finalized.id,
    verificationStatus: finalized.meta?.verificationStatus,
    responder:
      finalized.meta?.acknowledgedBy?.userName ||
      finalized.actor?.name ||
      'Unknown',
    responderTeam: finalized.meta?.acknowledgedBy?.teamName || '',
    responderCrew: finalized.meta?.acknowledgedBy?.crewName || '',
    verifiedAt:
      finalized.meta?.verifiedAt ||
      finalized.meta?.acknowledgedAt ||
      new Date().toISOString(),
    withinSLA: finalized.meta?.sla?.met ?? true,
  };
  const supersededAt = supersededBy.verifiedAt;

  return (timelineItems || []).map((item) => {
    if (!isAcknowledgmentTimelineGroup(item)) return item;
    if (item.id === finalized.id) return item;
    if (item.meta?.superseded) return item;
    const status = item.meta?.verificationStatus || 'Pending';
    if (status !== 'Pending') return item;
    return {
      ...item,
      meta: {
        ...item.meta,
        superseded: true,
        supersededAt,
        supersededBy,
      },
    };
  });
};

const buildCrewEscalationAcknowledgmentItems = (timelineItems, candidates, incident) => {
  if (!Array.isArray(candidates) || candidates.length === 0) return [];

  const existingUserIds = new Set();
  (timelineItems || [])
    .filter((item) => item.group === 'acknowledgment')
    .forEach((item) => {
      const uids = item.meta?.assignedUserIds;
      if (Array.isArray(uids)) {
        uids.forEach((uid) => {
          if (uid) existingUserIds.add(uid);
        });
      }
      const singleUid = getAcknowledgmentUserId(item);
      if (singleUid) {
        existingUserIds.add(singleUid);
      }
    });

  const missingCandidates = candidates.filter(
    (c) =>
      c?.userId &&
      c.crewId &&
      c.teamId &&
      c.escalationLevel !== 999 &&
      !existingUserIds.has(c.userId)
  );
  if (missingCandidates.length === 0) return [];

  const levelGroups = {};
  missingCandidates.forEach((candidate) => {
    const lvl = candidate.escalationLevel ?? 0;
    if (!levelGroups[lvl]) levelGroups[lvl] = [];
    levelGroups[lvl].push(candidate);
  });

  const sortedLevels = Object.keys(levelGroups)
    .map(Number)
    .sort((a, b) => a - b);
  const baseTime = incident?.created_at || incident?.detected_at || new Date().toISOString();
  const incidentReportedAt = parseTimestamp(baseTime);
  let currentOffsetMinutes = 0;
  const virtualItems = [];

  sortedLevels.forEach((lvl) => {
    const candidatesAtLevel = levelGroups[lvl];
    // No SLA on a level → it contributes no waiting window (no 5m fallback).
    const levelDuration = candidatesAtLevel[0]?.withinMinutes ?? 0;
    const notifiedAt = new Date(incidentReportedAt.getTime() + currentOffsetMinutes * 60000);

    const levelNames = candidatesAtLevel.map((c) => c.userName).join(', ');
    const primaryCandidate = candidatesAtLevel[0];
    const assignedUserIds = candidatesAtLevel.map((c) => c.userId);
    const assignees = candidatesAtLevel.map((c) => ({
      userId: c.userId,
      userName: c.userName,
      email: c.email || '',
    }));

    virtualItems.push({
      id: `crew-esc-lvl-${lvl}`,
      group: 'acknowledgment',
      timestamp: baseTime,
      actor: {
        id: primaryCandidate.userId,
        name: primaryCandidate.userName,
        type: 'user',
        email: primaryCandidate.email || undefined,
      },
      title: 'Incident Acknowledged',
      details: `Awaiting acknowledgment from ${levelNames} (${primaryCandidate.teamName})`,
      chips: ['Pending', 'Crew Escalation'],
      attachments: [],
      meta: {
        notifiedAt: notifiedAt.toISOString(),
        acknowledgedAt: null,
        acknowledgedBy: {
          userId: primaryCandidate.userId,
          userName: primaryCandidate.userName,
          crewName: primaryCandidate.crewName,
          teamName: primaryCandidate.teamName,
        },
        escalationLevel: lvl,
        isEscalated: lvl > 0,
        verificationStatus: 'Pending',
        sla: {
          withinMinutes: primaryCandidate.withinMinutes ?? undefined,
          durationMinutes: primaryCandidate.withinMinutes ?? undefined,
          cumulativeOffset: currentOffsetMinutes,
        },
        fromCrewEscalation: true,
        assignedUserIds,
        assignees,
        assignmentSummary: {
          label: levelNames,
          assignedUserCount: candidatesAtLevel.length,
          users: candidatesAtLevel.map((c) => ({ id: c.userId, name: c.userName })),
        },
      },
      isVirtual: true,
    });

    currentOffsetMinutes += levelDuration;
  });

  return virtualItems;
};

const IncidentTimeline = ({ incident, departments = [], readOnly = false, activeTab = 'during', hidePlannedCards = false, scrollContainerRef = null, onRevealTimelineItem = null, setActiveGroup, setShowTimelineModal, setEditingItem, setShowAcknowledgmentModal, setEditingAcknowledgment, setAcknowledgmentMode, setShowResponseActionModal, setEditingResponseAction, setShowStatusUpdateModal, setEditingStatusUpdate, setStatusUpdateGroup, setShowResolutionModal, setEditingResolution, setShowNotificationModal, setEditingNotification, setShowManualNotificationModal, setEditingManualNotification, onStartConditional, canShowPreconfigSave = false, onOpenPreconfigPrompt = null, onOpenMapGeneralInfo = null, isAdmin = false, canResolveIncident = isAdmin, isDepartmentLeader = false, onChatWithReporter, currentUser, onRefreshIncident, children }) => {
  const storedWebUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch (_) {
      return null;
    }
  }, []);
  const canLoadGlobalDirectory = isWebAdmin(currentUser || storedWebUser);
  const incidentStatus = String(incident?.status || '').trim().toLowerCase();
  const leaderCanCreateOperationalCards = Boolean(isDepartmentLeader && (
    (!incident?.is_exercise && !incident?.is_scenario && !incident?.is_planned_event)
    || (incident?.is_exercise && incidentStatus === 'active')
  ));
  const canCreateRestrictedTimelineActions = Boolean(isAdmin || leaderCanCreateOperationalCards);
  const isIncidentReadOnly = readOnly || ['resolved', 'closed', 'completed', 'cancelled', 'canceled', 'false_alarm', 'false alarm'].includes(
    incidentStatus
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!incident?.is_exercise) return;
    (async () => {
      try {
        if (!canLoadGlobalDirectory && incident?.id) {
          const res = await CommanServices.getIncidentRecipientDirectory(incident.id);
          const directory = normalizeIncidentRecipientDirectory(res);
          setCardDepartments(directory.departments.map((x) => ({ ...x, id: x.id || x._id })));
          return;
        }
        const res = await CommanServices.departments();
        if (res?.status) setCardDepartments((res.data || []).map((x) => ({ ...x, id: x.id || x._id })));
      } catch (e) { /* noop */ }
    })();
  }, [canLoadGlobalDirectory, incident?.id, incident?.is_exercise]);

  const [locations, setLocations] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [matchedResponders, setMatchedResponders] = useState([]);
  const [manualNotifyUserNames, setManualNotifyUserNames] = useState({});
  const [manualDeptNameById, setManualDeptNameById] = useState({});
  const [manualCrewById, setManualCrewById] = useState({});
  const [planningDepartments, setPlanningDepartments] = useState([]);
  const [planningAppUsers, setPlanningAppUsers] = useState([]);
  const [planningCrewOptions, setPlanningCrewOptions] = useState([]);
  const [cardDepartments, setCardDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const timelineRequestSequence = useRef(0);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [collapsedConditionals, setCollapsedConditionals] = useState({});
  const [editingResource, setEditingResource] = useState(null);
  const [resourceEntities, setResourceEntities] = useState([]);
  const [notificationMerged] = useState(false); // merge feature removed — always show notifications separately
  const [focusedItemId, setFocusedItemId] = useState(null);
  const [focusPulseRevision, setFocusPulseRevision] = useState(0);
  const [focusRequest, setFocusRequest] = useState(null);
  const [autoFollowPaused, setAutoFollowPaused] = useState(false);
  const [pendingResponderActivity, setPendingResponderActivity] = useState({ activity: null, count: 0 });
  const autoFollowPausedRef = useRef(false);
  const focusHighlightTimerRef = useRef(null);
  const focusRequestSequenceRef = useRef(0);
  const responderFollowTimerRef = useRef(null);
  const responderFollowBatchRef = useRef({ activity: null, count: 0 });
  const [expandedMergeBuckets, setExpandedMergeBuckets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`notifBuckets-${incident?.id}`) || '{}'); } catch (e) { return {}; }
  });

  const toggleMergeBucket = (b) => {
    setExpandedMergeBuckets((p) => {
      const next = { ...p, [b]: !p[b] };
      try { localStorage.setItem(`notifBuckets-${incident?.id}`, JSON.stringify(next)); } catch (e) {}
      window.dispatchEvent(new CustomEvent('notificationBucketsChanged', { detail: { incidentId: incident?.id, buckets: next } }));
      return next;
    });
  };

  // React to bucket expansions toggled from the Injection Sequence widget.
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.incidentId !== incident?.id) return;
      setExpandedMergeBuckets(e.detail.buckets || {});
    };
    window.addEventListener('notificationBucketsChanged', handler);
    return () => window.removeEventListener('notificationBucketsChanged', handler);
  }, [incident?.id]);

  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Load from localStorage or default to all expanded
    const saved = localStorage.getItem(`timeline-groups-${incident.id}`);
    return saved ? JSON.parse(saved) : {
      report: true,
      notification: true,
      acknowledgment: true,
      response_action: true,
      status_update: true,
      call_log: true,
      resolution: true,
      resource: true,
    };
  });

  // When a drill transitions to COMPLETED, force every timeline section to collapse once so the
  // completed view stays compact. The user can still manually re-expand any section afterwards.
  const completedCollapseRef = useRef(false);
  useEffect(() => {
    const isCompleted = String(incident?.status || '').toLowerCase() === 'completed';
    if (isCompleted && !completedCollapseRef.current) {
      completedCollapseRef.current = true;
      setExpandedGroups((prev) => {
        const collapsed = Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {});
        try { localStorage.setItem(`timeline-groups-${incident.id}`, JSON.stringify(collapsed)); } catch (_) {}
        return collapsed;
      });
    }
    if (!isCompleted) completedCollapseRef.current = false;
  }, [incident?.status, incident?.id]);


  const toggleGroup = (groupKey) => {
    const newState = {
      ...expandedGroups,
      [groupKey]: !expandedGroups[groupKey]
    };
    setExpandedGroups(newState);
    // Persist to localStorage
    localStorage.setItem(`timeline-groups-${incident.id}`, JSON.stringify(newState));
  };

  const isManualUserNotificationItem = (item) =>
    item?.group === 'notification' &&
    item?.meta?.manuallyCreated === true &&
    Array.isArray(item?.chips) &&
    item.chips.includes('Manual') &&
    !item.chips.includes('Department Alert') &&
    !(item.meta?.departmentIds?.length) &&
    !(item.meta?.crewIds?.length);

  const isManualDeptCrewNotificationItem = (item) =>
    item?.group === 'notification' &&
    item?.meta?.manuallyCreated === true &&
    Array.isArray(item?.chips) &&
    item.chips.includes('Manual') &&
    !item.chips.includes('Department Alert') &&
    (item.meta?.departmentIds?.length > 0 || item.meta?.crewIds?.length > 0);

  const isDepartmentAlertBroadcastItem = (item) =>
    item?.group === 'notification' &&
    Array.isArray(item?.chips) &&
    item.chips.includes('Department Alert') &&
    (item.meta?.departmentAlertBroadcast === true ||
      Array.isArray(item.meta?.recipientIds));

  const isGroupedBroadcastNotificationItem = (item) =>
    isManualDeptCrewNotificationItem(item) || isDepartmentAlertBroadcastItem(item);

  const getGroupedBroadcastDisplayName = (item, deptNameById = {}, crewById = {}) => {
    if (isDepartmentAlertBroadcastItem(item)) {
      return (
        item.meta?.department ||
        (item.meta?.departmentNames || [])[0] ||
        item.meta?.departmentName ||
        'department'
      );
    }
    return getManualDeptCrewDisplayName(item, deptNameById, crewById);
  };

  const getManualDeptCrewDisplayName = (item, deptNameById = {}, crewById = {}) => {
    const deptNames = item.meta?.departmentNames || [];
    const crewTargets = item.meta?.crewTargets || [];
    if (deptNames.length) return deptNames.join(', ');
    if (crewTargets.length) {
      return crewTargets
        .map((c) => [c.teamName, c.crewName].filter(Boolean).join(' · '))
        .join(', ');
    }
    const fromDept = (item.meta?.departmentIds || [])
      .map((id) => deptNameById[id])
      .filter(Boolean);
    const fromCrew = (item.meta?.crewIds || [])
      .map((id) => {
        const c = crewById[id];
        return c ? [c.teamName, c.crewName].filter(Boolean).join(' · ') : null;
      })
      .filter(Boolean);
    const label = [...fromDept, ...fromCrew].join(', ');
    if (label) return label;
    const fromTitle = item?.title?.match(/^Manual notification:\s*(.+)$/i)?.[1]?.trim();
    return fromTitle || 'Recipients';
  };

  const getManualUserNotificationDisplayName = (item, fallbackName) => {
    if (item?.meta?.userName) return item.meta.userName;
    const fromTitle = item?.title?.match(/Manual notification sent to (.+)$/i)?.[1]?.trim();
    if (fromTitle) return fromTitle;
    return fallbackName;
  };

  const expandManualUserNotificationItems = (items, userNameById = {}) => {
    const expanded = [];
    for (const item of items) {
      const ids = item.meta?.recipientIds || [];
      const isGroupedMaster =
        isManualUserNotificationItem(item) &&
        (item.meta?.recipientCount > 1 || ids.length > 1);
      if (!isGroupedMaster) {
        expanded.push(item);
        continue;
      }
      ids.forEach((uid) => {
        const name = userNameById[uid] || 'User';
        expanded.push({
          ...item,
          id: `${item.id}_${uid}`,
          title: `Manual notification sent to ${name}`,
          meta: {
            ...item.meta,
            userName: name,
            recipientCount: 1,
            recipientIds: [uid],
          },
        });
      });
    }
    return expanded;
  };

  // Fetch timeline data from backend
  useEffect(() => {
    fetchTimelineData();
  }, [incident.id]);

  useEffect(() => {
    if (!incident?.id) {
      setMatchedResponders([]);
      return;
    }
    const requestPayload = {
      incidentType: incident.incident_type || '',
      event: incident.event || '',
      trigger: incident.trigger || '',
      locationPath: incident.location || [],
    };
    CommanServices.teamsMatchedData(requestPayload)
      .then((res) => {
        if (res?.status) {
          setMatchedResponders(res.data?.candidates || []);
        } else {
          setMatchedResponders([]);
        }
      })
      .catch(() => setMatchedResponders([]));
  }, [incident?.id, incident?.incident_type, incident?.event, incident?.trigger, incident?.location]);

  const effectiveTimelineData = useMemo(() => {
    let merged = [...timelineData].filter(
      (it) => !(it?.is_system_generated && (it?.action === 'STATUS_CHANGED' || /status changed from/i.test(it?.details || '')))
        && !(hidePlannedCards && it?.plan_state === 'planned')
    );

    const crewEscalationAckItems = buildCrewEscalationAcknowledgmentItems(
      timelineData,
      matchedResponders,
      incident
    );
    if (crewEscalationAckItems.length) {
      merged = [...merged, ...crewEscalationAckItems];
    }

    // Supersede-on-verify is RETIRED: every assignee keeps their own live
    // verification card — one confirm never closes another's card, and a
    // user can answer multiple verification cards over time.
    return merged;
  }, [
    timelineData,
    hidePlannedCards,
    matchedResponders,
    incident,
  ]);

  const requestCardFocus = React.useCallback((detail = {}) => {
    const itemId = String(detail.itemId || '');
    if (!itemId) return;
    if (detail.incidentId && String(detail.incidentId) !== String(incident?.id)) return;

    const groupKey = normalizeFocusGroupKey(detail.groupKey);
    const sourceIds = Array.isArray(detail.sourceIds) ? detail.sourceIds.map(String) : [];

    // Explicit operator selections own the view immediately, including while
    // React is still expanding/refetching their target. A responder event that
    // lands in that window must queue instead of replacing the chosen card.
    if (detail.pauseAfterFocus) {
      autoFollowPausedRef.current = true;
      setAutoFollowPaused(true);
    }

    // A requested focus is authoritative: reveal the card instead of leaving
    // it hidden behind a stale search/category or the real-plan hide toggle.
    setFilter('all');
    setSearchQuery('');
    onRevealTimelineItem?.();

    if (groupKey) {
      setExpandedGroups((prev) => {
        if (prev[groupKey]) return prev;
        const next = { ...prev, [groupKey]: true };
        try { localStorage.setItem(`timeline-groups-${incident.id}`, JSON.stringify(next)); } catch (_) {}
        return next;
      });
    }
    if (sourceIds.length) {
      setCollapsedConditionals((prev) => {
        const next = { ...prev };
        sourceIds.forEach((sourceId) => { next[sourceId] = false; });
        return next;
      });
    }

    focusRequestSequenceRef.current += 1;
    setFocusRequest({
      ...detail,
      itemId,
      groupKey,
      sourceIds,
      sequence: focusRequestSequenceRef.current,
    });
  }, [incident?.id, onRevealTimelineItem]);

  // Resolve a focus request only when React has actually rendered the target.
  // State/data changes naturally re-run this effect, so there is no brittle
  // one-shot timeout competing with a timeline refetch.
  useEffect(() => {
    if (!focusRequest?.itemId) return undefined;

    const deferInterruptedResponderFocus = () => {
      const activity = focusRequest.activity || {
        incidentId: String(incident?.id || ''),
        itemId: String(focusRequest.itemId),
        groupKey: focusRequest.groupKey,
        summary: 'New app-user activity',
        occurredAt: focusRequest.occurredAt,
      };
      setPendingResponderActivity((current) => ({
        activity: queueLatestActivity(current, activity).activity,
        count: Math.max(0, Number(current?.count) || 0)
          + Math.max(1, Number(focusRequest.activityBatchCount) || 1),
      }));
      setFocusRequest((current) => current?.sequence === focusRequest.sequence ? null : current);
    };

    // The operator may start reading after the socket event but before its
    // refetch renders the target. Never let that already-pending response
    // steal the panel once manual reading has begun.
    if (focusRequest.reason === 'responder-activity' && autoFollowPausedRef.current) {
      deferInterruptedResponderFocus();
      return undefined;
    }

    const targetItem = (effectiveTimelineData || []).find(
      (item) => String(item?.id || '') === String(focusRequest.itemId)
    );
    const itemById = new Map((effectiveTimelineData || []).map((item) => [String(item?.id || ''), item]));
    const derivedSourceIds = [];
    let rootItem = targetItem;
    let guard = 0;
    while (rootItem?.meta?.conditional?.sourceItemId && guard < 20) {
      const sourceId = String(rootItem.meta.conditional.sourceItemId);
      derivedSourceIds.push(sourceId);
      rootItem = itemById.get(sourceId) || rootItem;
      guard += 1;
      if (String(rootItem?.id || '') === String(targetItem?.id || '') && guard > 1) break;
    }
    const targetGroup = normalizeFocusGroupKey(rootItem?.group || focusRequest.groupKey || targetItem?.group);
    if (targetGroup && !expandedGroups[targetGroup]) {
      setExpandedGroups((prev) => {
        const next = { ...prev, [targetGroup]: true };
        try { localStorage.setItem(`timeline-groups-${incident.id}`, JSON.stringify(next)); } catch (_) {}
        return next;
      });
      return undefined;
    }

    const conditionalIds = Array.from(new Set([
      ...(focusRequest.sourceIds || []).map(String),
      ...derivedSourceIds,
    ]));
    if (conditionalIds.some((sourceId) => collapsedConditionals[sourceId])) {
      setCollapsedConditionals((prev) => {
        const next = { ...prev };
        conditionalIds.forEach((sourceId) => { next[sourceId] = false; });
        return next;
      });
      return undefined;
    }

    if (notificationMerged && targetGroup === 'notification' && targetItem) {
      const bucket = notificationBucket(targetItem);
      if (!expandedMergeBuckets[bucket]) {
        setExpandedMergeBuckets((prev) => {
          const next = { ...prev, [bucket]: true };
          try { localStorage.setItem(`notifBuckets-${incident?.id}`, JSON.stringify(next)); } catch (_) {}
          window.dispatchEvent(new CustomEvent('notificationBucketsChanged', {
            detail: { incidentId: incident?.id, buckets: next },
          }));
          return next;
        });
        return undefined;
      }
    }

    const frame = window.requestAnimationFrame(() => {
      if (focusRequest.reason === 'responder-activity' && autoFollowPausedRef.current) {
        deferInterruptedResponderFocus();
        return;
      }
      const panel = scrollContainerRef?.current || document.getElementById('incident-tracking');
      if (!panel) return;
      const candidates = Array.from(panel.querySelectorAll('[id^="inject-"]'));
      const exactId = `inject-${focusRequest.itemId}`;
      const responderId = String(focusRequest.activity?.actor?.id || '');
      const targetRecipientIds = (targetItem?.meta?.recipientIds || []).map(String);
      const preferredRenderedId = responderId && targetRecipientIds.includes(responderId)
        ? `${exactId}_${responderId}`
        : exactId;
      const target = candidates.find((node) => node.id === preferredRenderedId)
        || candidates.find((node) => node.id === exactId)
        || candidates.find((node) => node.id.startsWith(`${exactId}_`));
      if (!target) return;
      const renderedTargetId = target.id.slice('inject-'.length);

      const top = timelineFocusTop({
        containerRect: panel.getBoundingClientRect(),
        targetRect: target.getBoundingClientRect(),
        scrollTop: panel.scrollTop,
        scrollHeight: panel.scrollHeight,
        clientHeight: panel.clientHeight,
      });
      if (typeof panel.scrollTo === 'function') {
        panel.scrollTo({ top, behavior: 'smooth' });
      } else {
        panel.scrollTop = top;
      }

      setFocusedItemId(renderedTargetId);
      setFocusPulseRevision((revision) => revision + 1);
      if (focusHighlightTimerRef.current) clearTimeout(focusHighlightTimerRef.current);
      focusHighlightTimerRef.current = setTimeout(() => {
        setFocusedItemId((current) => current === renderedTargetId ? null : current);
        focusHighlightTimerRef.current = null;
      }, 3000);

      if (focusRequest.pauseAfterFocus) {
        autoFollowPausedRef.current = true;
        setAutoFollowPaused(true);
      }
      if (focusRequest.openDiscussion) {
        window.dispatchEvent(new CustomEvent('open-card-discussion', {
          detail: {
            incidentId: incident?.id,
            itemId: focusRequest.itemId,
            skipTimelineFocus: true,
          },
        }));
      }
      setFocusRequest((current) => current?.sequence === focusRequest.sequence ? null : current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    autoFollowPaused,
    collapsedConditionals,
    effectiveTimelineData,
    expandedGroups,
    expandedMergeBuckets,
    filter,
    focusRequest,
    incident?.id,
    notificationMerged,
    scrollContainerRef,
    searchQuery,
  ]);

  useEffect(() => {
    autoFollowPausedRef.current = autoFollowPaused;
  }, [autoFollowPaused]);

  // Pointer/wheel/keyboard activity inside the timeline means the operator is
  // reading it manually. New responder events then queue behind one explicit
  // "New activity" control instead of stealing the view.
  useEffect(() => {
    const panel = scrollContainerRef?.current || document.getElementById('incident-tracking');
    if (!panel) return undefined;
    const pause = (event) => {
      if (event?.target?.closest?.('[data-testid="timeline-new-activity"]')) return;
      autoFollowPausedRef.current = true;
      setAutoFollowPaused(true);
    };
    const pauseForKey = (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) pause(event);
    };
    panel.addEventListener('wheel', pause, { passive: true });
    panel.addEventListener('touchstart', pause, { passive: true });
    panel.addEventListener('pointerdown', pause);
    panel.addEventListener('keydown', pauseForKey);
    return () => {
      panel.removeEventListener('wheel', pause);
      panel.removeEventListener('touchstart', pause);
      panel.removeEventListener('pointerdown', pause);
      panel.removeEventListener('keydown', pauseForKey);
    };
  }, [incident?.id, scrollContainerRef]);

  useEffect(() => {
    const handleFocus = (event) => requestCardFocus(event?.detail || {});
    const handleLegacyFocus = (event) => requestCardFocus({
      ...(event?.detail || {}),
      incidentId: event?.detail?.incidentId || incident?.id,
    });
    const handleCreated = (event) => {
      const detail = event?.detail || {};
      if (String(detail.incidentId || '') !== String(incident?.id) || !detail.itemId) return;
      fetchTimelineData();
      requestCardFocus({
        incidentId: incident?.id,
        itemId: detail.itemId,
        groupKey: detail.group,
        reason: 'created',
        pauseAfterFocus: true,
      });
    };
    const handleResponderActivity = (event) => {
      const activity = responderActivityFromSync(event?.detail, incident?.id);
      if (!activity) return;

      const addBatchesToPending = (batches) => {
        setPendingResponderActivity((current) => batches.reduce((next, batch) => {
          if (!batch?.activity) return next;
          const selected = queueLatestActivity(next, batch.activity).activity;
          return {
            activity: selected,
            count: Math.max(0, Number(next.count) || 0) + Math.max(1, Number(batch.count) || 1),
          };
        }, current));
      };

      if (autoFollowPausedRef.current) {
        const batches = [];
        if (responderFollowTimerRef.current) {
          clearTimeout(responderFollowTimerRef.current);
          responderFollowTimerRef.current = null;
        }
        if (responderFollowBatchRef.current.activity) batches.push(responderFollowBatchRef.current);
        responderFollowBatchRef.current = { activity: null, count: 0 };
        batches.push({ activity, count: 1 });
        addBatchesToPending(batches);
        return;
      }

      responderFollowBatchRef.current = queueLatestActivity(
        responderFollowBatchRef.current,
        activity,
      );
      if (responderFollowTimerRef.current) clearTimeout(responderFollowTimerRef.current);
      responderFollowTimerRef.current = setTimeout(() => {
        const batch = responderFollowBatchRef.current;
        responderFollowBatchRef.current = { activity: null, count: 0 };
        responderFollowTimerRef.current = null;
        if (!batch.activity) return;
        if (autoFollowPausedRef.current) {
          addBatchesToPending([batch]);
          return;
        }
        requestCardFocus({
          incidentId: incident?.id,
          itemId: batch.activity.itemId,
          groupKey: batch.activity.groupKey,
          reason: 'responder-activity',
          activity: batch.activity,
          activityBatchCount: batch.count,
        });
      }, 200);
    };

    window.addEventListener(TIMELINE_FOCUS_EVENT, handleFocus);
    window.addEventListener('expandInjectAndScroll', handleLegacyFocus);
    window.addEventListener('timelineCardCreated', handleCreated);
    window.addEventListener(TIMELINE_RESPONDER_ACTIVITY_EVENT, handleResponderActivity);
    return () => {
      if (responderFollowTimerRef.current) clearTimeout(responderFollowTimerRef.current);
      responderFollowTimerRef.current = null;
      responderFollowBatchRef.current = { activity: null, count: 0 };
      window.removeEventListener(TIMELINE_FOCUS_EVENT, handleFocus);
      window.removeEventListener('expandInjectAndScroll', handleLegacyFocus);
      window.removeEventListener('timelineCardCreated', handleCreated);
      window.removeEventListener(TIMELINE_RESPONDER_ACTIVITY_EVENT, handleResponderActivity);
    };
  }, [incident?.id, requestCardFocus]);

  useEffect(() => () => {
    if (focusHighlightTimerRef.current) clearTimeout(focusHighlightTimerRef.current);
  }, []);

  const focusPendingResponderActivity = () => {
    const activity = pendingResponderActivity.activity;
    if (!activity) return;
    setPendingResponderActivity({ activity: null, count: 0 });
    requestCardFocus({
      incidentId: incident?.id,
      itemId: activity.itemId,
      groupKey: activity.groupKey,
      reason: 'new-activity-control',
      pauseAfterFocus: true,
      activity,
    });
  };

  // Live exercise pause state — used to FREEZE all timeline countdowns while the drill is paused.
  const [exerciseRuntimeState, setExerciseRuntime] = useState({ paused: false, paused_at: null });
  // Resolved/closed real incidents freeze every countdown at the resolution
  // moment — "Resolve Incident" stops the clocks the same way Finish does for
  // an exercise. Reuses the pause plumbing all countdowns already honour.
  const exerciseRuntime = useMemo(() => {
    if (!incident?.is_exercise && ['resolved', 'closed'].includes(String(incident?.status || '').toLowerCase())) {
      return { paused: true, paused_at: incident?.resolved_at || incident?.updated_at || null };
    }
    return exerciseRuntimeState;
  }, [exerciseRuntimeState, incident?.is_exercise, incident?.status, incident?.resolved_at, incident?.updated_at]);
  useEffect(() => {
    if (!incident?.id || !incident?.is_exercise || String(incident?.status || '').toLowerCase() !== 'active') return;
    let alive = true;
    const load = async () => {
      try {
        const res = await CommanServices.getExerciseLog(incident.id);
        if (alive && res?.status) setExerciseRuntime({ paused: !!res.data?.paused, paused_at: res.data?.paused_at || null });
      } catch (_) {}
    };
    load();
    const poll = setInterval(load, 5000);
    const onPause = () => load();
    window.addEventListener('exercisePauseChanged', onPause);
    return () => { alive = false; clearInterval(poll); window.removeEventListener('exercisePauseChanged', onPause); };
  }, [incident?.id, incident?.is_exercise, incident?.status]);

  // Listen for timeline refresh events
  useEffect(() => {
    const handleTimelineRefresh = (event) => {
      const detail = event?.detail || {};
      if (detail.incident_id && String(detail.incident_id) !== String(incident?.id)) return;
      if (['chat_message_created', 'timeline_comment_added'].includes(String(detail.change_type || ''))) return;
      fetchTimelineData();
    };

    window.addEventListener('timelineRefresh', handleTimelineRefresh);
    return () => window.removeEventListener('timelineRefresh', handleTimelineRefresh);
  }, [incident?.id]);

  // Normal role edits are card-specific. The page-level assignment modal also
  // offers explicit same-role bulk reuse before activation. Both paths dispatch
  // 'timelineRefresh' (handled above), so no relay listener is needed here.

  // Injection model: every card except "report" counts as an injection.
  // Applies to scenario builder pages and exercises at the Requested stage.
  const injectModeEnabled = useMemo(() => {
    // Real incidents reviewing a scenario plan get the same inject-manager
    // section chrome as an exercise at the Requested stage.
    if (!incident?.is_exercise) return incident?.plan_state === 'planned';
    if (incident?.is_scenario) return true;
    return String(incident?.status || '').toLowerCase() === 'requested';
  }, [incident?.is_exercise, incident?.is_scenario, incident?.status, incident?.plan_state]);

  const injectCount = useMemo(() => {
    if (!injectModeEnabled) return 0;
    return (effectiveTimelineData || []).filter(
      (i) => i && i.group && i.group !== 'report' && i.is_active !== false
    ).length;
  }, [injectModeEnabled, effectiveTimelineData]);

  const roleCoverage = useMemo(() => {
    if (!injectModeEnabled) return { people: 0, roles: 0 };
    const totals = {};
    (effectiveTimelineData || []).forEach((i) => {
      if (!i || i.is_active === false || i.group === 'report') return;
      cleanScenarioRoles(i?.meta?.scenarioRoles).forEach((r) => {
        totals[r.role] = (totals[r.role] || 0) + r.count;
      });
    });
    const people = Object.values(totals).reduce((s, n) => s + n, 0);
    return { people, roles: Object.keys(totals).length };
  }, [injectModeEnabled, effectiveTimelineData]);

  // Staffing readiness at the Requested stage: how many role-bearing cards still need real personnel.
  const staffingStatus = useMemo(() => {
    const requested = !!incident?.is_exercise && !incident?.is_scenario &&
      String(incident?.status || '').toLowerCase() === 'requested';
    if (!requested) return null;
    let total = 0, unassigned = 0;
    (effectiveTimelineData || []).forEach((i) => {
      if (!i || i.is_active === false || i.group === 'report') return;
      if (!cardHasRoles(i)) return;
      total += 1;
      if (!cardIsFullyAssigned(i)) unassigned += 1;
    });
    return { total, unassigned };
  }, [incident?.is_exercise, incident?.is_scenario, incident?.status, effectiveTimelineData]);

  useEffect(() => {
    const needsUserNames = timelineData.some((item) => {
      if (item.meta?.assignedUserIds?.length > 0) {
        return true;
      }
      if (
        isManualUserNotificationItem(item) &&
        (item.meta?.recipientCount > 1 || (item.meta?.recipientIds?.length > 1))
      ) {
        return true;
      }
      return (
        (isManualDeptCrewNotificationItem(item) || isDepartmentAlertBroadcastItem(item)) &&
        (item.meta?.recipientIds?.length > 0)
      );
    });
    if (needsUserNames && canLoadGlobalDirectory) {
      CommanServices.getAppUsers()
        .then((res) => {
          const map = {};
          (Array.isArray(res?.data) ? res.data : []).forEach((u) => {
            if (u?.id) map[u.id] = u.name || u.full_name || u.email || 'User';
          });
          setManualNotifyUserNames(map);
        })
        .catch(() => { });
    }

    const needsDeptCrewLabels = timelineData.some(
      (item) =>
        isManualDeptCrewNotificationItem(item) &&
        !(item.meta?.departmentNames?.length) &&
        !(item.meta?.crewTargets?.length)
    );
    if (needsDeptCrewLabels && canLoadGlobalDirectory) {
      Promise.all([
        CommanServices.departments(),
        CommanServices.getTeamsData(),
      ])
        .then(([dRes, tRes]) => {
          const deptMap = {};
          (Array.isArray(dRes?.data) ? dRes.data : []).forEach((d) => {
            if (d?.id) deptMap[d.id] = d.department_name || d.name || 'Department';
          });
          setManualDeptNameById(deptMap);
          const crewMap = {};
          (Array.isArray(tRes?.data) ? tRes.data : []).forEach((team) => {
            (team.crews || []).forEach((c) => {
              if (c?.id) {
                crewMap[c.id] = {
                  crewName: c.crew_name || 'Crew',
                  teamName: team.team_name || 'Team',
                };
              }
            });
          });
          setManualCrewById(crewMap);
        })
        .catch(() => { });
    }
    if (!canLoadGlobalDirectory && incident?.id && (needsUserNames || needsDeptCrewLabels)) {
      CommanServices.getIncidentRecipientDirectory(incident.id)
        .then(normalizeIncidentRecipientDirectory)
        .then((directory) => {
          const userMap = {};
          directory.users.forEach((user) => {
            if (user?.id) userMap[user.id] = user.name || user.full_name || user.email || 'User';
          });
          setManualNotifyUserNames(userMap);

          const departmentMap = {};
          directory.departments.forEach((department) => {
            if (department?.id) departmentMap[department.id] = department.department_name || department.name || 'Department';
          });
          setManualDeptNameById(departmentMap);

          const crewMap = {};
          directory.crews.forEach((crew) => {
            if (crew?.id) {
              crewMap[crew.id] = {
                crewName: crew.crew_name || crew.name || 'Crew',
                teamName: crew.team_name || 'Team',
              };
            }
          });
          setManualCrewById(crewMap);
        })
        .catch(() => { });
    }
  }, [canLoadGlobalDirectory, incident?.id, timelineData]);

  useEffect(() => {
    if (!Array.isArray(incident?.planning_tasks) || incident.planning_tasks.length === 0) {
      setPlanningDepartments([]);
      setPlanningAppUsers([]);
      setPlanningCrewOptions([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const scopedDirectory = !canLoadGlobalDirectory && incident?.id
          ? normalizeIncidentRecipientDirectory(await CommanServices.getIncidentRecipientDirectory(incident.id))
          : null;
        const [departmentsRes, usersRes, teamsRes] = scopedDirectory ? [null, null, null] : await Promise.all([
          CommanServices.departments(),
          CommanServices.getAppUsers(),
          CommanServices.getTeamsData(),
        ]);
        if (cancelled) return;

        const departmentList = scopedDirectory?.departments || (Array.isArray(departmentsRes?.data)
          ? departmentsRes.data
          : (Array.isArray(departmentsRes) ? departmentsRes : []));
        const userList = scopedDirectory?.users || (Array.isArray(usersRes?.data)
          ? usersRes.data
          : (Array.isArray(usersRes) ? usersRes : []));
        const teamsData = Array.isArray(teamsRes?.data)
          ? teamsRes.data
          : (Array.isArray(teamsRes) ? teamsRes : []);
        const crews = scopedDirectory ? scopedDirectory.crews : [];
        teamsData.forEach((team) => {
          (team.crews || []).forEach((crew) => {
            if (crew?.id && crew.is_active !== false) {
              crews.push({
                id: crew.id,
                crew_name: crew.crew_name || 'Crew',
                team_name: team.team_name || 'Team',
              });
            }
          });
        });

        setPlanningDepartments(departmentList.map((department) => ({
          ...department,
          id: department.id || department._id,
        })));
        setPlanningAppUsers(userList);
        setPlanningCrewOptions(crews);
      } catch (_) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [canLoadGlobalDirectory, incident?.id, incident?.planning_tasks?.length]);

  const fetchTimelineData = async () => {
    const requestSequence = ++timelineRequestSequence.current;
    try {
      setLoading(true);
      const response = await CommanServices.getincidentTimeline(incident.id);
      if (requestSequence !== timelineRequestSequence.current) return;
      if (response.status) {
        const data = await response.data;
        setTimelineData(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch timeline data');
      }
    } catch (error) {
      if (requestSequence === timelineRequestSequence.current) {
        console.error('Error fetching timeline:', error);
      }
    } finally {
      if (requestSequence === timelineRequestSequence.current) setLoading(false);
    }
  };

  // Timeline groups configuration
  const timelineGroups = [
    {
      key: 'report',
      title: 'Report & Detection',
      icon: '🚨',
      color: 'border-l-gray-300',
      tooltip: 'Details about how the incident was detected, including detection method, type/event/trigger classifications, and reporter information.',
      phase: 'planning'
    },
    {
      key: 'notification',
      title: 'Notifications',
      icon: '⚠️',
      color: 'border-l-gray-300',
      tooltip: 'Records of who was notified about the incident and tracks their acknowledgment status.',
      phase: 'during'
    },
    {
      key: 'acknowledgment',
      title: 'Verifications',
      icon: '✅',
      color: 'border-l-gray-300',
      tooltip: 'Operational confirmation of the incident, confirming if it\'s real or a false alarm, and SLA tracking.',
      phase: 'during'
    },
    {
      key: 'status_update',
      title: 'Status Updates',
      icon: '🔁',
      color: 'border-l-gray-300',
      tooltip: 'Live notes, threaded replies, and attachments related to the ongoing incident.',
      phase: 'during'
    },
    {
      key: 'call_log',
      title: 'Call Logs',
      icon: '📞',
      color: 'border-l-indigo-400',
      tooltip: 'Automated summaries of audio and video calls, including participants and outcomes.',
      phase: 'during'
    },
    {
      key: 'response_action',
      title: 'Response Actions',
      icon: '📮',
      color: 'border-l-gray-300',
      tooltip: 'Specific response tasks assigned to teams, including SLA and completion tracking.',
      phase: 'during'
    },
    {
      key: 'resource',
      title: 'Resources',
      icon: '📦',
      color: 'border-l-gray-300',
      tooltip: 'Resources injected into the drill (equipment, vehicles, supplies) allocated to action cards or tasks.',
      phase: 'during'
    },
    {
      key: 'resolution',
      title: 'Resolution & Final Update',
      icon: '🏁',
      color: 'border-l-gray-300',
      tooltip: 'Final outcome of the incident, closure notes, and confirmations.',
      phase: 'during'
    }
  ].filter((group) => {
    // Resources are an exercise-only inject group.
    if (group.key === 'resource' && !incident?.is_exercise) return false;
    return true;
  });

  const timelineFilterOptions = timelineGroups;
  const timelineFilterKeys = timelineFilterOptions.map((group) => group.key).join('|');

  // Phase/context changes can remove a category (for example Resources in a real
  // incident). Never leave the select holding a category that cannot be rendered.
  useEffect(() => {
    if (filter !== 'all' && !timelineFilterKeys.split('|').includes(filter)) {
      setFilter('all');
    }
  }, [filter, timelineFilterKeys]);

  // Build the Resource inject "Allocate to" options from Action Cards + their tasks
  // (resources attach to a whole action card or a specific task within it).
  useEffect(() => {
    if (!incident?.is_exercise) return;
    const opts = [];
    (effectiveTimelineData || []).forEach((it) => {
      if (!it || (it.group !== 'response_action' && it.group !== 'pre_incident_action') || it.is_active === false) return;
      const cardName = it.meta?.actionCardName || it.title || 'Action Card';
      opts.push({ type: 'card', id: String(it.id), name: cardName });
      (it.meta?.tasks || []).forEach((t, idx) => {
        const tid = t.id || `${it.id}::task::${idx}`;
        opts.push({ type: 'task', id: String(tid), name: `${cardName} › ${t.title || `Task ${idx + 1}`}`, parentId: String(it.id) });
      });
    });
    setResourceEntities(opts);
  }, [incident?.is_exercise, effectiveTimelineData]);

  const openAddResource = () => { setEditingResource(blankResource()); setShowResourceModal(true); };
  const openEditResource = (item) => { setEditingResource(timelineItemToResource(item)); setShowResourceModal(true); };

  const handleSaveResource = async (form) => {
    if (!form.category?.trim()) { return; }
    try {
      if (form.id) {
        const existing = (timelineData || []).find((it) => it.id === form.id) || { id: form.id };
        await CommanServices.updatePutIncidentTimeline(incident.id, form.id, buildResourceFullItem(existing, form));
      } else {
        await CommanServices.addincidentTimeline(incident.id, resourceToTimelinePayload(form));
      }
      setShowResourceModal(false);
      setEditingResource(null);
      fetchTimelineData();
      window.dispatchEvent(new Event('timelineRefresh'));
    } catch (e) {
      console.error('Failed to save resource inject', e);
    }
  };

  const handleDeleteResource = async (item) => {
    if (!window.confirm('Remove this resource inject?')) return;
    try {
      await CommanServices.deleteTimelineItem(incident.id, item.id);
      fetchTimelineData();
      window.dispatchEvent(new Event('timelineRefresh'));
    } catch (e) {
      console.error('Failed to delete resource inject', e);
    }
  };

  // Pen (Inject Setup) → edit the card CONTENT. Notification opens the manual-notification
  // recipient picker; verification opens the config modal (no status buttons).
  const handleCardEdit = (editItem) => {
    if (editItem?.isVirtual) return;
    const g = editItem.group;
    if (g === 'acknowledgment') {
      setAcknowledgmentMode && setAcknowledgmentMode('config');
      setEditingAcknowledgment(editItem);
      setShowAcknowledgmentModal(true);
    } else if (g === 'response_action' || g === 'pre_incident_action') {
      setEditingResponseAction(editItem);
      setShowResponseActionModal(true);
    } else if (g === 'status_update') {
      setStatusUpdateGroup('status_update');
      setEditingStatusUpdate(editItem);
      setShowStatusUpdateModal(true);
    } else if (g === 'resolution') {
      if (!canResolveIncident) return;
      setEditingResolution(editItem);
      setShowResolutionModal(true);
    } else if (g === 'resource') {
      openEditResource(editItem);
    } else if (g === 'notification') {
      setEditingManualNotification && setEditingManualNotification(editItem);
      setShowManualNotificationModal(true);
    } else {
      setEditingItem(editItem);
      setActiveGroup(g);
      setShowTimelineModal(true);
    }
  };

  // Takeover → act ON BEHALF of the assignee. Verification opens a slim "status only"
  // modal; notification opens the acknowledge/off-duty modal.
  const handleTakeover = (editItem) => {
    if (!isAdmin || editItem?.isVirtual) return;
    const g = editItem.group;
    if (g === 'acknowledgment') {
      setAcknowledgmentMode && setAcknowledgmentMode('takeover');
      setEditingAcknowledgment(editItem);
      setShowAcknowledgmentModal(true);
    } else if (g === 'response_action' || g === 'pre_incident_action') {
      setEditingResponseAction(editItem);
      setShowResponseActionModal(true);
    } else if (g === 'status_update') {
      setStatusUpdateGroup('status_update');
      setEditingStatusUpdate(editItem);
      setShowStatusUpdateModal(true);
    } else if (g === 'resource') {
      openEditResource(editItem);
    } else if (g === 'notification') {
      setEditingNotification({
        ...editItem,
        _originalMeta: JSON.parse(JSON.stringify(editItem.meta || {})),
      });
      setShowNotificationModal(true);
    } else {
      setEditingItem(editItem);
      setActiveGroup(g);
      setShowTimelineModal(true);
    }
  };

  // Create new timeline entry
  const createTimelineEntry = async (entryData) => {
    try {
      const response = await CommanServices.addincidentTimeline(incident.id, entryData);

      if (response.status) {
        // Refresh timeline data
        fetchTimelineData();
        return true;
      } else {
        console.error('Failed to create timeline entry');
        return false;
      }
    } catch (error) {
      console.error('Error creating timeline entry:', error);
      return false;
    }
  };

  // Legacy fallback: inject virtual cards only when backend action entries do not exist yet
  const approvedPlans = (incident?.planning_tasks || []).filter(p => String(p.status || '').toLowerCase() === 'approved');
  const planHasBackendActions = (planId) =>
    (incident?.approved_actions || []).some((action) => action.plan_id === planId && action.meta) ||
    (effectiveTimelineData || []).some(
      (item) => item.meta?.plan_id === planId
    );

  const planActionCards = approvedPlans.flatMap(p => {
    const planId = p.id || p._id;
    if (planHasBackendActions(planId)) return [];

    return (p.tasks || []).map(t => {
    const timing = String(t.timing || '').toLowerCase();
    const isBeforeEvent = timing.includes('before') || timing.includes('pre') || timing.includes('readiness') || timing.includes('t-');
    return {
      id: `plan-task-${t.id || t.title}-${p.id || p._id}`,
      group: isBeforeEvent ? 'pre_incident_action' : 'response_action',
      title: t.title,
      details: `Task from approved plan: ${p.title} (${resolvePlanOwnerDisplayName(p, { departments: planningDepartments, appUsers: planningAppUsers, crewOptions: planningCrewOptions, fallback: 'General' }).name})`,
      actor: { name: resolvePlanOwnerDisplayName(p, { departments: planningDepartments, appUsers: planningAppUsers, crewOptions: planningCrewOptions, fallback: 'System' }).name, type: 'team' },
      timestamp: p.created_at || incident.created_at,
      chips: [resolveAssigneeDisplayName(t, planningAppUsers, 'Unassigned'), t.timing || 'During-event'],
      status: 'pending', // Initial status for converted action cards
      isActionCard: true,
      meta: {
        actionCardName: t.title,
        performanceStatus: t.isCompleted ? 'Performed' : 'Not Performed',
        scheduledAt: t.scheduled_at,
        tasks: [{
          taskId: `task-${t.id || t.title}`,
          taskText: t.title,
          isCompleted: !!t.isCompleted,
          completedAt: t.completedAt,
          completedBy: t.completedBy
        }],
        assignmentSummary: { label: resolveAssigneeDisplayName(t, planningAppUsers, 'Unassigned') },
        isVirtual: true,
        performedBy: {
          userId: p.creator || 'system',
          userName: p.creator || 'System',
          type: 'user'
        },
        planId: p.id || p._id,
        originalTaskId: t.id || t.title
      }
    };
  });
  });

  // Group items by type
  const groupedItems = timelineGroups.reduce((acc, group) => {
    let items = [];
    if (group.key === 'status_update') {
      items = effectiveTimelineData.filter(
        (item) => item.group === 'status_update' || item.group === 'status_update_2'
      );
    } else if (group.key === 'response_action') {
      items = effectiveTimelineData.filter(
        (item) => item.group === 'response_action' || item.group === 'pre_incident_action'
      );
    } else {
      items = effectiveTimelineData.filter(item => item.group === group.key);
    }

    // Before-event cards keep their backend compatibility key, but share the
    // current Response Actions section instead of a separate legacy heading.
    if (group.key === 'response_action') {
      const injectedTasks = planActionCards.filter(
        (card) => card.group === 'response_action' || card.group === 'pre_incident_action'
      );
      items = [...items, ...injectedTasks];
    }

    // Conditional follow-up injects are rendered nested under their source card, not in their own section.
    items = items.filter((it) => !it?.meta?.conditional);

    // Exercises (scenario / requested / active): order cards within each section by their Fire time
    // (T+X offset measured from activation), earliest first. Non-exercise incidents keep their order.
    if (incident?.is_exercise) {
      items = [...items].sort((a, b) => {
        const fa = Number(a?.meta?.fireOffsetMinutes);
        const fb = Number(b?.meta?.fireOffsetMinutes);
        const va = Number.isFinite(fa) ? fa : Number.MAX_SAFE_INTEGER;
        const vb = Number.isFinite(fb) ? fb : Number.MAX_SAFE_INTEGER;
        if (va !== vb) return va - vb;
        return new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime();
      });
    }

    acc[group.key] = items;
    return acc;
  }, {});

  console.log("groupedItems", groupedItems)

  const primaryTimelineGroups = useMemo(
    () => timelineGroups.filter((group) => {
      if (group.key === 'call_log') return false;
      return true;
    }),
    [timelineGroups]
  );

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrentUserFromStorage = () => {
    try {
      const raw = localStorage.getItem('userData') || localStorage.getItem('user');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const userCanModerateSuggestions = () => {
    const user = getCurrentUserFromStorage();
    const role = String(user?.role || '').toLowerCase();
    return role.includes('admin') || role.includes('manager') || role.includes('leader');
  };

  // Exercise planning: admins can edit/delete cards and set a "fire time"
  // (when the inject/action should trigger) while the exercise is at the "Requested" stage.
  const isExerciseRequestedStage =
    !!incident?.is_exercise &&
    String(incident?.status || '').toLowerCase() === 'requested';
  // A real incident with a scenario plan under review gets the same per-card
  // editing experience as an exercise at the Requested stage (planned cards only).
  const isRealPlanReview = !incident?.is_exercise && incident?.plan_state === 'planned';
  const canEditExerciseTimeline = (isExerciseRequestedStage || isRealPlanReview) && isAdmin;
  const leaderCanEditPendingCard = (item) => Boolean(
    !readOnly && !isAdmin && isDepartmentLeader && leaderOwnedCardIsPending(item, currentUser)
  );
  // Real incidents: admins can correct the reported classification & location
  // from the Report & Detection card while the incident is still open; the
  // backend re-runs scenario matching against the corrected conditions.
  const canEditRealReport = Boolean(
    !incident?.is_exercise
    && isAdmin
    && !['resolved', 'closed', 'cancelled', 'completed', 'false_alarm'].includes(String(incident?.status || '').toLowerCase())
  );
  // Conditional follow-ups are a scenario-planning (role-based) feature.
  const canAuthorConditional = (!!incident?.is_scenario || isExerciseRequestedStage || isRealPlanReview) && isAdmin;
  const groupByKey = (key) => timelineGroups.find(
    (group) => group.key === normalizeTimelineGroupKey(key)
  );
  const normalizedTimelineSearchQuery = normalizeTimelineSearch(searchQuery);
  const conditionalChildrenBySourceId = useMemo(() => {
    const bySource = {};
    (effectiveTimelineData || []).forEach((item) => {
      const sourceId = item?.meta?.conditional?.sourceItemId;
      if (!sourceId) return;
      if (!bySource[sourceId]) bySource[sourceId] = [];
      bySource[sourceId].push(item);
    });
    return bySource;
  }, [effectiveTimelineData]);

  const matchesTimelineItemForView = (item, group) => {
    if (!normalizedTimelineSearchQuery) return true;
    const meta = item?.meta || {};
    const userIds = [
      ...(Array.isArray(meta.recipientIds) ? meta.recipientIds : []),
      ...(Array.isArray(meta.userIds) ? meta.userIds : []),
      ...(Array.isArray(meta.assignedUserIds) ? meta.assignedUserIds : []),
      meta.userId,
    ].filter(Boolean);
    const departmentIds = Array.isArray(meta.departmentIds) ? meta.departmentIds : [];
    const crewIds = Array.isArray(meta.crewIds) ? meta.crewIds : [];
    const resolvedLabels = [
      ...userIds.map((id) => manualNotifyUserNames[id]).filter(Boolean),
      ...departmentIds.map((id) => manualDeptNameById[id]).filter(Boolean),
      ...crewIds.map((id) => manualCrewById[id]).filter(Boolean),
      meta.fireOffsetMinutes !== null && meta.fireOffsetMinutes !== undefined
        ? `Fire at T+${meta.fireOffsetMinutes} T+${meta.fireOffsetMinutes} fires T+${meta.fireOffsetMinutes}m`
        : null,
      meta.sla?.withinMinutes !== null && meta.sla?.withinMinutes !== undefined
        ? `SLA ${meta.sla.withinMinutes}m within ${meta.sla.withinMinutes}m`
        : null,
    ].filter(Boolean);
    return timelineItemMatchesSearch(
      [item, resolvedLabels],
      normalizedTimelineSearchQuery,
      group?.title || ''
    );
  };

  const timelineItemTreeMatchesView = (item, group, visited = new Set()) => {
    if (!item) return false;
    const itemKey = item.id || item;
    if (visited.has(itemKey)) return false;
    const itemMatchesSelectedGroup = filter === 'all'
      || normalizeTimelineGroupKey(item.group) === filter;
    if (itemMatchesSelectedGroup && matchesTimelineItemForView(item, group)) return true;
    const nextVisited = new Set(visited);
    nextVisited.add(itemKey);
    return (conditionalChildrenBySourceId[item.id] || []).some((child) => (
      timelineItemTreeMatchesView(child, groupByKey(child.group) || group, nextVisited)
    ));
  };

  const TimelineItemImpl = ({ item, incident: incidentProp, groupColor, group, isLatest = false, onEdit, onTakeover, allTimelineItems = [] }) => {
    // Scenario-planned cards on a REAL incident behave exactly like exercise
    // injects: requested-stage presentation while planned (no clocks, no
    // counting), exercise-active behavior once the plan — or this card via
    // Fire now — is activated. Present a virtual exercise incident to this
    // card so every requested/active branch below applies verbatim. Live
    // items (report, sent notifications, manual cards) keep the real incident.
    const planManagedCard = incidentProp && !incidentProp.is_exercise &&
      (item?.plan_state === 'planned' || item?.plan_state === 'active');
    const incident = planManagedCard
      ? {
          ...incidentProp,
          is_exercise: true,
          __planVirtual: true, // real incident underneath — hide exercise-only affordances
          status: item?.plan_state === 'planned' ? 'requested' : 'active',
          activated_at: incidentProp.activated_at || item?.plan_activated_at || incidentProp.plan_activated_at,
        }
      : incidentProp;
    // Handle both api data and sample data formats
    const timestamp = item.timestamp || item.created_at;
    const simActivated = !(incident?.is_scenario || String(incident?.status || '').toLowerCase() === 'requested');
    const cardRoles = cleanScenarioRoles(item?.meta?.scenarioRoles);
    const showRoleChip = !simActivated && cardRoles.length > 0;
    const actorName = item.actor?.name || item.actor || 'Unknown';
    const isManualUserNotification = isManualUserNotificationItem(item);
    const isManualDeptCrewNotification = isManualDeptCrewNotificationItem(item);
    const isDepartmentAlertBroadcast = isDepartmentAlertBroadcastItem(item);
    const isGroupedBroadcast = isGroupedBroadcastNotificationItem(item);
    const notificationDisplayName = isManualUserNotification
      ? getManualUserNotificationDisplayName(item, actorName)
      : isGroupedBroadcast
        ? getGroupedBroadcastDisplayName(item, manualDeptNameById, manualCrewById)
        : (item.meta?.userName || actorName);
    const [manualRecipientsExpanded, setManualRecipientsExpanded] = React.useState(false);
    const [attachmentPreview, setAttachmentPreview] = React.useState(null);
    const isReportItem = item.group === 'report' || group?.key === 'report';

    // Resource inject card (exercise-only "Resources" timeline group) — matches standard card style
    const isResourceItem = item.group === RESOURCE_GROUP || group?.key === RESOURCE_GROUP;
    if (isResourceItem) {
      const r = timelineItemToResource(item);
      const av = RESOURCE_AVAILABILITY[r.availability] || RESOURCE_AVAILABILITY.once;
      const totalCost = resourceNum(r.quantity) * resourceNum(r.cost);
      return (
        <div className={`relative pl-6 pb-6 border-l-2 ${groupColor} ml-3`} data-testid={`resource-card-${item.id}`}>
          <div className="timeline-card-focus-surface relative bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow group">
            <CardCornerActions item={item} incident={incident} currentUser={currentUser} isAdmin={isAdmin} />
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                {simActivated && <span className="text-xs font-mono text-gray-500">[{formatTime(timestamp)}]</span>}
                <span className="text-sm font-semibold text-gray-900">{r.category || 'Resource'}</span>
                <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${av.color}`}>{av.label}</span>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2 leading-relaxed flex flex-wrap gap-x-4 gap-y-1">
              <span><span className="font-semibold text-gray-800">Qty:</span> {resourceNum(r.quantity)}</span>
              <span><span className="font-semibold text-gray-800">Unit:</span> {resourceMoney(r.cost)}</span>
              <span><span className="font-semibold text-gray-800">Total:</span> {resourceMoney(totalCost)}</span>
              {r.availability === 'time' && r.replenishEvery ? (
                <span className="text-amber-700">Replenish every {r.replenishEvery} {resourceUnitLabel(r.replenishUnit)}</span>
              ) : null}
              {r.slaMinutes !== '' && r.slaMinutes != null ? (
                <span className="font-semibold text-indigo-600">SLA {r.slaMinutes}m</span>
              ) : null}
            </div>
            <div className="text-xs text-gray-600">
              {showRoleChip ? (
                <RoleChips roles={cardRoles} note={item?.meta?.assignmentNote} />
              ) : (() => {
                // Live linkage summary instead of the old "Allocated to" — how much of
                // this resource is currently linked to action cards / tasks.
                let linked = 0;
                (allTimelineItems || []).forEach((it) => {
                  (it?.meta?.linkedResources || []).forEach((lr) => { if (lr?.resourceId === item.id) linked += Number(lr.quantity) || 0; });
                  (it?.meta?.tasks || []).forEach((t) => (t?.linkedResources || []).forEach((lr) => { if (lr?.resourceId === item.id) linked += Number(lr.quantity) || 0; }));
                });
                const total = Number(r.quantity) || 0;
                const avail = Math.max(0, total - linked);
                return linked > 0
                  ? <span data-testid={`resource-link-summary-${item.id}`}><span className="font-semibold text-gray-800">Linked:</span> {linked} · <span className={avail === 0 ? 'text-red-600 font-semibold' : 'text-emerald-700 font-semibold'}>{avail} available</span></span>
                  : <span className="text-gray-400">Not linked yet — link it from an action card (whole card or a specific task).</span>;
              })()}
            </div>
            <ScenarioRoleChips item={item} incident={incident} />
            <CardAssignedPeople item={item} incident={incident} />
            <ExerciseCardControls item={item} onEdit={onEdit} incident={incident} canEdit={canEditExerciseTimeline || leaderCanEditPendingCard(item)} creatorMode={leaderCanEditPendingCard(item)} onRefresh={fetchTimelineData} allItems={allTimelineItems} />
          </div>
        </div>
      );
    }

    // For Report & Detection, always use incident's created_at as the authoritative timestamp
    const reportTimestamp = (() => {
      if (isReportItem && incident?.is_exercise) {
        return incident?.activated_at || null; // captured at activation
      }
      return isReportItem && incident?.created_at ? incident.created_at : timestamp;
    })();
    const hasEdits = item.meta?.history && item.meta.history.length > 0;
    const [showAllHistory, setShowAllHistory] = React.useState(false);
    const [showChangeHistory, setShowChangeHistory] = React.useState(false); // Collapsed by default

    // Check for missing data in Report & Detection items
    const checkMissingData = (item) => {
      if (!isReportItem) return false;

      const meta = item.meta || {};
      const missingFields = [];

      if (!meta.locationPath || (Array.isArray(meta.locationPath) && meta.locationPath.length === 0)) {
        missingFields.push('Location');
      }
      if (!meta.reporterContact || meta.reporterContact.trim() === '') {
        missingFields.push('Reporter Contact');
      }
      if (!meta.initialDescription || meta.initialDescription.trim() === '') {
        missingFields.push('Description');
      }

      return missingFields.length > 0 ? missingFields : false;
    };

    const missingFields = checkMissingData(item);
    const needsInput = missingFields && missingFields.length > 0;

    // Enhanced Report & Detection Card Layout
    if (isReportItem) {
      const reportIncidentType = item.meta?.incidentType || incident?.incident_type || 'Not specified';
      const reportEvent = item.meta?.event || incident?.event || '';
      const reportTrigger = item.meta?.trigger || incident?.trigger || '';
      const reportLocations = collectReportSummaryLocations(incident, item.meta, locations);
      const reportAttachments = item.meta?.attachments || item.attachments || [];
      const reportAttSig = `${incident?.id || ''}:${reportAttachments.map((a) => a.key || a.id || a.url || '').join('|')}`;
      const [previewAttechment, setPreviewAttechment] = useState(() => reportPreviewCache[reportAttSig] || []);
      const [selectedPreview, setSelectedPreview] = useState(null);

      useEffect(() => {
        // Already resolved for this exact set of attachments → show instantly (no flicker).
        if (reportPreviewCache[reportAttSig]) {
          setPreviewAttechment(reportPreviewCache[reportAttSig]);
          return;
        }
        if (reportAttachments.length === 0) {
          setPreviewAttechment([]);
          return;
        }
        let cancelled = false;
        (async () => {
          const previews = [];
          for (const image of reportAttachments) {
            if (!image?.key) continue;
            try {
              const response = await CommanServices.imagePreview(image.key);
              if (response.status) {
                const data = await response.data;
                previews.push({ ...data, id: image.id || image.key, location_zone_id: incident?.id });
              }
            } catch (error) {
              console.error('Error fetching preview for', image.key, error);
            }
          }
          if (!cancelled) {
            reportPreviewCache[reportAttSig] = previews;
            setPreviewAttechment(previews);
          }
        })();
        return () => { cancelled = true; };
      }, [reportAttSig]);

      return (
        <div className={`relative pl-6 pb-6 border-l-2 ${groupColor} ml-3`}>

          {/* Enhanced Report & Detection Card */}
          <div className="timeline-card-focus-surface relative bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow group">
            {/* Header row - Cleaned up format */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-900">
                    {incident?.is_scenario ? (
                      <span className="inline-flex items-center gap-1.5 font-bold text-emerald-700">
                        <span className="text-base">🧪</span> Scenario Template
                      </span>
                    ) : (
                      <>
                        <span className="font-bold">Incident ID:</span> {incident?.id ? `INC-${incident.id.split('-').pop().substring(0, 8).toUpperCase()}` : 'N/A'}
                      </>
                    )}
                  </div>
                </div>
                {reportTimestamp && (
                  <div className="text-xs font-mono text-gray-500 mb-2">
                    {`[${formatTime(reportTimestamp)} · ${new Date(reportTimestamp).toLocaleDateString('en-GB')}]`}
                  </div>
                )}
                {/* Exercise schedule moved into the card body (after Trigger) */}
              </div>

              {/* Map + discuss/approve (top-right corner); pen only for exercise planning */}
              <div className="absolute top-3 right-3 flex items-center gap-1 z-0">
                {(
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="report-open-map-btn"
                  className="h-6 w-6 p-0 hover:bg-emerald-100 rounded"
                  onClick={() => {
                    const el = document.getElementById('location-map-card');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      navigate(`/map?focus=${incident?.id || ''}`);
                    }
                  }}
                  title="View on map"
                >
                  <MapPin className="h-3.5 w-3.5 text-emerald-600" />
                </Button>
                )}
                {isAdmin && !readOnly && onOpenMapGeneralInfo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="report-general-info-btn"
                    className="h-6 gap-1 px-1.5 text-[10px] font-bold text-indigo-600 hover:bg-indigo-100 rounded"
                    onClick={onOpenMapGeneralInfo}
                    title="Add or edit incident location information"
                  >
                    <Info className="h-3.5 w-3.5" />
                    General Info
                  </Button>
                )}
                {!incident?.__planVirtual && (incident?.is_exercise ? canEditExerciseTimeline : canEditRealReport) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="report-edit-btn"
                    className="opacity-100 transition-opacity h-6 w-6 p-0 hover:bg-indigo-100 rounded"
                    onClick={() => { if (onEdit) onEdit(item); }}
                    title="Edit report details"
                  >
                    <Pencil className="h-3 w-3 text-indigo-600" />
                  </Button>
                )}
                {incident?.is_exercise && (
                  <CardDiscussion incident={incident} itemId={item.id} currentUser={currentUser} label="report" alwaysVisible />
                )}
              </div>
            </div>

            {/* Detailed Information Grid */}
            <div className="space-y-2 text-sm">
              {/* Incident Type */}
              <div className="flex items-start space-x-2">
                <span className="text-base">📋</span>
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <span className="ml-1 text-gray-600">{reportIncidentType}</span>
                </div>
              </div>

              {/* Event */}
              {reportEvent && (
                <div className="flex items-start space-x-2">
                  <span className="text-base">⚡️</span>
                  <div>
                    <span className="font-medium text-gray-700">Event:</span>
                    <span className="ml-1 text-gray-600">{reportEvent}</span>
                  </div>
                </div>
              )}

              {/* Trigger */}
              {reportTrigger && (
                <div className="flex items-start space-x-2">
                  <span className="text-base">🧩</span>
                  <div>
                    <span className="font-medium text-gray-700">Trigger:</span>
                    <span className="ml-1 text-gray-600">{reportTrigger}</span>
                  </div>
                </div>
              )}

              {/* Exercise / Planned Schedule */}

              {reportLocations.length > 0 && (
                <div className="flex items-start space-x-2">
                  <span className="text-base">📍</span>
                  <div className="flex-1">
                    <span className="font-medium text-gray-700">Location:</span>
                    {reportLocations.length === 1 ? (
                      <span className="ml-1 text-gray-600">{reportLocations[0]}</span>
                    ) : (
                      <div className="mt-1 space-y-0.5">
                        {reportLocations.map((locationLabel, locationIdx) => (
                          <div key={locationIdx} className="text-gray-600">
                            {locationLabel}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Impacted Departments (directly below Location) — exercises use the
                  explicitly selected list; real incidents fall back to the
                  departments linked to the reported location. */}
              {(() => {
                const locRel = {};
                const locDeptNames = {};
                (incident?.location || []).forEach((lid) => {
                  const loc = locations.find((l) => l.id === lid);
                  (loc?.departments || []).forEach((da) => {
                    const did = da.department_id || da.id;
                    if (did) {
                      locRel[did] = da.relationship_type;
                      if (da.department_name) locDeptNames[did] = da.department_name;
                    }
                  });
                });
                const selected = Array.isArray(incident?.impacted_departments) ? incident.impacted_departments.filter(Boolean) : [];
                const deptIds = selected.length > 0
                  ? selected
                  : (!incident?.is_exercise ? Object.keys(locRel) : []);
                if (deptIds.length === 0) return null;
                const departmentPool = [
                  ...(Array.isArray(departments) ? departments : []),
                  ...(Array.isArray(cardDepartments) ? cardDepartments : []),
                  ...(Array.isArray(planningDepartments) ? planningDepartments : []),
                ];
                const departmentDetails = deptIds.map((value) => {
                  const directName = value && typeof value === 'object'
                    ? (value.department_name || value.name)
                    : null;
                  const id = value && typeof value === 'object'
                    ? (value.department_id || value.id || value._id)
                    : value;
                  const match = departmentPool.find((department) => (
                    String(department?.id || department?._id || department?.department_id) === String(id)
                    || String(department?.department_name || department?.name) === String(id)
                  ));
                  const name = directName || match?.department_name || match?.name || locDeptNames[id];
                  return {
                    id: String(id || directName || ''),
                    name: name || null,
                    relationship: locRel[id],
                  };
                }).filter((department) => Boolean(department.id && department.name));
                if (departmentDetails.length === 0) return null;
                const uniqueDepartments = Array.from(new Map(
                  departmentDetails.map((department) => [department.id, department])
                ).values());
                const relationshipBadge = (rel) => {
                  if (rel === 'primary') return <span className="px-1.5 py-0 rounded-full text-[9px] font-bold bg-rose-100 text-rose-700 border border-rose-200">Primary</span>;
                  if (rel === 'secondary') return <span className="px-1.5 py-0 rounded-full text-[9px] font-bold bg-sky-100 text-sky-700 border border-sky-200">Secondary</span>;
                  return null;
                };
                return (
                  <div className="ml-6" data-testid="report-impacted-departments">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Building className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Involved Departments</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {uniqueDepartments.map((department) => {
                        return (
                          <span key={department.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-gray-50 border border-gray-200">
                            <span className="font-medium text-gray-800">{department.name}</span>
                            {relationshipBadge(department.relationship)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Initial Description */}
              <div className="flex items-start space-x-2">
                <span className="text-base">📝</span>
                <div className="flex-1">
                  <span className="font-medium text-gray-700">Initial Description:</span>
                  <div className="ml-1 text-gray-600 mt-1 italic">
                    "{item.meta?.initialDescription || item.details || 'No description provided'}"
                  </div>
                </div>
              </div>

              {/* Reported By */}
              <div className="flex items-start space-x-2">
                <span className="text-base">👤</span>
                <div>
                  <span className="font-medium text-gray-700">Reported By:</span>
                  <span className="ml-1 text-gray-600">
                    {item.meta?.reportedBy || incident?.reporter_name || item.actor?.name || incident?.created_by_name || 'Reporter'}
                    {item.actor?.email ? ` (${item.actor.email})` : ''}
                  </span>
                </div>
              </div>

              {/* Reporter Contact (directly under Reported By) */}
              {(item.meta?.reporterContact || incident?.reporter_contact) && (
                <div className="flex items-start space-x-2">
                  <span className="text-base">☎️</span>
                  <div>
                    <span className="font-medium text-gray-700">Contact:</span>
                    <span className="ml-1 text-gray-600">{item.meta?.reporterContact || incident?.reporter_contact}</span>
                  </div>
                </div>
              )}

              {/* Reporter device location (GPS captured at report time) — opens Google Maps.
                  Distinct from the incident's selected location, which the map widget shows. */}
              {(() => {
                const gps = incident?.gps_coordinates || {};
                const lat = gps.latitude ?? gps.lat ?? incident?.gps_latitude;
                const lng = gps.longitude ?? gps.lng ?? incident?.gps_longitude;
                if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return null;
                return (
                  <div className="flex items-start space-x-2">
                    <span className="text-base">📍</span>
                    <div>
                      <span className="font-medium text-gray-700">Reporter location:</span>
                      <a
                        href={`https://maps.google.com/?q=${Number(lat)},${Number(lng)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        data-testid="reporter-location-link"
                        title="Device GPS captured when the incident was reported — opens in Google Maps"
                      >
                        {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
                      </a>
                    </div>
                  </div>
                );
              })()}

              {/* Exercise schedule (moved below Reported By) — hidden once active/completed. */}
              {!incident?.is_scenario && incident?.is_exercise && !['active', 'completed'].includes(String(incident?.status || '').toLowerCase()) && (
                <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-100" data-testid="report-exercise-schedule">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-white shadow-sm bg-emerald-600">
                      <Clock className="w-3 h-3 mr-1" />
                      EXERCISE SCHEDULE
                    </span>
                  </div>
                  <div className="text-xs space-y-1 text-emerald-800">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3 text-emerald-600" />
                      <span className="font-semibold uppercase tracking-wider">Schedule:</span>
                    </div>
                    <div className="ml-5 font-medium">
                      {incident?.planned_windows?.map((window, idx) => (
                        <div key={idx} className="flex flex-col sm:flex-row sm:gap-2 mb-1 last:mb-0">
                          <span>{new Date(window.start).toLocaleString()}</span>
                          <span className="hidden sm:inline">→</span>
                          <span>{(window.end_open || !window.end) ? 'Open' : new Date(window.end).toLocaleString()}</span>
                        </div>
                      )) || (
                          <span>
                            {incident?.planned_start_at ? new Date(incident.planned_start_at).toLocaleString() : 'N/A'}
                            {' → '}
                            {incident?.planned_end_at ? new Date(incident.planned_end_at).toLocaleString() : 'Open'}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Attachments */}
              {(item.meta?.attachments || item.attachments) && (item.meta?.attachments || item.attachments).length > 0 && (
                <div className="flex items-start space-x-2">
                  <span className="text-base">📎</span>
                  <div className="flex-1">
                    <span className="font-medium text-gray-700">Attachments:</span>
                    <div className="ml-1 mt-1 space-y-1">
                      {/* If already added Preview Section */}
                      {previewAttechment.length > 0 && (
                        <div className="flex flex-wrap gap-3">
                          {previewAttechment.map((item, index) => {
                            const src = item.preview_url;
                            const _nm = (item.name || item.key || '').toLowerCase().split('?')[0];
                            const _ext = _nm.includes('.') ? _nm.split('.').pop() : '';
                            const ft = (item.file_type || '').toLowerCase();
                            const isImage = ft.startsWith("image/") || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'].includes(_ext);
                            const isVideo = ft.startsWith("video/") || ['mp4', 'webm', 'ogg', 'mov', 'mkv'].includes(_ext);
                            const isPDF = ft.includes('pdf') || _ext === 'pdf';
                            const isDoc = /(msword|officedocument|presentation|spreadsheet|excel|sheet)/i.test(ft) || ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(_ext);

                            return (
                              <div
                                key={index}
                                className="relative w-14 h-14 rounded-lg overflow-hidden border"
                              >
                                {isImage ? (
                                  <AuthenticatedMedia
                                    as="img"
                                    src={src}
                                    alt={item.name || item.key || 'preview'}
                                    className="object-cover w-full h-full cursor-pointer"
                                    onClick={() => {
                                      setSelectedPreview({
                                        type: item.file_type,
                                        name: item.name || item.key || 'Attachment',
                                        size: item.size,
                                        lastModified: item.lastModified,
                                        src,
                                        _isObjectURL: false
                                      });
                                    }}
                                  />
                                ) : isVideo ? (
                                  <AuthenticatedMedia
                                    as="video"
                                    src={src}
                                    className="object-cover w-full h-full cursor-pointer"
                                    onClick={() => {
                                      setSelectedPreview({
                                        type: item.file_type,
                                        name: item.name || item.key || 'Attachment',
                                        size: item.size,
                                        lastModified: item.lastModified,
                                        src,
                                        _isObjectURL: false
                                      });
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="w-full h-full flex items-center justify-center cursor-pointer bg-gray-50"
                                    onClick={() => {
                                      setSelectedPreview({
                                        type: item.file_type,
                                        name: item.name || item.key || `attachment-${index}`,
                                        size: item.size,
                                        lastModified: item.lastModified,
                                        src,
                                        _isObjectURL: false
                                      });
                                    }}
                                    title={item.name || item.key || 'Attachment'}
                                  >
                                    <div className="text-center text-gray-600">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h6l4 4v6a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                                      </svg>
                                      <div className="text-xs mt-1 px-1">
                                        {isPDF ? 'PDF' : isDoc ? 'DOC' : 'FILE'}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {/* Remove Button */}
                                {/* <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                    removeFileAllreadyAdded(item?.location_zone_id, item?.id);
                                }}
                                className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1"
                              >
                                <X className="w-3 h-3" />
                              </button> */}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Reporter Contact now shown under Reported By above */}
            </div>

            {/* Updated Chips */}
            <div className="flex flex-wrap gap-1 mt-3">
              {/* Remove Auto chip, keep others */}
              {/* {item.chips && item.chips.filter(chip => chip.toLowerCase() !== 'auto').map((chip, index) => {
                let chipClass = "bg-gray-100 text-gray-700";
                if (chip.toLowerCase() === 'edited') chipClass = "bg-blue-100 text-blue-700";
                if (chip.toLowerCase() === 'initial') chipClass = "bg-gray-100 text-gray-700";

                return (
                  <span
                    key={index}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${chipClass}`}
                  >
                    {chip}
                  </span>
                );
              })}
              {hasEdits && !item.chips?.some(chip => chip.toLowerCase() === 'edited') && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  Edited
                </span>
              )} */}
              {/* Chat with reporter chip */}
              {onChatWithReporter &&
                (item.meta?.reportedById || incident?.created_by) &&
                (item.meta?.reportedById || incident?.created_by) !== (currentUser?.id || currentUser?._id) && (
                  <button
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChatWithReporter({
                        id: item.meta?.reportedById || incident?.created_by,
                        name: item.actor?.name || item.meta?.reportedBy || incident?.created_by_name || 'Reporter',
                      });
                    }}
                    title="Start a private conversation with the reporter"
                  >
                    💬 Chat with reporter
                  </button>
                )}
            </div>


          </div>
          {/* Attachment Preview Modal */}
          {selectedPreview && (
            <AttachmentPreviewModal
              attachment={{
                type: selectedPreview.type,
                name: selectedPreview.name,
                size: selectedPreview.size,
                lastModified: selectedPreview.lastModified,
                // AttachmentPreviewModal will use `src` (or preview_url) to render content
                src: selectedPreview.src || selectedPreview.preview_url || selectedPreview.url,
                isObjectURL: selectedPreview._isObjectURL
              }}
              onClose={() => {
                // Revoke object URL if we generated one for local File previews
                if (selectedPreview?._isObjectURL && selectedPreview.src) {
                  try {
                    URL.revokeObjectURL(selectedPreview.src);
                  } catch (err) {
                    /* ignore */
                  }
                }
                setSelectedPreview(null);
              }}
            />
          )}
        </div>
      );
    }

    // Enhanced Acknowledgment Card Layout
    const isAcknowledgment = item.group === 'acknowledgment' || group?.key === 'acknowledgment';
    if (isAcknowledgment) {
      const ackData = item.meta?.acknowledgedBy || item.meta?.ackEntity || {};
      const slaData = item.meta?.sla || {};
      const vStatus = item.meta?.verificationStatus || 'Pending';
      // Phase 1b: exercise SLA/overdue clocks stay dormant until the exercise is Active
      const exerciseClockRunning = !incident?.is_exercise || String(incident?.status || '').toLowerCase() === 'active';
      const fireOffsetMinutes = item?.meta?.fireOffsetMinutes;
      const activatedAtDate = incident?.activated_at ? parseTimestamp(incident.activated_at) : null;
      const hasOffset = Number.isFinite(Number(fireOffsetMinutes));

      // Enrich parallel assignee details from matchedResponders if missing in DB
      const currentLvl = item.meta?.escalationLevel ?? 0;
      const candidatesAtLevel = Array.isArray(matchedResponders) 
        ? matchedResponders.filter(c => (c.escalationLevel ?? 0) === currentLvl) 
        : [];
      
      const assignmentSummary = item.meta?.assignmentSummary || null;
      const assigneesList = item.meta?.assignees || [];
      
      let assignedLabel = assignmentSummary?.label || "";
      if (!assignedLabel && assigneesList.length > 0) {
        assignedLabel = assigneesList.map(u => u.userName || u.name).filter(Boolean).join(', ');
      }
      if (!assignedLabel && candidatesAtLevel.length > 0) {
        assignedLabel = candidatesAtLevel.map(c => c.userName).join(', ');
      }
      if (!assignedLabel) {
        assignedLabel = ackData.userName || item.actor?.name || actorName;
      }

      let assignedUserCount = Number(
        assignmentSummary?.assignedUserCount || 
        item.meta?.assignedUserIds?.length || 
        assigneesList.length || 
        0
      );
      if (assignedUserCount <= 1 && candidatesAtLevel.length > 1) {
        assignedUserCount = candidatesAtLevel.length;
      }

      const getScaledMinutes = (c) => {
        const count = Number(c?.meta?.assignmentSummary?.assignedUserCount || c?.meta?.assignedUserIds?.length || 0);
        const lvl = c?.meta?.escalationLevel ?? 0;
        const matchingCand = Array.isArray(matchedResponders) 
          ? matchedResponders.filter(m => (m.escalationLevel ?? 0) === lvl) 
          : [];
        const mult = Math.max(1, count, matchingCand.length);
        // Cards without an SLA contribute no escalation wait (no 5m fallback).
        return (c?.meta?.sla?.withinMinutes ?? 0) * mult;
      };

      const userMultiplier = Math.max(1, assignedUserCount);
      // No SLA entered → undefined: SLA line and countdown simply don't render.
      const scaledSlaMinutes =
        slaData.withinMinutes != null ? slaData.withinMinutes * userMultiplier : undefined;

      const formatTimeLocal = (timestamp) => {
        return parseTimestamp(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      };

      // Real-time update trigger - force re-render every second
      const [updateTrigger, setUpdateTrigger] = React.useState(0);
      React.useEffect(() => {
        const interval = setInterval(() => {
          setUpdateTrigger(prev => prev + 1);
        }, 1000); // Update every second
        return () => clearInterval(interval);
      }, []);

      // Calculate server time ONCE per render - reuse for all checks to avoid race conditions
      const serverTime = (exerciseRuntime?.paused && exerciseRuntime?.paused_at) ? parseTimestamp(exerciseRuntime.paused_at) : new Date();

      // Time-based card visibility check
      const notifiedAt = item.meta?.notifiedAt ? parseTimestamp(item.meta.notifiedAt) : null;
      const isNotificationDue = !notifiedAt || notifiedAt <= serverTime;

      // Don't render card if notification time hasn't come yet (Phase 3 requirement)
      // Skip this check for now - will be enabled in Phase 3
      // if (!isNotificationDue) return null;

      return (
        <div className={`relative pl-6 pb-6 border-l-2 ${groupColor} ml-3`}>
          {/* Acknowledgment Card */}
          <div className="timeline-card-focus-surface relative bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow group">
            <CardCornerActions item={item} incident={incident} currentUser={currentUser} onTakeover={!String(item.meta?.verificationStatus || '').toLowerCase().includes('suspend') ? onTakeover : undefined} withTakeover={!!onEdit && !item.isVirtual && !item.meta?.superseded} isAdmin={isAdmin} />
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                {/* Fire time countdown → then Notified at (activation + offset) */}
                {simActivated && !item.meta?.offDuty && (
                  <FireTimeCountdown item={item} incident={incident} paused={exerciseRuntime?.paused} pausedAt={exerciseRuntime?.paused_at} />
                )}
                <CardAssignedPeople item={item} incident={incident} />

                {/* Escalated at: show for escalated cards (Card #2+) when previous card is unverified */}
                {(() => {
                  // Escalation chains were removed everywhere: no "Escalated at:" line.
                  return null;
                  /* eslint-disable no-unreachable */
                  // 🛑 Don't show "Escalated at:" if this card is superseded
                  if (item.meta?.superseded) {
                    return null;
                  }
                  // 🛑 Conditional follow-ups are a separate branch, not part of the escalation chain.
                  if (item.meta?.conditional) {
                    return null;
                  }

                  const allAckCards = allTimelineItems.filter(t => t.group === 'acknowledgment' && !t.meta?.conditional);
                  const sortedAckCards = allAckCards.sort((a, b) => {
                    const aWithin = getScaledMinutes(a);
                    const bWithin = getScaledMinutes(b);
                    return aWithin - bWithin;
                  });
                  const currentIndex = sortedAckCards.findIndex(c => c.id === item.id);

                  // Show "Escalated at" only for Card #2+ (not first card)
                  if (currentIndex > 0) {
                    // Check if previous card shows "🔴 Unverified" chip
                    const previousCard = sortedAckCards[currentIndex - 1];
                    const previousVerificationStatus = previousCard?.meta?.verificationStatus || 'Pending';
                    const incidentReportedAt = parseTimestamp(incident.created_at);

                    // Calculate verification status for previous card (must match Verification Status chip logic)
                    let previousElapsed, previousWithinMinutes;

                    if (previousCard?.meta?.escalatedAt && previousCard?.meta?.manualEscalation) {
                      // Manual escalation case
                      const manualEscalatedTime = parseTimestamp(previousCard.meta.escalatedAt);
                      previousElapsed = Math.floor((serverTime - manualEscalatedTime) / 60000);
                      previousWithinMinutes = 0; // Manual escalation shows as late immediately
                    } else {
                      // Normal timing calculation for Card #1
                      if (currentIndex === 1) {
                        // Previous is Card #1 - calculate from incident time
                        previousElapsed = Math.floor((serverTime - incidentReportedAt) / 60000);
                        previousWithinMinutes = getScaledMinutes(previousCard);
                      } else {
                        // Previous is Card #2+ - calculate from its escalation start time
                        let cumulativeForPrevious = 0;
                        for (let i = 0; i < currentIndex - 1; i++) {
                          cumulativeForPrevious += getScaledMinutes(sortedAckCards[i]);
                        }
                        const previousEscalationStartTime = new Date(incidentReportedAt.getTime() + cumulativeForPrevious * 60000);
                        previousElapsed = Math.floor((serverTime - previousEscalationStartTime) / 60000);
                        previousWithinMinutes = getScaledMinutes(previousCard);
                      }
                    }

                    const previousRemaining = previousWithinMinutes - previousElapsed;

                    // Show "Escalated at" when escalation already happened for previous card
                    // This should show regardless of whether previous card is now verified or still pending
                    // Check: Did the previous card's SLA expire? If yes, escalation happened.
                    const escalationHappened = previousRemaining <= 0;

                    if (escalationHappened) {
                      // Calculate escalated at time
                      let escalatedAtTime;
                      if (previousCard?.meta?.escalatedAt) {
                        // Manual escalation
                        escalatedAtTime = previousCard.meta.escalatedAt;
                      } else {
                        // Automatic escalation - when previous card verification went late
                        let cumulativeForPrevious = 0;
                        for (let i = 0; i < currentIndex - 1; i++) {
                          cumulativeForPrevious += getScaledMinutes(sortedAckCards[i]);
                        }
                        const prevEscalationStart = new Date(incidentReportedAt.getTime() + cumulativeForPrevious * 60000);
                        const escalatedAtDate = new Date(prevEscalationStart.getTime() + previousWithinMinutes * 60000);
                        escalatedAtTime = escalatedAtDate.toISOString();
                      }

                      return (
                        <div className="text-xs font-mono text-gray-500 mb-1">
                          Escalated at: {formatTimeLocal(escalatedAtTime)} · {parseTimestamp(escalatedAtTime).toLocaleDateString('en-GB')}
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {showRoleChip ? (
                    <RoleChips roles={cardRoles} note={item?.meta?.assignmentNote} />
                  ) : cardAssignedByRole(item).length > 0 ? null : (
                    <>
                      Assigned To: {assignedLabel}
                      {assignedUserCount > 1 && (
                        <span className="text-xs font-normal text-gray-500 ml-1">({assignedUserCount} assigned users)</span>
                      )}
                      {assignedUserCount <= 1 && item.actor?.email ? (
                        <span className="text-xs font-normal text-gray-500 ml-1">({item.actor.email})</span>
                      ) : ''}
                    </>
                  )}
                </div>
                {(ackData.crewName || ackData.teamName) && (
                  <div className="text-xs text-gray-500 mt-1">
                    {[ackData.crewName, ackData.teamName].filter(Boolean).join(' • ')}
                  </div>
                )}
              </div>
            </div>

            {/* Content rows */}
            <div className="space-y-2 text-sm">
              {/* Verification Status - Exact UI clone of Acknowledgment Status chip */}
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-700">Verification Status:</span>
                {item.meta?.offDuty ? (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                      ⚪ Skipped
                    </span>
                    {item.meta?.forced && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-300">
                        🔵 Force Notification
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {(() => {
                      // 🛑 CHECK IF SUPERSEDED FIRST
                      if (item.meta?.superseded) {
                        return (
                          <div className="flex flex-col space-y-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Superseded
                            </span>
                          </div>
                        );
                      }

                      const vStatus = item.meta?.verificationStatus || 'Pending';

                      // EXERCISES: each verification fires at its own T+offset and is judged against
                      // its own SLA measured FROM its fire/notified time — never the global
                      // incident.created_at and never a cumulative escalation chain. This prevents a
                      // just-activated drill from instantly showing "🔴 Unverified (late …)" on a card
                      // that hasn't even fired yet.
                      if (incident?.is_exercise) {
                        const fireTime = item.meta?.notifiedAt ? parseTimestamp(item.meta.notifiedAt) : cardFireTime(item, incident);
                        const slaMin = scaledSlaMinutes;
                        if (vStatus === 'Confirmed' || vStatus === 'False Alarm') {
                          const verifiedAt = item.meta?.verifiedAt ? parseTimestamp(item.meta.verifiedAt) : null;
                          let lateSuffix = '';
                          if (verifiedAt && fireTime) {
                            const lateMin = Math.floor((verifiedAt - fireTime) / 60000) - slaMin;
                            if (lateMin > 0) lateSuffix = ` (late ${lateMin}m)`;
                          }
                          return vStatus === 'Confirmed' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">🟢 Confirmed{lateSuffix}</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">⚫️ False Alarm{lateSuffix}</span>
                          );
                        }
                        // Pending — dormant before activation
                        if (!exerciseClockRunning) {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              ⏳ Starts on activation{hasOffset ? ` · fires T+${fireOffsetMinutes}m` : ''}{slaData.withinMinutes != null ? ` · SLA ${slaData.withinMinutes}m` : ''}
                            </span>
                          );
                        }
                        // Activated but the card hasn't fired yet → just pending (FireTimeCountdown shows "Fires in …")
                        if (fireTime && serverTime < fireTime) {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">🟡 Pending</span>
                          );
                        }
                        // Fired → judge against SLA counted from the fire time
                        const elapsed = fireTime ? Math.floor((serverTime - fireTime) / 60000) : 0;
                        const remaining = slaMin - elapsed;
                        if (remaining <= 0) {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">🔴 Unverified (late {Math.abs(remaining)}m)</span>
                          );
                        }
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">🟡 Pending</span>
                        );
                      }

                      // CORRECTED ACTIVATION LOGIC (same as Acknowledgment Status)
                      const allAckCards = allTimelineItems.filter(t => t.group === 'acknowledgment');
                      const sortedAckCards = allAckCards.sort((a, b) => {
                        const aWithin = getScaledMinutes(a);
                        const bWithin = getScaledMinutes(b);
                        return aWithin - bWithin;
                      });

                      // Find this card's position
                      const currentIndex = sortedAckCards.findIndex(c => c.id === item.id);

                      // Rule 1: Card #1 (first card) is ALWAYS active
                      if (currentIndex === 0) {
                        // Card #1: Always show normal chips (never gray)
                        if (vStatus === 'Confirmed' || vStatus === 'False Alarm') {
                          // Check if card was late when verification was set
                          const verifiedAt = item.meta?.verifiedAt ? parseTimestamp(item.meta.verifiedAt) : null;
                          const incidentReportedAt = parseTimestamp(item.meta?.notifiedAt || incident.created_at);
                          const withinMinutes = scaledSlaMinutes;

                          let lateSuffix = '';
                          if (verifiedAt) {
                            // Calculate if it was late at verification time
                            const elapsedAtVerification = Math.floor((verifiedAt - incidentReportedAt) / 60000);
                            const lateMinutes = elapsedAtVerification - withinMinutes;

                            if (lateMinutes > 0) {
                              lateSuffix = ` (late ${lateMinutes}m)`;
                            }
                          }

                          if (vStatus === 'Confirmed') {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                🟢 Confirmed{lateSuffix}
                              </span>
                            );
                          } else {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                ⚫️ False Alarm{lateSuffix}
                              </span>
                            );
                          }
                        } else {
                          // Pending - dormant until the exercise is activated
                          if (incident?.is_exercise && !exerciseClockRunning) {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                ⏳ Starts on activation{!simActivated ? `${Number.isFinite(Number(fireOffsetMinutes)) ? ` · fires T+${fireOffsetMinutes}m` : ''}${slaData.withinMinutes != null ? ` · SLA ${slaData.withinMinutes}m` : ''}` : ''}
                              </span>
                            );
                          }
                          // Pending - check if late (must match AcknowledgmentCountdown logic)
                          const incidentReportedAt = parseTimestamp(item.meta?.notifiedAt || incident.created_at);

                          // Check if this is a manual escalation
                          let elapsed, withinMinutes;
                          if (item.meta?.escalatedAt && item.meta?.manualEscalation) {
                            // Manual escalation - calculate from escalatedAt time
                            const manualEscalatedTime = parseTimestamp(item.meta.escalatedAt);
                            elapsed = Math.floor((serverTime - manualEscalatedTime) / 60000);
                            // For manual escalation, show as late immediately (withinMinutes = 0)
                            withinMinutes = 0;
                          } else {
                            // Normal timing calculation
                            elapsed = Math.floor((serverTime - incidentReportedAt) / 60000);
                            withinMinutes = scaledSlaMinutes;
                          }

                          const remaining = withinMinutes - elapsed;

                          if (remaining <= 0) {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                🔴 Unverified (late {Math.abs(remaining)}m)
                              </span>
                            );
                          } else {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                🟡 Pending
                              </span>
                            );
                          }
                        }
                      }

                      // Rule 2: Cards #2+ check if PREVIOUS card's Verification is "Unverified (late)"
                      const previousCard = sortedAckCards[currentIndex - 1];
                      const previousVerificationStatus = previousCard?.meta?.verificationStatus || 'Pending';

                      // Check if previous card is "Unverified (late)" - MUST match Verification Status chip logic exactly
                      const incidentReportedAt = parseTimestamp(incident.created_at);

                      // Calculate previous card's escalation start time CUMULATIVELY
                      // Card #1 (index 0): starts at incident time
                      // Card #2 (index 1): starts at incident + Card#1 WITHIN
                      // Card #3 (index 2): starts at incident + Card#1 WITHIN + Card#2 WITHIN
                      const previousIndex = currentIndex - 1;
                      let cumulativeMinutes = 0;

                      // Sum up all WITHIN minutes of cards before the previous card
                      for (let i = 0; i < previousIndex; i++) {
                        cumulativeMinutes += getScaledMinutes(sortedAckCards[i]);
                      }

                      const previousEscalationStartTime = new Date(incidentReportedAt.getTime() + cumulativeMinutes * 60000);

                      // Use shared serverTime to avoid race conditions
                      const elapsedMinutes = Math.floor((serverTime - previousEscalationStartTime) / 60000);
                      const previousWithinMinutes = getScaledMinutes(previousCard);
                      const previousRemaining = previousWithinMinutes - elapsedMinutes;
                      // Use <= 0 to match Verification Status chip exactly
                      // When Verification shows "🔴 Unverified (late 0m)", escalation triggers immediately
                      const isPreviousLate = previousVerificationStatus === 'Pending' && previousRemaining <= 0;
                      const isPreviousUnverified = previousVerificationStatus === 'False Alarm';

                      // Escalation trigger: Previous card is Unverified (late) OR False Alarm
                      const isEscalated = isPreviousLate || isPreviousUnverified;

                      // Check if THIS card's own SLA has expired
                      const incReportedAt2 = parseTimestamp(incident.created_at);
                      let cumulativeMinutesForThisCard2 = 0;
                      for (let i = 0; i < currentIndex; i++) {
                        cumulativeMinutesForThisCard2 += getScaledMinutes(sortedAckCards[i]);
                      }
                      const thisCardEscalationStartTime2 = new Date(incReportedAt2.getTime() + cumulativeMinutesForThisCard2 * 60000);
                      const thisCardElapsed2 = Math.floor((serverTime - thisCardEscalationStartTime2) / 60000);
                      const thisCardSLA2 = scaledSlaMinutes;
                      const thisCardRemaining2 = thisCardSLA2 - thisCardElapsed2;
                      const thisCardIsLate2 = thisCardRemaining2 <= 0;

                      // PRE-ESCALATION: Show yellow "Pending" only if:
                      // 1. Previous card hasn't escalated yet, AND
                      // 2. This card's own SLA hasn't expired yet
                      if (!isEscalated && !thisCardIsLate2 && vStatus === 'Pending') {
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            🟡 Pending
                          </span>
                        );
                      }

                      // POST-ESCALATION: Normal chip logic
                      if (vStatus === 'Confirmed' || vStatus === 'False Alarm') {
                        // Check if card was late when verification was set (relative to escalation time)
                        const verifiedAt = item.meta?.verifiedAt ? parseTimestamp(item.meta.verifiedAt) : null;
                        const incidentReportedAt = parseTimestamp(incident.created_at);

                        // Calculate escalation start time cumulatively
                        let cumulativeMinutesForThisCard = 0;
                        for (let i = 0; i < currentIndex; i++) {
                          cumulativeMinutesForThisCard += getScaledMinutes(sortedAckCards[i]);
                        }
                        const escalationStartTime = item?.meta?.notifiedAt
                          ? parseTimestamp(item.meta.notifiedAt)
                          : new Date(incidentReportedAt.getTime() + cumulativeMinutesForThisCard * 60000);
                        const withinMinutes = scaledSlaMinutes;

                        let lateSuffix = '';
                        if (verifiedAt) {
                          // Calculate if it was late at verification time (relative to escalation)
                          const elapsedAtVerification = Math.floor((verifiedAt - escalationStartTime) / 60000);
                          const lateMinutes = elapsedAtVerification - withinMinutes;

                          if (lateMinutes > 0) {
                            lateSuffix = ` (late ${lateMinutes}m)`;
                          }
                        }

                        if (vStatus === 'Confirmed') {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              🟢 Confirmed{lateSuffix}
                            </span>
                          );
                        } else {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              ⚫️ False Alarm{lateSuffix}
                            </span>
                          );
                        }
                      } else {
                        // Pending - dormant until the exercise is activated
                        if (incident?.is_exercise && !exerciseClockRunning) {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              ⏳ Starts on activation
                            </span>
                          );
                        }
                        // Pending - calculate time from escalation moment CUMULATIVELY
                        // Calculate THIS card's escalation start time cumulatively
                        let cumulativeMinutesForThisCard = 0;

                        // Sum up all WITHIN minutes of cards BEFORE this card
                        for (let i = 0; i < currentIndex; i++) {
                          cumulativeMinutesForThisCard += getScaledMinutes(sortedAckCards[i]);
                        }

                        const escalationStartTime = item?.meta?.notifiedAt
                          ? parseTimestamp(item.meta.notifiedAt)
                          : new Date(incidentReportedAt.getTime() + cumulativeMinutesForThisCard * 60000);
                        const elapsed = Math.floor((serverTime - escalationStartTime) / 60000);
                        const withinMinutes = scaledSlaMinutes;
                        const remaining = withinMinutes - elapsed;

                        if (remaining <= 0) {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              🔴 Unverified (late {Math.abs(remaining)}m)
                            </span>
                          );
                        } else {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              🟡 Pending
                            </span>
                          );
                        }
                      }
                    })()}
                  </>
                )}
              </div>

              {/* Off duty note */}
              {item.meta?.offDuty && (
                <div className="text-xs text-gray-500 italic">
                  {item.meta?.userName || actorName} is off duty now.
                </div>
              )}

              {/* SLA Info */}
              {simActivated && slaData.withinMinutes != null && (
                <div className="text-xs text-gray-500 mt-1">
                  <Clock className="inline h-3 w-3 mr-1" />
                  <span className="font-medium">SLA:</span> {slaData.withinMinutes} minute{slaData.withinMinutes !== 1 ? 's' : ''}{assignedUserCount > 1 ? ' each' : ''}
                </div>
              )}

              {/* Countdown Timer for pending cards (dormant until exercise active) */}
              {vStatus === 'Pending' && !item.meta?.superseded && incident?.is_exercise && !exerciseClockRunning && simActivated && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100">
                    <Clock className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">
                      ⏳ Starts on activation{Number.isFinite(Number(fireOffsetMinutes)) ? ` · fires T+${fireOffsetMinutes}m` : ''}{slaData.withinMinutes != null ? ` · SLA ${slaData.withinMinutes}m` : ''}
                    </span>
                  </div>
                </div>
              )}
              {vStatus === 'Pending' && !item.meta?.superseded && notifiedAt && exerciseClockRunning && (
                (() => {
                  // For exercises, the clock starts at activation + the card's fire offset
                  const fireTime = (incident?.is_exercise && activatedAtDate && hasOffset)
                    ? new Date(activatedAtDate.getTime() + Number(fireOffsetMinutes) * 60000)
                    : notifiedAt;
                  // Not yet fired (waiting for its T+offset after activation)
                  if (fireTime > serverTime) {
                    const untilFire = fireTime - serverTime;
                    const fm = Math.floor(untilFire / 60000);
                    const fs = Math.floor((untilFire % 60000) / 1000);
                    return (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-50">
                          <Clock className="h-4 w-4 text-indigo-600" />
                          <span className="text-sm font-medium text-indigo-800">⏳ Fires in {fm}m {fs}s{slaData.withinMinutes != null ? ` · then SLA ${slaData.withinMinutes}m` : ''}</span>
                        </div>
                      </div>
                    );
                  }
                  // No SLA entered → nothing to count down.
                  if (scaledSlaMinutes === undefined) return null;
                  const slaDeadline = new Date(fireTime.getTime() + scaledSlaMinutes * 60 * 1000);
                  const timeRemaining = slaDeadline - serverTime;
                  const minutesRemaining = Math.floor(timeRemaining / 60000);
                  const secondsRemaining = Math.floor((timeRemaining % 60000) / 1000);
                  const isOverdue = timeRemaining < 0;
                  const isWarning = timeRemaining < 120000 && timeRemaining > 0; // Less than 2 minutes

                  return (
                    <div className="mt-3 space-y-2">
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${
                        isOverdue ? 'bg-red-100' : isWarning ? 'bg-yellow-100' : 'bg-blue-100'
                      }`}>
                        <Clock className={`h-4 w-4 ${isOverdue ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-blue-600'}`} />
                        <span className={`text-sm font-medium ${isOverdue ? 'text-red-800' : isWarning ? 'text-yellow-800' : 'text-blue-800'}`}>
                          {isOverdue ? (
                            `⚠️ Overdue by ${Math.abs(minutesRemaining)}m ${Math.abs(secondsRemaining)}s`
                          ) : (
                            `⏱️ ${minutesRemaining}m ${secondsRemaining}s remaining`
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })()
              )}

              {/* Modes */}
              {item.meta?.modes && item.meta.modes.length > 0 && (
                <div className="flex items-start space-x-2">
                  <span className="font-medium text-gray-700">Modes:</span>
                  <div className="flex flex-wrap gap-1">
                    {item.meta.modes.map((mode, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {mode}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {item.meta?.notes && (
                <div className="flex items-start space-x-2">
                  <span className="font-medium text-gray-700">Notes:</span>
                  <span className="text-gray-600 flex-1">
                    {item.meta.notes.length > 100 ? item.meta.notes.substring(0, 100) + '...' : item.meta.notes}
                  </span>
                </div>
              )}

              {/* Assessment Notes */}
              {item.meta?.assessmentNotes && (
                <div className="flex items-start space-x-2">
                  <span className="font-medium text-gray-700">Assessment Notes:</span>
                  <span className="text-gray-600 flex-1">
                    {item.meta.assessmentNotes.length > 150 ? item.meta.assessmentNotes.substring(0, 150) + '...' : item.meta.assessmentNotes}
                  </span>
                </div>
              )}
            </div>

            {/* Footer chips */}
            {item.meta?.manualEscalation && !item.meta?.superseded && (
              <div className="flex flex-wrap gap-1 mt-3">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  ⚡ Manually Escalated
                </span>
              </div>
            )}



            {/* Next Escalation Target Section */}
            {(() => {
              // Escalation is a live-runtime concept — hide entirely on the scenario / requested (pre-activation) page
              if (!simActivated) return null;
              // Real incidents dropped escalation rules: no chain UI at all.
              if (!incident?.is_exercise || incident?.__planVirtual) return null;
              const currentEscalationLevel = item.meta?.escalationLevel ?? 0;
              const currentVerificationStatus = item.meta?.verificationStatus || 'Pending';

              // Exercises: no escalation chain UI on verification cards — only the verified
              // success banner once a card is Confirmed / False Alarm.
              if (incident?.is_exercise && currentVerificationStatus !== 'Confirmed' && currentVerificationStatus !== 'False Alarm') {
                return null;
              }

              // 🛑 Hide if this card is superseded
              if (item.meta?.superseded) {
                return null;
              }

              // 🛑 Hide if this card is verified (Confirmed or False Alarm)
              // Once verified, card should either show success banner OR escalation section for late cards
              // But never the "Next Escalation Target" countdown
              if (currentVerificationStatus === 'Confirmed' || currentVerificationStatus === 'False Alarm') {
                // This card is finalized - check if it was verified on time or late
                const allAckCards = allTimelineItems.filter(t => t.group === 'acknowledgment');
                const sortedByWithin = [...allAckCards].sort((a, b) => {
                  const aWithin = getScaledMinutes(a);
                  const bWithin = getScaledMinutes(b);
                  return aWithin - bWithin;
                });

                const currentWithinMinutes = scaledSlaMinutes;
                const nextLevelCard = sortedByWithin.find(t =>
                  getScaledMinutes(t) > currentWithinMinutes
                );

                // Check if this card was verified on time or late
                // IMPORTANT: Use verifiedAt timestamp, not current serverTime
                const verifiedAt = item.meta?.verifiedAt ? parseTimestamp(item.meta.verifiedAt) : serverTime;
                const incidentReportedAt = parseTimestamp(incident.created_at);
                const sortedAckCards = allAckCards.sort((a, b) => {
                  const aWithin = getScaledMinutes(a);
                  const bWithin = getScaledMinutes(b);
                  return aWithin - bWithin;
                });
                const currentIndex = sortedAckCards.findIndex(c => c.id === item.id);
                let cumulativeMinutesForThisCard = 0;
                for (let i = 0; i < currentIndex; i++) {
                  cumulativeMinutesForThisCard += getScaledMinutes(sortedAckCards[i]);
                }
                const escalationStartTime = item?.meta?.notifiedAt
                          ? parseTimestamp(item.meta.notifiedAt)
                          : new Date(incidentReportedAt.getTime() + cumulativeMinutesForThisCard * 60000);
                const elapsedAtVerification = Math.floor((verifiedAt - escalationStartTime) / 60000);
                const currentWithinMinutesForCheck = scaledSlaMinutes;
                const wasLate = elapsedAtVerification > currentWithinMinutesForCheck;

                // Only show success banner if this card didn't escalate (wasn't late or is last card)
                const isLastCard = !nextLevelCard;
                const shouldShowSuccessBanner = incident?.is_exercise || isLastCard || !wasLate;

                if (shouldShowSuccessBanner) {
                  const verifierName = item.meta?.acknowledgedBy?.userName || 'Unknown';
                  const verifiedAtRaw = item.meta?.verifiedAt || item.meta?.acknowledgedAt || null;
                  const verifiedAt = verifiedAtRaw ? parseTimestamp(verifiedAtRaw) : null;
                  const timeStr = verifiedAt
                    ? verifiedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                    : '—';
                  const dateStr = verifiedAt
                    ? verifiedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '';

                  return (
                    <div className="mt-4 pt-3 border-t border-green-200 bg-green-50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                      <div className="flex items-center space-x-2 text-xs text-green-800">
                        <span className="font-semibold">✅ Verified by {item.actor?.name || verifierName}{item.actor?.email ? ` (${item.actor.email})` : ''} at {timeStr}{dateStr ? ` · ${dateStr}` : ''}</span>
                      </div>
                    </div>
                  );
                }

                // Escalation chain visuals are an exercise feature — real incidents dropped escalation rules.
                if (!incident?.is_exercise || incident?.__planVirtual) return null;
                if (wasLate && nextLevelCard) {
                  const nextUserName = nextLevelCard.meta?.acknowledgedBy?.userName || 'Unknown';
                  const nextTeamName = nextLevelCard.meta?.acknowledgedBy?.teamName || '';
                  const nextCrewName = nextLevelCard.meta?.acknowledgedBy?.crewName || '';

                  return (
                    <div className="mt-4 pt-3 border-t border-red-200 bg-red-50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                      <div className="text-xs font-semibold text-gray-700 mb-1">
                        🔴 Escalated To:
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">{nextUserName}</div>
                          {(nextTeamName || nextCrewName) && (
                            <div className="text-xs text-gray-600 mt-0.5">
                              {[nextCrewName, nextTeamName].filter(Boolean).join(' • ')}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          <span className="font-medium text-red-700">Escalated</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // If we reach here, return null (no escalation section for finalized cards)
                return null;
              }

              // From here, only Pending cards should show "Next Escalation Target"
              if (currentVerificationStatus !== 'Pending') {
                return null;
              }

              // Get all acknowledgment cards for escalation logic
              const allAckCards = allTimelineItems.filter(t => t.group === 'acknowledgment');

              // Sort cards by WITHIN response time (ascending)
              const sortedByWithin = [...allAckCards].sort((a, b) => {
                const aWithin = getScaledMinutes(a);
                const bWithin = getScaledMinutes(b);
                return aWithin - bWithin;
              });

              // Find next card by WITHIN time
              const currentWithinMinutes = scaledSlaMinutes;
              const nextLevelCard = sortedByWithin.find(t =>
                getScaledMinutes(t) > currentWithinMinutes
              );

              // This is the last escalation level if it has the longest WITHIN time
              const isLastEscalationLevel = !nextLevelCard;

              // If this is the last card by WITHIN time and verification is pending, show "Last escalation level"
              if (isLastEscalationLevel) {
                return null;
              }

              // Show next escalation target if it exists (only for Pending cards)
              if (nextLevelCard) {
                const nextUserName = nextLevelCard.meta?.acknowledgedBy?.userName || 'Unknown';
                const nextTeamName = nextLevelCard.meta?.acknowledgedBy?.teamName || '';
                const nextCrewName = nextLevelCard.meta?.acknowledgedBy?.crewName || '';

                // Check if current card's Verification Status is "Unverified (late)"
                // For escalated cards, calculate from escalation start time, not incident report time
                const incidentReportedAt = parseTimestamp(incident.created_at);
                // Use shared serverTime

                // Find if this is an escalated card by checking its position
                const sortedAckCards = allAckCards.sort((a, b) => {
                  const aWithin = getScaledMinutes(a);
                  const bWithin = getScaledMinutes(b);
                  return aWithin - bWithin;
                });
                const currentIndex = sortedAckCards.findIndex(c => c.id === item.id);

                // Calculate THIS card's escalation start time CUMULATIVELY
                let cumulativeMinutesForThisCard = 0;
                for (let i = 0; i < currentIndex; i++) {
                  cumulativeMinutesForThisCard += getScaledMinutes(sortedAckCards[i]);
                }
                const escalationStartTime = item?.meta?.notifiedAt
                          ? parseTimestamp(item.meta.notifiedAt)
                          : new Date(incidentReportedAt.getTime() + cumulativeMinutesForThisCard * 60000);

                const elapsed = Math.floor((serverTime - escalationStartTime) / 60000);
                const currentWithinMinutes = scaledSlaMinutes;
                const currentRemaining = currentWithinMinutes - elapsed;

                // Check if escalation happened (only for Pending cards now)
                const hasEscalated = currentRemaining <= 0;

                return (
                  <div className={`mt-4 pt-3 border-t ${hasEscalated ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'} -mx-4 -mb-4 px-4 py-3 rounded-b-lg`}>
                    <div className="text-xs font-semibold text-gray-700 mb-1">
                      {hasEscalated ? '🔴 Escalated To:' : '⏳ Next Escalation Target:'}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">{nextUserName}</div>
                        {(nextTeamName || nextCrewName) && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            {[nextCrewName, nextTeamName].filter(Boolean).join(' • ')}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        {hasEscalated ? (
                          <span className="font-medium text-red-700">Escalated</span>
                        ) : (
                          <EscalationCountdown
                            targetTime={new Date(escalationStartTime.getTime() + currentWithinMinutes * 60000).toISOString()}
                          />
                        )}
                      </div>
                    </div>
                    {!hasEscalated && (
                      <div className="text-xs text-gray-500 mt-1">
                        Will escalate when this card becomes late
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })()}
            <ScenarioRoleChips item={item} incident={incident} />
            <ExerciseCardControls item={item} onEdit={onEdit} incident={incident} canEdit={canEditExerciseTimeline || leaderCanEditPendingCard(item)} creatorMode={leaderCanEditPendingCard(item)} onRefresh={fetchTimelineData} allItems={allTimelineItems} />
          </div>
        </div>
      );
    }

    // Enhanced Response Action Card Layout
    const isResponseAction = item.group === 'response_action' || group?.key === 'response_action' || item.group === 'pre_incident_action' || group?.key === 'pre_incident_action';
    if (isResponseAction) {
      const performedBy = item.meta?.performedBy || {};
      const slaData = item.meta?.sla || {};
      const tasks = item.meta?.tasks || [];
      const assignmentSummary = item.meta?.assignmentSummary || null;
      const assignedLabel = assignmentSummary?.label
        || (performedBy?.userName ? `User: ${performedBy.userName}` : 'User: Unknown');
      const assignedUserCount = Number(assignmentSummary?.assignedUserCount || item.meta?.assignedUserIds?.length || 0);
      const extractSuggestionCandidates = (meta = {}) => {
        const pickArray = (value) => {
          if (Array.isArray(value)) return value;
          if (value && typeof value === 'object') return Object.values(value);
          return [];
        };

        const candidates = [
          ...pickArray(meta.suggestedTasks),
          ...pickArray(meta.taskSuggestions),
          ...pickArray(meta.suggestions),
          ...pickArray(meta.suggested_actions),
          ...pickArray(meta.suggestedActions),
          ...pickArray(meta.proposedTasks),
          ...pickArray(meta.proposals)
        ];

        // Some apps store suggested items inside tasks with "pending approval" style statuses.
        if (Array.isArray(meta.tasks)) {
          const taskBacked = meta.tasks.filter((t) => {
            const source = String(t?.source || '').toLowerCase();
            const approval = String(t?.approvalStatus || t?.suggestionStatus || '').toLowerCase();
            return (
              source.includes('suggest') ||
              approval.includes('pending') ||
              approval.includes('suggest')
            );
          });
          candidates.push(...taskBacked);
        }

        return candidates;
      };

      const suggestedTasks = extractSuggestionCandidates(item.meta || {});
      const actionCardName = item.meta?.actionCardName || item.title || 'Response Action';
      const performanceStatus = item.meta?.performanceStatus || 'Not Performed';
      const isSuperseded = item.meta?.isSuperseded || false;
      // Keep moderation available for all web users for now.
      // Some environments don't persist role in localStorage consistently.
      const canModerateSuggestions = true;
      const [processingSuggestionId, setProcessingSuggestionId] = React.useState(null);

      const formatTimeLocal = (timestamp) => {
        if (!timestamp) return '--';
        // Handle both Date objects and strings
        const date = timestamp instanceof Date ? timestamp : parseTimestamp(timestamp);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      };

      // Real-time update trigger
      const [updateTrigger, setUpdateTrigger] = React.useState(0);
      React.useEffect(() => {
        const interval = setInterval(() => {
          setUpdateTrigger(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
      }, []);

      const serverTime = (exerciseRuntime?.paused && exerciseRuntime?.paused_at) ? parseTimestamp(exerciseRuntime.paused_at) : new Date();

      const isImmediate = item.meta?.actionCardName === 'Immediate action card';

      const computedTasks = isImmediate
        ? computeTasksWithState(tasks, item.meta?.notifiedAt || item.timestamp, serverTime)
        : tasks;

      // Get incident report time - handle both Date objects and strings
      const incidentReportedAt = incident.created_at instanceof Date ? incident.created_at : parseTimestamp(incident.created_at);
      const notifiedAt = item.meta?.notifiedAt ? (item.meta.notifiedAt instanceof Date ? item.meta.notifiedAt : parseTimestamp(item.meta.notifiedAt)) : null;

      // Task completion progress
      const completedTasks = tasks.filter(t => t.isCompleted).length;
      const totalTasks = tasks.length;
      const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      const isPerformed = performanceStatus === 'Performed';

      // Resource cost rollup: allocated to THIS action card versus explicitly recorded usage.
      const cardResourceCost = (() => {
        // Per-card resource money, USED vs ALLOCATED:
        //  - allocated: cost of everything linked to this card (whole-card
        //    links + per-task links + legacy allocate-field assignments).
        //  - consumed: only explicit resource usage records. Completing a
        //    task or card never assumes that every linked unit was used.
        const resourceById = {};
        (allTimelineItems || []).forEach((res) => {
          if (!res || res.group !== RESOURCE_GROUP || res.is_active === false) return;
          const m = res.meta?.resource || {};
          resourceById[res.id] = {
            unitCost: resourceNum(m.cost),
            qty: resourceNum(m.quantity),
            availability: m.availability || 'once',
            usageEvents: Array.isArray(m.usageEvents) ? m.usageEvents : [],
            legacyHere: (m.assigneeType === 'card' && String(m.assigneeId) === String(item.id)) ||
              (m.assigneeType === 'task' && String(m.assigneeParentId || '') === String(item.id)),
          };
        });
        let allocated = 0;
        let consumed = 0;
        const collect = (lr, taskId = null) => {
          const r = resourceById[lr?.resourceId];
          if (!r) return;
          const cost = resourceNum(lr.quantity) * r.unitCost;
          allocated += cost;
          const matchingEvents = r.usageEvents.filter((event) => (
            String(event?.actionCardId || '') === String(item.id) &&
            String(event?.taskId || '') === String(taskId || '') &&
            !event?.reversedAt &&
            String(event?.status || '').toLowerCase() !== 'reversed'
          ));
          if (matchingEvents.length > 0) {
            consumed += matchingEvents.reduce(
              (sum, event) => sum + resourceNum(event?.quantity) * resourceNum(event?.unitCost),
              0
            );
          }
        };
        (item?.meta?.linkedResources || []).forEach((lr) => collect(lr, null));
        (item?.meta?.tasks || []).forEach((t) =>
          (t?.linkedResources || []).forEach((lr) => collect(lr, t?.taskId || t?.id)));
        // Legacy direct allocations still contribute to the allocated budget,
        // but completion alone does not create usage.
        Object.values(resourceById).forEach((r) => {
          if (r.legacyHere) {
            const cost = r.qty * r.unitCost;
            allocated += cost;
            consumed += r.usageEvents
              .filter((event) => (
                String(event?.actionCardId || '') === String(item.id) &&
                !event?.reversedAt &&
                String(event?.status || '').toLowerCase() !== 'reversed'
              ))
              .reduce(
                (sum, event) => sum + resourceNum(event?.quantity) * resourceNum(event?.unitCost),
                0
              );
          }
        });
        return { allocated, consumed };
      })();
      const resourceUsageOverAllocation = cardResourceCost.consumed > cardResourceCost.allocated;
      const liveResourceName = (link) => {
        const resourceItem = (allTimelineItems || []).find((row) => row?.group === RESOURCE_GROUP && String(row?.id) === String(link?.resourceId));
        return resourceItem?.meta?.resource?.category || resourceItem?.title || link?.category || 'Resource';
      };

      // NOTE: previously this silently auto-saved expired tasks to the DB and dispatched
      // 'timelineRefresh' on every tick, which made the timeline refresh in the background
      // (especially noticeable when opening a handover modal). The expired/failed state is
      // already computed live for display via computeTasksWithState, so no background write
      // is needed. Persistence happens when the user saves through the handover modal.
      const hasPendingTasks = totalTasks > 0 && completedTasks < totalTasks;

      const getResponseStatusChip = () => {
        if (isSuperseded) {
          return { text: 'Superseded', cls: 'bg-gray-100 text-gray-600' };
        }
        if (!isPerformed) {
          return { text: 'Pending', cls: 'bg-yellow-100 text-yellow-800' };
        }

        const notifiedAtDate = notifiedAt || null;
        const performedAtDate = item.meta?.performedAt
          ? (item.meta.performedAt instanceof Date ? item.meta.performedAt : parseTimestamp(item.meta.performedAt))
          : null;
        const scheduledAt = item.meta?.scheduledAt || slaData.scheduledDeadline;
        const slaState = computeScheduledActionSlaFromMeta(
          {
            notifiedAt: item.meta?.notifiedAt,
            withinMinutes: slaData.withinMinutes,
            scheduledAt,
          },
          performedAtDate || serverTime
        );
        const deadline = slaState?.deadline || (
          notifiedAtDate && Number.isFinite(Number(slaData.withinMinutes))
            ? new Date(notifiedAtDate.getTime() + Number(slaData.withinMinutes) * 60 * 1000)
            : null
        );
        if (notifiedAtDate && performedAtDate && deadline && performedAtDate.getTime() > deadline.getTime()) {
          const lateMinutes = Math.max(
            1,
            Math.round((performedAtDate.getTime() - deadline.getTime()) / 60000)
          );
          return { text: `Done (late ${lateMinutes}m)`, cls: 'bg-orange-100 text-orange-800' };
        }
        if (hasPendingTasks) {
          return { text: 'Uncompleted', cls: 'bg-red-100 text-red-800' };
        }
        return { text: 'Completed', cls: 'bg-green-100 text-green-800' };
      };
      const responseStatusChip = getResponseStatusChip();

      const normalizeSuggestedTask = (suggestion, idx) => {
        const toText = (value) => {
          if (typeof value === 'string') return value;
          if (typeof value === 'number') return String(value);
          if (value && typeof value === 'object') {
            return (
              value.userName ||
              value.name ||
              value.full_name ||
              value.email ||
              value.taskText ||
              value.text ||
              ''
            );
          }
          return '';
        };

        if (!suggestion || typeof suggestion !== 'object') {
          return {
            id: `suggestion-${idx}`,
            text: '',
            status: 'pending',
            suggestedBy: 'Unknown',
            suggestedAt: null
          };
        }

        return {
          ...suggestion,
          id: suggestion.id || suggestion.suggestionId || `suggestion-${idx}`,
          text: toText(suggestion.text) || toText(suggestion.taskText) || toText(suggestion.title) || '',
          status: String(
            suggestion.status ||
            suggestion.approvalStatus ||
            suggestion.suggestionStatus ||
            'pending'
          ).toLowerCase(),
          suggestedBy:
            toText(suggestion.suggestedBy) ||
            toText(suggestion.userName) ||
            toText(suggestion.actorName) ||
            toText(suggestion.suggested_by) ||
            'Unknown',
          suggestedById: suggestion.suggestedById || suggestion.userId || suggestion.actorId || null,
          suggestedAt: suggestion.suggestedAt || suggestion.createdAt || suggestion.timestamp || null
        };
      };

      const pendingSuggestions = (() => {
        const rawCandidates = (Array.isArray(suggestedTasks) ? suggestedTasks : [])
          .map((s, idx) => normalizeSuggestedTask(s, idx));

        // Find all unique keys that have been explicitly approved or rejected
        const resolvedKeys = new Set();
        rawCandidates.forEach(s => {
          const status = String(s.status || '').toLowerCase();
          if (status === 'approved' || status === 'rejected') {
            const idKey = String(s.id || '').trim().toLowerCase();
            const textKey = String(s.text || '').trim().toLowerCase();
            if (idKey) resolvedKeys.add(idKey);
            if (textKey) resolvedKeys.add(textKey);
          }
        });

        return rawCandidates
          .filter((s) => {
            const status = String(s.status || '').toLowerCase();
            const idKey = String(s.id || '').trim().toLowerCase();
            const textKey = String(s.text || '').trim().toLowerCase();

            // If this task was approved/rejected anywhere, it's no longer pending
            if (resolvedKeys.has(idKey) || resolvedKeys.has(textKey)) {
              return false;
            }

            return s.text && (
              status === 'pending' ||
              status === 'pending_approval' ||
              status === 'awaiting_approval' ||
              status === 'suggested'
            );
          })
          .filter((s, idx, arr) => {
            const idKey = String(s.id || '').trim().toLowerCase();
            const textKey = String(s.text || '').trim().toLowerCase();
            const uniqueKey = idKey || textKey;
            if (!uniqueKey) return true;
            return arr.findIndex((x) => {
              const xId = String(x.id || '').trim().toLowerCase();
              const xText = String(x.text || '').trim().toLowerCase();
              const xKey = xId || xText;
              return xKey === uniqueKey;
            }) === idx;
          });
      })();

      const processTaskSuggestion = async (suggestion, decision) => {
        if (!suggestion?.id) return;
        setProcessingSuggestionId(suggestion.id);
        try {
          const currentMeta = item.meta || {};
          const existingSuggestions = Array.isArray(currentMeta.suggestedTasks)
            ? currentMeta.suggestedTasks
            : (Array.isArray(currentMeta.taskSuggestions) ? currentMeta.taskSuggestions : []);
          const existingTasks = Array.isArray(currentMeta.tasks) ? currentMeta.tasks : [];
          const currentUser = getCurrentUserFromStorage();
          const reviewerName = currentUser?.name || currentUser?.full_name || currentUser?.email || 'Admin';

          const updatedSuggestions = existingSuggestions.map((s, idx) => {
            const normalized = normalizeSuggestedTask(s, idx);
            if (normalized.id !== suggestion.id) return s;
            return {
              ...s,
              id: normalized.id,
              status: decision,
              reviewedBy: reviewerName,
              reviewedAt: new Date().toISOString()
            };
          });

          let updatedTasks = existingTasks;
          if (decision === 'approved') {
            const exists = existingTasks.some((t) => {
              const tText = String(t?.taskText || '').trim().toLowerCase();
              return tText && tText === String(suggestion.text || '').trim().toLowerCase();
            });

            if (!exists) {
              updatedTasks = [
                ...existingTasks,
                {
                  taskId: `task_${Date.now()}`,
                  taskText: suggestion.text,
                  order: existingTasks.length + 1,
                  isCompleted: false,
                  completedBy: null,
                  completedAt: null,
                  notes: '',
                  notApplicable: false,
                  source: 'suggested',
                  approvalStatus: 'approved'
                }
              ];
            } else {
              updatedTasks = existingTasks.map((t) => {
                const tText = String(t?.taskText || '').trim().toLowerCase();
                if (tText && tText === String(suggestion.text || '').trim().toLowerCase()) {
                  return { ...t, approvalStatus: 'approved' };
                }
                return t;
              });
            }
          } else if (decision === 'rejected') {
            updatedTasks = existingTasks.map((t) => {
              const tText = String(t?.taskText || '').trim().toLowerCase();
              if (tText && tText === String(suggestion.text || '').trim().toLowerCase()) {
                return { ...t, approvalStatus: 'rejected' };
              }
              return t;
            });
          }

          const payload = {
            ...item,
            meta: {
              ...currentMeta,
              tasks: updatedTasks,
              suggestedTasks: updatedSuggestions,
              taskSuggestions: updatedSuggestions
            }
          };

          const res = await CommanServices.updatePutIncidentTimeline(incident.id, item.id, payload);
          if (!res.status) throw new Error('Failed to update suggested task');

          fetchTimelineData();
          window.dispatchEvent(new Event('timelineRefresh'));
        } catch (error) {
          console.error('Error processing task suggestion:', error);
          alert('Failed to process suggested task. Please try again.');
        } finally {
          setProcessingSuggestionId(null);
        }
      };

      return (
        <div className="relative pl-6 pb-6 border-l-2 border-l-gray-300 ml-3">

          {/* Response Action Card */}
          <div className="timeline-card-focus-surface relative bg-white rounded-lg border border-gray-200 shadow-sm">
            <CardCornerActions item={item} incident={incident} currentUser={currentUser} onTakeover={onTakeover} withTakeover={!isSuperseded && !isPerformed} isAdmin={isAdmin} />
            {/* Card Header */}
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">{actionCardName}</span>
                    {simActivated && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${responseStatusChip.cls}`}>
                        {responseStatusChip.text}
                      </span>
                    )}
                    {/* Resource spend badge: USED / ALLOCATED for this card. It turns
                        red when recorded usage exceeds the allocation. */}
                    {cardResourceCost.allocated > 0 && (
                      <span
                        data-testid={`card-resource-cost-${item.id}`}
                        title={resourceUsageOverAllocation
                          ? `Resource usage exceeds allocation by SAR ${resourceMoney(cardResourceCost.consumed - cardResourceCost.allocated)}`
                          : 'Resource cost used / allocated to this action card'}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${resourceUsageOverAllocation
                          ? 'bg-red-50 text-red-700 border-red-300'
                          : cardResourceCost.consumed > 0
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                      >
                        <img
                          src={SAUDI_RIYAL_MARK}
                          alt="Saudi Riyal"
                          className="h-3 w-3 shrink-0 object-contain mix-blend-multiply"
                        />
                        {resourceMoney(cardResourceCost.consumed)} / {resourceMoney(cardResourceCost.allocated)}
                        <span className="ml-0.5 text-[9px] font-semibold uppercase tracking-wide opacity-70">
                          used / allocated
                        </span>
                      </span>
                    )}
                  </div>

                  {/* Assignment Summary (Master card) */}
                  <div className="mt-2 text-sm text-gray-600">
                    {showRoleChip ? (
                      <RoleChips roles={cardRoles} note={item?.meta?.assignmentNote} />
                    ) : cardAssignedByRole(item).length > 0 ? null : (
                    <>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Assigned To: {assignedLabel}</span>
                      {item.meta?.assignedUserIds?.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setManualRecipientsExpanded((v) => !v)}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 shrink-0"
                          aria-expanded={manualRecipientsExpanded}
                        >
                          <span>{manualRecipientsExpanded ? 'Collapse' : 'Expand'}</span>
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${manualRecipientsExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      )}
                    </div>
                    {manualRecipientsExpanded && item.meta?.assignedUserIds?.length > 1 && (
                      <ul className="mt-2 pl-3 border-l-2 border-gray-200 space-y-1 max-h-40 overflow-y-auto">
                        {item.meta.assignedUserIds.map((uid) => (
                          <li key={uid} className="text-xs text-gray-700">
                            {manualNotifyUserNames[uid] || uid}
                          </li>
                        ))}
                      </ul>
                    )}
                    </>
                    )}
                  </div>
                </div>

                {/* Header right actions moved to top-right corner via CardCornerActions */}
              </div>

              {/* Fire time countdown → then Notified at (activation + offset) */}
              {simActivated && notifiedAt && (
                <FireTimeCountdown item={item} incident={incident} paused={exerciseRuntime?.paused} pausedAt={exerciseRuntime?.paused_at} />
              )}
              <CardAssignedPeople item={item} incident={incident} />

              {/* SLA Info */}
              {!isImmediate && (item.meta?.scheduledAt || slaData.scheduledDeadline) && (
                <div className="mt-2 text-xs text-gray-500">
                  <span className="font-medium">Scheduled:</span>{' '}
                  {formatTimeLocal(parseTimestamp(item.meta?.scheduledAt || slaData.scheduledDeadline))}
                  {' '}({parseTimestamp(item.meta?.scheduledAt || slaData.scheduledDeadline).toLocaleDateString()})
                </div>
              )}
              {simActivated && !isImmediate && slaData.withinMinutes != null && !(item.meta?.scheduledAt || slaData.scheduledDeadline) && (
                <div className="mt-2 text-xs text-gray-500">
                  <span className="font-medium">SLA:</span> {slaData.withinMinutes} minute{slaData.withinMinutes !== 1 ? 's' : ''}{assignedUserCount > 1 ? ' each' : ''}
                </div>
              )}

              {/* Countdown Timer for pending cards (dormant until exercise active) */}
              {!isImmediate && !isPerformed && !isSuperseded && (() => {
                // Phase 1b: exercise response-action clocks start at activation + fire offset
                const raClockRunning = !incident?.is_exercise || String(incident?.status || '').toLowerCase() === 'active';
                const raOffset = item?.meta?.fireOffsetMinutes;
                const raHasOffset = Number.isFinite(Number(raOffset));
                const raActivatedAt = incident?.activated_at ? parseTimestamp(incident.activated_at) : null;
                if (incident?.is_exercise && !raClockRunning) {
                  return (
                    <div className="mt-3"><div className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100">
                      <Clock className="h-4 w-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">⏳ Starts on activation{raHasOffset ? ` · fires T+${raOffset}m` : ''}{slaData.withinMinutes != null ? ` · SLA ${slaData.withinMinutes}m` : ''}</span>
                    </div></div>
                  );
                }
                if (incident?.is_exercise && raActivatedAt && raHasOffset) {
                  const fireTime = new Date(raActivatedAt.getTime() + Number(raOffset) * 60000);
                  if (fireTime > serverTime) {
                    const untilFire = fireTime - serverTime;
                    const fm = Math.floor(untilFire / 60000);
                    const fs = Math.floor((untilFire % 60000) / 1000);
                    return (
                      <div className="mt-3"><div className="flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-50">
                        <Clock className="h-4 w-4 text-indigo-600" />
                        <span className="text-sm font-medium text-indigo-800">⏳ Fires in {fm}m {fs}s{slaData.withinMinutes != null ? ` · then SLA ${slaData.withinMinutes}m` : ''}</span>
                      </div></div>
                    );
                  }
                  if (slaData.withinMinutes == null) return null;
                  const slaMinutes = slaData.withinMinutes;
                  const slaDeadline = new Date(fireTime.getTime() + slaMinutes * 60 * 1000);
                  const timeRemaining = slaDeadline - serverTime;
                  const minutesRemaining = Math.floor(timeRemaining / 60000);
                  const secondsRemaining = Math.floor((timeRemaining % 60000) / 1000);
                  const isOverdue = timeRemaining < 0;
                  const isWarning = timeRemaining < 120000 && timeRemaining > 0;
                  return (
                    <div className="mt-3 space-y-2">
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${isOverdue ? 'bg-red-100' : isWarning ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                        <Clock className={`h-4 w-4 ${isOverdue ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-blue-600'}`} />
                        <span className={`text-sm font-medium ${isOverdue ? 'text-red-800' : isWarning ? 'text-yellow-800' : 'text-blue-800'}`}>
                          {isOverdue ? `⚠️ Overdue by ${Math.abs(minutesRemaining)}m ${Math.abs(secondsRemaining)}s` : `⏱️ ${minutesRemaining}m ${secondsRemaining}s remaining`}
                        </span>
                      </div>
                    </div>
                  );
                }
                const scheduledAt = item.meta?.scheduledAt || slaData.scheduledDeadline;
                const slaState = computeScheduledActionSlaFromMeta(
                  {
                    notifiedAt: item.meta?.notifiedAt,
                    withinMinutes: slaData.withinMinutes,
                    scheduledAt,
                  },
                  serverTime
                );

                if (!slaState) {
                  if (!notifiedAt || slaData.withinMinutes == null) return null;
                  const slaMinutes = slaData.withinMinutes;
                  const slaDeadline = new Date(notifiedAt.getTime() + slaMinutes * 60 * 1000);
                  const timeRemaining = slaDeadline - serverTime;
                  const minutesRemaining = Math.floor(timeRemaining / 60000);
                  const secondsRemaining = Math.floor((timeRemaining % 60000) / 1000);
                  const isOverdue = timeRemaining < 0;
                  const isWarning = timeRemaining < 120000 && timeRemaining > 0;

                  return (
                    <div className="mt-3 space-y-2">
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${isOverdue ? 'bg-red-100' : isWarning ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                        <Clock className={`h-4 w-4 ${isOverdue ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-blue-600'}`} />
                        <span className={`text-sm font-medium ${isOverdue ? 'text-red-800' : isWarning ? 'text-yellow-800' : 'text-blue-800'}`}>
                          {isOverdue
                            ? `⚠️ Overdue by ${Math.abs(minutesRemaining)}m ${Math.abs(secondsRemaining)}s`
                            : `⏱️ ${minutesRemaining}m ${secondsRemaining}s remaining`}
                        </span>
                      </div>
                    </div>
                  );
                }

                const { isOverdue, minutesRemaining, secondsRemaining, diffMs } = slaState;
                const isWarning = diffMs < 120000 && diffMs > 0;

                return (
                  <div className="mt-3 space-y-2">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${isOverdue ? 'bg-red-100' : isWarning ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                      <Clock className={`h-4 w-4 ${isOverdue ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-blue-600'}`} />
                      <span className={`text-sm font-medium ${isOverdue ? 'text-red-800' : isWarning ? 'text-yellow-800' : 'text-blue-800'}`}>
                        {isOverdue
                          ? `⚠️ Overdue by ${minutesRemaining}m ${secondsRemaining}s`
                          : `⏱️ ${minutesRemaining}m ${secondsRemaining}s remaining`}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Resources linked to the WHOLE action card — shown right under the card name */}
            {(item?.meta?.linkedResources || []).length > 0 && (
              <div className="px-4 pt-3 flex flex-wrap items-center gap-1.5" data-testid={`card-resource-chips-${item.id}`}>
                <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Card resources:</span>
                {item.meta.linkedResources.map((lr) => (
                  <span key={lr.resourceId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    <Package className="h-3 w-3" /> {liveResourceName(lr)} ×{lr.quantity}
                  </span>
                ))}
              </div>
            )}

            {/* Card Body - Tasks */}
            {tasks.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Tasks Progress</span>
                  <span className="text-sm font-medium text-gray-900">{completedTasks} / {totalTasks} completed</span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>

                {/* Task List */}
                <div className="space-y-3.5 max-h-60 overflow-y-auto pr-1">
                  {computedTasks.map((task, idx) => {
                    const isTaskCompleted = task.isCompleted;
                    const isTaskFailed = task.state === 'failed' || task.failedSLA;
                    const isTaskActive = task.state === 'active';

                    return (
                      <div key={task.taskId || idx} className="flex flex-col gap-1.5 text-sm border-b border-slate-100 pb-2.5 last:border-0 last:pb-0">
                        <div className="flex items-start gap-2">
                          {isTaskCompleted ? (
                            <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                          ) : isTaskFailed ? (
                            <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">✗</span>
                          ) : (
                            <span className="text-slate-300 font-bold flex-shrink-0 mt-0.5">○</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`
                              ${isTaskCompleted ? 'line-through text-slate-400' : ''} 
                              ${isTaskFailed ? 'text-slate-400 line-through' : 'text-slate-700'} 
                              ${isTaskActive ? 'font-semibold text-slate-900' : ''}
                            `}>
                              {task.taskText}
                            </span>

                            {/* Resources linked to THIS task */}
                            {(task.linkedResources || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1" data-testid={`task-resource-chips-${item.id}-${idx}`}>
                                {task.linkedResources.map((lr) => (
                                  <span key={lr.resourceId} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    <Package className="h-2.5 w-2.5" /> {liveResourceName(lr)} ×{lr.quantity}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Completion Details */}
                            {isTaskCompleted && task.completedBy && (
                              <div className="text-xs text-slate-400 mt-0.5 italic">
                                Completed by {task.completedBy} {task.completedAt && `at ${formatTimeLocal(task.completedAt)}`}
                              </div>
                            )}

                            {/* Failed State Indicator */}
                            {isTaskFailed && (
                              <div className="text-xs text-red-500 mt-0.5 font-medium">
                                Timer Expired (Missed SLA)
                              </div>
                            )}
                          </div>
                          {canAuthorConditional && onStartConditional && (
                            <button
                              type="button"
                              data-testid={`add-conditional-task-${item.id}-${idx}`}
                              onClick={(e) => { e.stopPropagation(); onStartConditional(item, idx); }}
                              title="Add conditional follow-up for this task"
                              className="flex-shrink-0 inline-flex items-center gap-1 rounded-full border border-dashed border-amber-300 bg-amber-50/60 px-2 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                            >
                              <Zap className="h-3 w-3" /> Conditional
                            </button>
                          )}
                        </div>

                        {/* Timers for Immediate Cards */}
                        {isImmediate && task.timerMinutes && (
                          <div className="pl-6">
                            {isTaskCompleted ? (
                              <span className="inline-flex items-center text-[10px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100/50">
                                ⏱️ {task.timerMinutes}m timer (Completed)
                              </span>
                            ) : isTaskFailed ? (
                              <span className="inline-flex items-center text-[10px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded border border-red-100/50">
                                ⏱️ {task.timerMinutes}m timer (Expired)
                              </span>
                            ) : isTaskActive ? (() => {
                              const remainingMs = task.remainingMs;
                              const isOverdue = remainingMs < 0;

                              // Calculate MM:SS countdown format
                              const totalSecs = Math.floor(Math.abs(remainingMs) / 1000);
                              const mins = String(Math.floor(totalSecs / 60)).padStart(2, '0');
                              const secs = String(totalSecs % 60).padStart(2, '0');
                              const formatted = `${mins}:${secs}`;

                              return (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border ${isOverdue
                                    ? 'bg-rose-50 text-rose-700 border-rose-100 animate-pulse'
                                    : 'bg-amber-50 text-amber-700 border-amber-100'
                                  }`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${isOverdue ? 'bg-rose-500' : 'bg-amber-500'} animate-ping`} />
                                  ⏱️ {isOverdue ? `Overdue: -${formatted}` : `Active: ${formatted}`}
                                </span>
                              );
                            })() : (
                              <span className="inline-flex items-center text-[10px] font-medium text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                ⏱️ {task.timerMinutes}m timer (queued)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Suggested Tasks (from users) */}
            {pendingSuggestions.length > 0 && (
              <div className="p-4 border-b border-gray-100 bg-amber-50/40">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Suggested Tasks</span>
                  <span className="text-xs text-gray-500">{pendingSuggestions.length} pending</span>
                </div>

                <div className="space-y-2">
                  {pendingSuggestions.map((suggestion) => (
                    <div key={suggestion.id} className="rounded border border-amber-200 bg-white p-3">
                      <div className="text-sm text-gray-800">{suggestion.text}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        Suggested by {suggestion.suggestedBy}
                        {suggestion.suggestedAt ? ` • ${new Date(suggestion.suggestedAt).toLocaleString()}` : ''}
                      </div>

                      {canModerateSuggestions && !isSuperseded && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            disabled={processingSuggestionId === suggestion.id}
                            onClick={() => processTaskSuggestion(suggestion, 'approved')}
                            className="px-2.5 py-1.5 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={processingSuggestionId === suggestion.id}
                            onClick={() => processTaskSuggestion(suggestion, 'rejected')}
                            className="px-2.5 py-1.5 text-xs font-medium rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-60"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {item.meta?.notes && (
              <div className="p-4 border-b border-gray-100">
                <div className="text-xs font-medium text-gray-600 mb-1">Notes:</div>
                <div className="text-sm text-gray-700">{item.meta.notes}</div>
              </div>
            )}

            {/* Supersede Message */}


            <div className="px-4 pb-3">
              <ScenarioRoleChips item={item} incident={incident} />
              <ExerciseCardControls item={item} onEdit={onEdit} incident={incident} canEdit={canEditExerciseTimeline || leaderCanEditPendingCard(item)} creatorMode={leaderCanEditPendingCard(item)} onRefresh={fetchTimelineData} allItems={allTimelineItems} />
            </div>
          </div>
        </div>
      );
    }

    // Call Log Card Layout
    const isCallLog = item.group === 'call_log' || group?.key === 'call_log' || item.meta?.is_call_log;
    if (isCallLog) {
      const meta = item.meta || {};
      const statusType = meta.customStatusType || 'Call Log';
      const timestamp = parseTimestamp(item.timestamp);
      const isMissedCall = item.type === 'call_missed';
      const initiator = item.actor?.name || 'System';

      return (
        <div className="relative pl-6 pb-6 border-l-2 border-l-indigo-300 ml-3">
          <div className={`timeline-card-focus-surface bg-white rounded-lg border shadow-sm ${isMissedCall ? 'border-red-200' : 'border-indigo-100'}`}>
            <div className={`p-3 rounded-t-lg border-b ${isMissedCall ? 'bg-red-50 border-red-100' : 'bg-indigo-50/50 border-indigo-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isMissedCall ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'}`}>
                    {statusType}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">
                    {timestamp.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  <span>Log</span>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isMissedCall ? 'bg-red-100' : 'bg-indigo-100'}`}>
                  <Video className={`w-4 h-4 ${isMissedCall ? 'text-red-600' : 'text-indigo-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none mb-2">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {item.details}
                    </ReactMarkdown>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 text-[10px] font-semibold rounded uppercase tracking-wider">
                      Audio/Video Call
                    </span>
                    {meta.duration && meta.duration !== 'N/A' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-[11px] rounded-md border border-slate-200">
                        <Clock className="w-3 h-3" />
                        <span className="font-semibold">Duration:</span> {meta.duration}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Enhanced Status Update Card Layout
    const isStatusUpdate = (item.group === 'status_update' || item.group === 'status_update_2' || group?.key === 'status_update') && !item.meta?.is_call_log;
    if (isStatusUpdate) {
      const meta = item.meta || {};

      const statusType = meta.is_call_log ? 'Call Log' : (meta.customStatusType || meta.statusType || 'Update');
      const statusText = meta.statusText || item.details || '';

      // Handle impactedAreas - can be string, array, or undefined
      let impactedAreas = [];
      if (Array.isArray(meta.impactedAreas)) {
        impactedAreas = meta.impactedAreas;
      } else if (typeof meta.impactedAreas === 'string' && meta.impactedAreas) {
        impactedAreas = [meta.impactedAreas];
      }

      const resourcesRequested = Array.isArray(meta.resourcesRequested) ? meta.resourcesRequested : [];
      const attachments = Array.isArray(item.attachments) ? item.attachments : [];
      const updatedBy = item.actor || {};
      const timestamp = parseTimestamp(item.timestamp);

      // Resolve location names from the shared app-wide cache so the "Impacted Areas / Departments"
      // chips render their readable name immediately (no UUID → name flicker, no per-card refetch).
      const [locationNames, setLocationNames] = React.useState(() => ({ ...locationNamesCache }));

      React.useEffect(() => {
        if (impactedAreas.length === 0) return;
        const allResolved = impactedAreas.every((lid) => locationNamesCache[lid]);
        if (allResolved) {
          setLocationNames({ ...locationNamesCache });
          return;
        }
        let cancelled = false;
        ensureLocationNames().then(() => {
          if (!cancelled) setLocationNames({ ...locationNamesCache });
        });
        return () => { cancelled = true; };
      }, [impactedAreas.join(',')]);

      // Get status type color
      const getStatusColor = (type) => {
        const colors = {
          'In Progress': 'bg-blue-100 text-blue-800',
          'Contained': 'bg-green-100 text-green-800',
          'Escalating': 'bg-red-100 text-red-800',
          'Awaiting Resources': 'bg-yellow-100 text-yellow-800',
          'Resolved': 'bg-green-100 text-green-800',
          'Call Log': 'bg-purple-100 text-purple-800'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
      };

      const isMissedCall = item.type === 'call_missed';

      return (
        <div className={`relative pl-6 ${isMissedCall ? 'pb-2' : 'pb-6'} border-l-2 border-l-gray-300 ml-3`}>

          {/* Status Update Card */}
          <div className={`timeline-card-focus-surface relative bg-white rounded-lg border ${isMissedCall ? 'border-red-500 shadow-sm' : 'border-gray-200 shadow-sm'}`}>
            <CardCornerActions item={item} incident={incident} currentUser={currentUser} onTakeover={onTakeover} withTakeover={!isMissedCall && !meta.is_call_log} isAdmin={isAdmin} />
            {/* Card Header */}
            <div className={`p-4 border-b border-gray-100 ${isMissedCall ? 'bg-red-50 py-2' : 'bg-gradient-to-r from-orange-50 to-yellow-50'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {!isMissedCall && !simActivated ? (
                    <div className="space-y-1.5 mb-1">
                      {showRoleChip ? (
                        <RoleChips roles={cardRoles} note={item?.meta?.assignmentNote} />
                      ) : (
                        <div className="text-sm text-gray-600"><span className="font-medium">{updatedBy.name || 'Unknown'}</span></div>
                      )}
                      <div>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          ⏳ Starts on activation{Number.isFinite(Number(item?.meta?.fireOffsetMinutes)) ? ` · fires T+${item.meta.fireOffsetMinutes}m` : ''}
                        </span>
                      </div>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${getStatusColor(statusType)}`}>
                          {statusType}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Fire time countdown → then "Notified at" (activation + offset), same as Verifications/Notifications */}
                      {simActivated && !isMissedCall && (
                        <FireTimeCountdown item={item} incident={incident} paused={exerciseRuntime?.paused} pausedAt={exerciseRuntime?.paused_at} />
                      )}
                      {!isMissedCall && (
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${getStatusColor(statusType)}`}>
                            {statusType}
                          </span>
                        </div>
                      )}

                      {/* Updated By + assigned personnel (carried from Requested stage) */}
                      <div className={`text-sm ${isMissedCall ? 'text-red-700' : 'text-gray-600'}`}>
                        {(() => {
                          const nm = updatedBy.name;
                          const showNm = nm && !['Scenario Planner', 'Unknown', 'System', 'Scenario'].includes(nm) && cardAssignedByRole(item).length === 0;
                          return (
                            <>
                              {showNm && <span className="font-medium">{nm}</span>}
                            </>
                          );
                        })()}
                      </div>
                      <CardAssignedPeople item={item} incident={incident} />
                    </>
                  )}
                </div>

                <div className="flex items-center mr-9">
                  {/* Resolve button only for missed calls (delete removed for status updates) */}
                  {isAdmin && !meta.is_call_log && isMissedCall && (
                    <button
                      className="ml-2 text-red-400 hover:text-green-600"
                      title="Resolve Missed Call"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm('Mark this missed call as resolved?')) {
                          try {
                            const response = await CommanServices.deleteTimelineItem(incident.id, item.id);
                            if (response.status) {
                              console.log('✅ Item removed successfully');
                            }
                          } catch (err) {
                            console.error('❌ Failed to remove item:', err);
                          }
                        }
                      }}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Card Body - Status Description */}
            <div className={`p-4 ${isMissedCall ? 'bg-red-50/30' : 'border-b border-gray-100'}`}>
              <div className={`text-sm ${isMissedCall ? 'text-red-900 font-medium' : 'text-gray-700'} prose prose-sm max-w-none`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {statusText}
                </ReactMarkdown>
              </div>
            </div>

            {/* Hide Replies for missed calls and immutable call logs */}
            {!isMissedCall && !meta.is_call_log && (
              <StatusUpdateReplies
                incidentId={incident.id}
                timelineItemId={item.id}
                replies={meta.replies}
                readOnly={isIncidentReadOnly}
              />
            )}

            {/* Impacted Areas */}
            {impactedAreas.length > 0 && (
              <div className="p-4 border-b border-gray-100 bg-blue-50">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-blue-900 mb-2">Impacted Areas / Departments</div>
                    <div className="flex flex-wrap gap-2">
                      {impactedAreas.map((locationId, idx) => {
                        // Show the readable name; while unresolved show a neutral label (never the raw UUID).
                        const displayName = locationNames[locationId] || locationNamesCache[locationId]
                          || (String(locationId).length > 20 ? 'Resolving…' : locationId);
                        return (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                          >
                            {displayName}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Resources Requested */}
            {resourcesRequested.length > 0 && (
              <div className="p-4 border-b border-gray-100 bg-amber-50">
                <div className="flex items-start gap-2">
                  <Package className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-amber-900 mb-2">Resources Requested</div>
                    <div className="flex flex-wrap gap-2">
                      {resourcesRequested.map((resource, idx) => {
                        const displayText = typeof resource === 'string' ? resource : (resource.name || resource.label || 'Resource');
                        return (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                          >
                            {displayText}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Request Input Department */}
            {statusType === 'Request Input' && meta.requestInputDepartment && (
              <div className="p-4 border-b border-gray-100 bg-green-50">
                <div className="flex items-start gap-2">
                  <Building className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-green-900 mb-2">Department</div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      {meta.requestInputDepartment.department_name}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Related Incident */}
            {meta.relatedIncident && meta.relatedIncident.type_id && (() => {
              // Get readable names from meta if available
              const typeName = meta.relatedIncident.type_name || meta.relatedIncident.type_id;
              const eventName = meta.relatedIncident.event_name || meta.relatedIncident.event_id;
              const triggerName = meta.relatedIncident.trigger_name || meta.relatedIncident.trigger_id;
              const locationDisplay = meta.relatedIncident.location_name || meta.relatedIncident.location;
              const incidentId = meta.relatedIncident.incident_id || meta.relatedIncidentId;

              return (
                <div className="p-4 border-b border-gray-100 bg-purple-50">
                  <div className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-purple-900 mb-2">Related Incident</div>
                      {incidentId && (
                        <div className="mb-2">
                          <button
                            onClick={() => navigate(`/incident-details/${incidentId}`)}
                            className="text-sm font-semibold text-purple-700 hover:text-purple-900 hover:underline cursor-pointer"
                          >
                            Incident ID: {incidentId}
                          </button>
                        </div>
                      )}
                      <div className="space-y-1.5 text-xs text-purple-800">
                        <div><span className="font-medium">Type:</span> {typeName}</div>
                        {eventName && eventName !== 'none' && (
                          <div><span className="font-medium">Event:</span> {eventName}</div>
                        )}
                        {triggerName && triggerName !== 'none' && (
                          <div><span className="font-medium">Trigger:</span> {triggerName}</div>
                        )}
                        {locationDisplay && (
                          <div><span className="font-medium">Location:</span> {locationDisplay}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start gap-2">
                  <Upload className="h-4 w-4 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Attachments ({attachments.length})</div>
                    <div className="space-y-2">
                      {attachments.map((attachment, idx) => {
                        const filename = attachment.filename || attachment.name || 'Unknown file';
                        const url = attachment.url || attachment.file_url || '#';
                        const size = attachment.size || 0;

                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Upload className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  let fileUrl = url;
                                  if (fileUrl && fileUrl.startsWith('/')) {
                                    fileUrl = `${ApiEndPoint}${fileUrl}`;
                                  }
                                  setAttachmentPreview({
                                    type: attachment.type || attachment.content_type,
                                    name: filename,
                                    size: size,
                                    src: fileUrl,
                                    url: fileUrl,
                                  });
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate cursor-pointer"
                              >
                                {filename}
                              </span>
                              {size > 0 && (
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  ({Math.round(size / 1024)}KB)
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}


            {!meta.is_call_log && !isMissedCall && (
              <div className="px-4 pb-3">
                <ScenarioRoleChips item={item} incident={incident} />
            <ExerciseCardControls item={item} onEdit={onEdit} incident={incident} canEdit={canEditExerciseTimeline || leaderCanEditPendingCard(item)} creatorMode={leaderCanEditPendingCard(item)} onRefresh={fetchTimelineData} allItems={allTimelineItems} />
              </div>
            )}
          </div>
          {attachmentPreview && (
            <AttachmentPreviewModal attachment={attachmentPreview} onClose={() => setAttachmentPreview(null)} />
          )}
        </div>
      );
    }

    // Enhanced Resolution Card Layout
    const isResolution = item.group === 'resolution' || group?.key === 'resolution';
    if (isResolution) {
      const meta = item.meta || {};
      const resolutionStatus = meta.customResolutionStatus || meta.resolutionStatus || 'Resolved';
      const resolutionSummary = meta.resolutionSummary || item.details || '';
      const rootCause = meta.rootCause || '';
      const actionsTaken = meta.actionsTaken || '';
      const lessonsLearned = meta.lessonsLearned || '';
      const afterActionReview = meta.afterActionReview || '';
      const finalSeverity = meta.finalSeverity || '';
      const timeToResolution = meta.timeToResolution || '';
      const relatedIncidents = meta.relatedIncidents || '';
      const attachments = Array.isArray(item.attachments) ? item.attachments : [];
      const resolvedBy = item.actor || {};
      const timestamp = parseTimestamp(item.timestamp);

      // Get resolution status color
      const getResolutionColor = (status) => {
        const colors = {
          'Resolved Successfully': 'bg-green-100 text-green-800 border-green-300',
          'False Alarm': 'bg-blue-100 text-blue-800 border-blue-300',
          'Duplicate Incident': 'bg-purple-100 text-purple-800 border-purple-300',
          'Escalated to External Agency': 'bg-orange-100 text-orange-800 border-orange-300',
          'Cannot Resolve': 'bg-red-100 text-red-800 border-red-300'
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
      };

      // Get severity color
      const getSeverityColor = (severity) => {
        const colors = {
          'Critical': 'bg-red-200 text-red-900',
          'High': 'bg-red-100 text-red-800',
          'Medium': 'bg-orange-100 text-orange-800',
          'Low': 'bg-yellow-100 text-yellow-800'
        };
        return colors[severity] || 'bg-gray-100 text-gray-800';
      };

      return (
        <div className="relative pl-6 pb-6 border-l-2 border-l-gray-300 ml-3">

          {/* Resolution Card */}
          <div className="timeline-card-focus-surface bg-white rounded-lg border border-gray-200 shadow-md">
            {/* Card Header - Resolution Status */}
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${getResolutionColor(resolutionStatus)}`}>
                      {resolutionStatus}
                    </span>
                    {timeToResolution && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                        ⏱️ {timeToResolution}
                      </span>
                    )}
                    {finalSeverity && (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getSeverityColor(finalSeverity)}`}>
                        Final: {finalSeverity}
                      </span>
                    )}
                  </div>

                  {/* Resolved By */}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">{resolvedBy.name || 'Unknown'}</span>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="text-gray-500">{timestamp.toLocaleString()}</span>
                  </div>
                </div>

                {/* Header right actions */}
                <div className="flex items-center gap-2 ml-2">
                  {incident?.is_exercise && !incident?.__planVirtual && (
                    <CardDiscussion incident={incident} itemId={item.id} currentUser={currentUser} alwaysVisible addedLive={item?.meta?.addedDuringActive === true} />
                  )}
                  {/* Edit Button */}
                  {canResolveIncident && (
                    <button
                      className="text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingResolution(item);
                        setShowResolutionModal(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Card Body - Resolution Summary */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-start gap-2 mb-2">
                <FileText className="h-4 w-4 text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs font-semibold text-gray-700">Resolution Summary</div>
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap ml-6">
                {resolutionSummary}
              </div>
            </div>

            {/* Root Cause Analysis */}
            {rootCause && (
              <div className="p-4 border-b border-gray-100 bg-red-50">
                <div className="flex items-start gap-2 mb-2">
                  <Target className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs font-semibold text-red-900">Root Cause Analysis</div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap ml-6">
                  {rootCause}
                </div>
              </div>
            )}

            {/* Actions Taken */}
            {actionsTaken && (
              <div className="p-4 border-b border-gray-100 bg-blue-50">
                <div className="flex items-start gap-2 mb-2">
                  <ClipboardList className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs font-semibold text-blue-900">Actions Taken</div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap ml-6">
                  {actionsTaken}
                </div>
              </div>
            )}

            {/* Lessons Learned */}
            {lessonsLearned && (
              <div className="p-4 border-b border-gray-100 bg-yellow-50">
                <div className="flex items-start gap-2 mb-2">
                  <Lightbulb className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs font-semibold text-yellow-900">Lessons Learned</div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap ml-6">
                  {lessonsLearned}
                </div>
              </div>
            )}

            {/* After Action Review (AAR) */}
            {afterActionReview && (
              <details className="p-4 border-b border-gray-100 bg-violet-50" data-testid="resolution-aar">
                <summary className="cursor-pointer text-xs font-semibold text-violet-900 flex items-center gap-2">
                  🏁 After Action Review
                </summary>
                <div className="text-xs text-gray-700 whitespace-pre-wrap font-mono mt-2 ml-1 max-h-96 overflow-y-auto bg-white border border-violet-100 rounded p-3">
                  {afterActionReview}
                </div>
              </details>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start gap-2">
                  <Upload className="h-4 w-4 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Attachments ({attachments.length})</div>
                    <div className="space-y-2 ml-6">
                      {attachments.map((attachment, idx) => {
                        const filename = attachment.filename || attachment.name || 'Unknown file';
                        const url = attachment.url || attachment.file_url || '#';
                        const size = attachment.size || 0;

                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    await downloadAuthenticatedMedia(url, filename);
                                  } catch (error) {
                                    console.error('Attachment download failed', error);
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate cursor-pointer"
                              >
                                {filename}
                              </span>
                              {size > 0 && (
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  ({Math.round(size / 1024)}KB)
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      );
    }

    // Standard timeline card for other groups
    return (
      <div className={`relative pl-6 pb-6 border-l-2 ${groupColor} ml-3`}>

        {/* Timeline card */}
        <div className="timeline-card-focus-surface relative bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow group">
          {/* Header row - only for non-notification groups */}
          {(item.group !== 'notification' && group?.key !== 'notification') && (() => {
            const headerTime = (simActivated && cardFireTime(item, incident)) || timestamp;
            const showActor = actorName && !['Scenario Planner', 'Unknown', 'System', 'Scenario'].includes(actorName) && cardAssignedByRole(item).length === 0;
            return (
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-mono text-gray-500">[{formatTime(headerTime)} · {headerTime.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}]</span>
                {showActor && <span className="text-sm font-semibold text-gray-900">{actorName}</span>}
              </div>
              <div className="flex items-center gap-1">
                {!incident?.__planVirtual && <TakeoverButton item={item} incident={incident} onAct={onTakeover} canManage={isAdmin} />}
                {incident?.is_exercise && !incident?.__planVirtual && (
                  <CardDiscussion incident={incident} itemId={item.id} currentUser={currentUser} alwaysVisible addedLive={item?.meta?.addedDuringActive === true} />
                )}
              </div>
            </div>
            );
          })()}

          {/* Assigned personnel (carried from the Requested stage) for non-notification cards */}
          {(item.group !== 'notification' && group?.key !== 'notification') && (
            <CardAssignedPeople item={item} incident={incident} />
          )}

          {/* Discussion & Approval for notification cards */}
          {(item.group === 'notification' || group?.key === 'notification') && incident?.is_exercise && !incident?.__planVirtual && (
            <div className="absolute top-3 right-3 flex items-center gap-1 z-0">
              <TakeoverButton item={item} incident={incident} onAct={onTakeover} canManage={isAdmin} />
              <CardDiscussion incident={incident} itemId={item.id} currentUser={currentUser} alwaysVisible addedLive={item?.meta?.addedDuringActive === true} />
            </div>
          )}

          {/* Edit button for notification group - positioned absolutely */}
          {/* {(item.group === 'notification' || group?.key === 'notification') && onEdit && (
            <div className="absolute top-4 right-4">
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 hover:bg-gray-100 rounded"
                onClick={() => onEdit(item)}
                title="Edit notification"
              >
                <Pencil className="h-3 w-3 text-gray-500" />
              </Button>
            </div>
          )} */}

          {/* Title - Enhanced format for notification group */}
          {(item.group === 'notification' || group?.key === 'notification') ? (
            <div className="mb-3 pr-16">
              {/* Fire time countdown → then Notified at (activation + offset) */}
              {simActivated && !item.meta?.offDuty && (
                <FireTimeCountdown item={item} incident={incident} paused={exerciseRuntime?.paused} pausedAt={exerciseRuntime?.paused_at} />
              )}
              {/* User / department / crew name (role chip while planning; hidden once real people are assigned) */}
              {showRoleChip ? (
                <div className="mb-1"><RoleChips roles={cardRoles} note={item?.meta?.assignmentNote} /></div>
              ) : cardAssignedByRole(item).length > 0 ? null : isGroupedBroadcast ? (
                <div className="mb-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900">
                      {notificationDisplayName}
                    </div>
                    {(item.meta?.recipientIds?.length > 0) && (
                      <button
                        type="button"
                        onClick={() => setManualRecipientsExpanded((v) => !v)}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 shrink-0"
                        aria-expanded={manualRecipientsExpanded}
                      >
                        <span>{manualRecipientsExpanded ? 'Collapse' : 'Expand'}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${manualRecipientsExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                    )}
                  </div>
                  {manualRecipientsExpanded && (item.meta?.recipientIds?.length > 0) && (
                    <ul className="mt-2 pl-3 border-l-2 border-gray-200 space-y-1 max-h-40 overflow-y-auto">
                      {item.meta.recipientIds.map((uid) => (
                        <li key={uid} className="text-xs text-gray-700">
                          {manualNotifyUserNames[uid] || uid}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="text-sm font-medium text-gray-900 mb-1">
                  {notificationDisplayName}
                </div>
              )}
              <CardAssignedPeople item={item} incident={incident} />
              {!isGroupedBroadcast && (() => {
                const m = item.meta || {};
                const source = String(m.source || '').toLowerCase();
                const isDeptLeader =
                  m.isDepartmentLeader === true ||
                  m.is_leader === true ||
                  String(m.position || '').toLowerCase() === 'leader' ||
                  source.includes('department_leader') ||
                  source.includes('role_leader') ||
                  String(item.actor?.role || '').toLowerCase().includes('department leader');
                if (!isDeptLeader) return null;
                const deptName = m.department_name || m.department || m.departmentName || 'department';
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200 mb-1">
                    Department Leader of {deptName}
                  </span>
                );
              })()}
              {/* Crew and team on separate line */}
              {(() => {
                const crewName = item.meta?.crewName || item.meta?.crew_name;
                const teamName = item.meta?.teamName || item.meta?.team_name;
                if (crewName || teamName) {
                  return (
                    <div className="text-xs text-gray-600">
                      {[crewName, teamName].filter(Boolean).join(' • ')}
                    </div>
                  );
                }
                return null;
              })()}

            </div>
          ) : (
            <div className="text-sm font-semibold text-gray-800 mb-1">
              {item.title}
            </div>
          )}

          {/* Notification Status for notification group */}
          {(item.group === 'notification' || group?.key === 'notification') && (
            <div className="space-y-2 mb-3">
              {/* Notification Status */}
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-700">Notification status:</span>
                {item.meta?.offDuty ? (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                      ⚪ Skipped
                    </span>
                    {item.meta?.forced && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-300">
                        🔵 Force Notification
                      </span>
                    )}
                  </>
                ) : (
                  simActivated ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
                      🟢 Notified
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300" title="Recipients will be notified when the simulation is activated">
                      ⚪ Will notify on activation
                    </span>
                  )
                )}
              </div>

              {/* Off duty note */}
              {item.meta?.offDuty && (
                <div className="text-xs text-gray-500 italic">
                  {notificationDisplayName} is off duty now.
                </div>
              )}
            </div>
          )}

          {/* Acknowledgment Status for notification group - show if not off duty OR if forced */}
          {(item.group === 'notification' || group?.key === 'notification') && (!item.meta?.offDuty || item.meta?.forced) && (
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-xs font-medium text-gray-700">Acknowledgment status:</span>
              <NotificationAckStatus item={item} incident={incident} simActivated={simActivated} paused={exerciseRuntime?.paused} pausedAt={exerciseRuntime?.paused_at} />
            </div>
          )}

          {/* Details - Skip for notification group */}
          {(item.group !== 'notification' && group?.key !== 'notification') && (
            <div className="text-sm text-gray-600 mb-3 leading-relaxed">
              {item.details}
            </div>
          )}

          {/* Chips */}
          {(() => {
            const isNotif = item.group === 'notification' || group?.key === 'notification';
            let chips = [...(item.chips || [])];
            if (isNotif) {
              // Notification cards: drop the noisy "Manual"/"Admin" source chips
              chips = chips.filter((c) => !['Manual', 'Admin'].includes(c));
            } else if ((isManualUserNotification || isManualDeptCrewNotification) && !chips.includes('Admin')) {
              chips.push('Admin');
            }
            if (!chips.length) return null;
            return (
              <div className="flex flex-wrap gap-1 mb-2">
                {chips.map((chip, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            );
          })()}

          {/* Attachments */}
          {item.attachments && item.attachments.length > 0 && (
            <div className="flex items-center text-xs text-gray-500">
              <Paperclip className="h-3 w-3 mr-1" />
              {item.attachments.length} attachment{item.attachments.length > 1 ? 's' : ''}
            </div>
          )}

          <ScenarioRoleChips item={item} incident={incident} />
          <ExerciseCardControls item={item} onEdit={onEdit} incident={incident} canEdit={canEditExerciseTimeline || leaderCanEditPendingCard(item)} creatorMode={leaderCanEditPendingCard(item)} onRefresh={fetchTimelineData} allItems={allTimelineItems} />
        </div>
      </div>
    );
  };

  // TimelineItemImpl is re-created on every IncidentTimeline render (it closes
  // over live component scope). Rendering it directly would hand React a NEW
  // component type each render — unmounting and remounting every card. The
  // page re-renders every second (live clocks), so cards remounted each
  // second: hover styles restarted ("cards move while hovering") and the
  // "Fires at T+" input lost focus/state before a value could be typed.
  // Keep ONE stable component identity and delegate to the latest
  // implementation through a ref — closures stay fresh, identity stays fixed.
  const timelineItemImplRef = React.useRef(TimelineItemImpl);
  timelineItemImplRef.current = TimelineItemImpl;
  const [TimelineItem] = React.useState(() => {
    const Stable = (props) => timelineItemImplRef.current(props);
    Stable.displayName = 'TimelineItem';
    return Stable;
  });

  const fetchLocations = async () => {
    try {
      const response = await CommanServices.getlocationszonesdata();
      if (response.status) {
        const data = await response.data;
        setLocations(data || []);
        console.log('✅ LocationSelector: Fetched', (data.locations || []).length, 'locations with caching');
      }

    } catch (err) {
      console.error('❌ LocationSelector: Error fetching locations:', err);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [])

  // Only the very first load shows the spinner. Refreshes update in place so
  // the page never collapses/jumps — an emergency timeline must hold its
  // position (generalized: real incidents and the exercise inject manager).
  if (loading && (!timelineData || timelineData.length === 0)) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-500">Loading timeline...</span>
      </div>
    );
  }

  const getTimelineGroupItems = (group) => {
    let items = groupedItems[group.key] || [];

    if (group.key === 'acknowledgment') {
      items = [...items].sort((a, b) => {
        const aWithin = a.meta?.sla?.withinMinutes ?? 999;
        const bWithin = b.meta?.sla?.withinMinutes ?? 999;
        return aWithin - bWithin;
      });
    }

    if (group.key === 'notification') {
      items = expandManualUserNotificationItems([...items], manualNotifyUserNames);
      items = items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }

    if (group.key === 'status_update') {
      items = [...items].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }

    if (filter !== 'all' || normalizedTimelineSearchQuery) {
      items = items.filter((item) => timelineItemTreeMatchesView(item, group));
    }

    return items;
  };

  const timelineGroupHasSearchMatches = (group) => {
    if (!normalizedTimelineSearchQuery) return true;
    return getTimelineGroupItems(group).length > 0;
  };

  const selectedFilterHasAncestorContext = filter !== 'all'
    && primaryTimelineGroups
      .some((group) => group.key !== filter && getTimelineGroupItems(group).length > 0);

  const timelineGroupMatchesSelectedFilter = (group) => {
    if (filter === 'all') return true;
    const itemCount = getTimelineGroupItems(group).length;
    if (itemCount > 0) return true;
    return group.key === filter && !selectedFilterHasAncestorContext;
  };

  const visiblePrimaryTimelineGroups = primaryTimelineGroups
    .filter(timelineGroupMatchesSelectedFilter)
    .filter(timelineGroupHasSearchMatches);
  const visibleCallLogGroups = timelineGroups
    .filter((group) => group.key === 'call_log' && (filter === 'all' || filter === 'call_log'))
    .filter(timelineGroupHasSearchMatches);
  const showTimelineSearchEmptyState = Boolean(normalizedTimelineSearchQuery)
    && visiblePrimaryTimelineGroups.length === 0
    && visibleCallLogGroups.length === 0;
  const timelineGroupExpandedForView = (groupKey) => Boolean(
    expandedGroups[groupKey] || filter !== 'all' || normalizedTimelineSearchQuery
  );
  const timelineItemIsFocused = (itemId) => {
    const current = String(focusedItemId || '');
    const candidate = String(itemId || '');
    return !!current && candidate === current;
  };
  const timelineItemFocusClass = (itemId) => {
    if (!timelineItemIsFocused(itemId)) return '';
    return `timeline-card-focus-pulse timeline-card-focus-pulse-${focusPulseRevision % 2 ? 'a' : 'b'}`;
  };

  // Renders a source inject card, then (recursively) any conditional follow-up cards
  // linked to it — nested, indented, with a dashed amber "conditional" frame.
  const renderInjectNode = (item, group, groupColor, isLatest) => {
    const allChildren = conditionalChildrenBySourceId[item.id] || [];
    const children = allChildren.filter((child) => (
      timelineItemTreeMatchesView(child, groupByKey(child.group) || group)
    ));
    return (
      <React.Fragment key={item.id}>
        <div
          id={`inject-${item.id}`}
          data-testid={`timeline-focus-target-${item.id}`}
          data-focus-highlight={timelineItemIsFocused(item.id) ? 'true' : 'false'}
          className={timelineItemFocusClass(item.id)}
        >
          <TimelineItem
            item={item}
            incident={incident}
            groupColor={groupColor}
            group={group}
            isLatest={isLatest}
            allTimelineItems={effectiveTimelineData}
            onEdit={handleCardEdit}
            onTakeover={handleTakeover}
          />
        </div>

        {/* Nested conditional follow-up cards */}
        {children.length > 0 && (() => {
          const collapsed = !!collapsedConditionals[item.id]
            && filter === 'all'
            && !normalizedTimelineSearchQuery;
          return (
            <div className="ml-6 mb-2">
              <button
                type="button"
                data-testid={`toggle-conditionals-${item.id}`}
                onClick={() => setCollapsedConditionals((p) => ({ ...p, [item.id]: !p[item.id] }))}
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 hover:bg-amber-200 px-3 py-1 text-[11px] font-bold text-amber-800 transition-colors mb-2"
              >
                <Zap className="h-3.5 w-3.5" />
                {collapsed ? 'Show' : 'Hide'} {children.length} conditional follow-up{children.length > 1 ? 's' : ''}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
              </button>
              {!collapsed && (
                <div className="pl-3 border-l-2 border-dashed border-amber-300 space-y-1">
                  {children.map((child) => {
                    const childGroup = groupByKey(child.group) || group;
                    if (conditionalSuppressed(child, effectiveTimelineData, incident)) {
                      return (
                        <div
                          key={child.id}
                          data-testid={`conditional-suppressed-${child.id}`}
                          className="ml-3 mb-1 flex items-center gap-2 rounded-md bg-gray-100 border border-gray-200 px-3 py-2 text-[11px] font-medium text-gray-500"
                        >
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                          <span>Not triggered — source completed in time ({conditionalChipText(child.meta?.conditional)})</span>
                        </div>
                      );
                    }
                    return (
                      <div key={child.id} className="relative">
                        <div className="flex items-center gap-1.5 ml-3 mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                          <Zap className="h-3.5 w-3.5" />
                          <span data-testid={`conditional-chip-${child.id}`}>
                            {conditionalChipText(child.meta?.conditional)}
                          </span>
                        </div>
                        {renderInjectNode(child, childGroup, childGroup?.color || groupColor, false)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </React.Fragment>
    );
  };

  const renderTimelineGroupSection = (group) => {
    const items = getTimelineGroupItems(group);
    const requiresScopedCreateAccess = ['acknowledgment', 'notification', 'response_action', 'status_update'].includes(group.key);
    const requiresAdminCreateAccess = ['resource'].includes(group.key);

    return (
      <React.Fragment key={group.key}>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
            <div
              className="flex items-center space-x-2 flex-1 cursor-pointer"
              onClick={() => toggleGroup(group.key)}
            >
              <span className="text-2xl">{group.icon}</span>
              <h4 className="text-sm font-semibold text-gray-800">{group.title}</h4>
              <SectionTooltip content={group.tooltip} />
              <span className="text-xs text-gray-500">({items.length})</span>
            </div>

            <div className="flex items-center space-x-2">
              {group.key !== 'report' && group.key !== 'call_log' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-gray-200 rounded"
                  data-testid={`timeline-group-add-${group.key}`}
                  disabled={
                    isIncidentReadOnly ||
                    (group.key === 'resolution' && !canResolveIncident) ||
                    (requiresScopedCreateAccess && !canCreateRestrictedTimelineActions) ||
                    (requiresAdminCreateAccess && !isAdmin)
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isIncidentReadOnly) return;
                    if (group.key === 'resolution' && !canResolveIncident) return;
                    if (
                      requiresScopedCreateAccess &&
                      !canCreateRestrictedTimelineActions
                    ) {
                      return;
                    }
                    if (requiresAdminCreateAccess && !isAdmin) return;
                    if (group.key === 'acknowledgment') {
                      setShowAcknowledgmentModal(true);
                    } else if (group.key === 'response_action') {
                      setShowResponseActionModal(true);
                    } else if (group.key === 'status_update') {
                      setStatusUpdateGroup(group.key);
                      setShowStatusUpdateModal(true);
                    } else if (group.key === 'resolution') {
                      setShowResolutionModal(true);
                    } else if (group.key === 'resource') {
                      openAddResource();
                    } else if (group.key === 'notification') {
                      setShowManualNotificationModal(true);
                    } else {
                      setActiveGroup(group.key);
                      setShowTimelineModal(true);
                    }
                  }}
                  title={
                    isIncidentReadOnly
                      ? 'Incident is resolved and read-only'
                      : group.key === 'resolution' && !canResolveIncident
                      ? 'Your assigned role cannot resolve this incident'
                      : requiresScopedCreateAccess &&
                      !canCreateRestrictedTimelineActions
                      ? 'Only administrators and incident-scoped leaders can create this entry'
                      : requiresAdminCreateAccess && !isAdmin
                      ? 'Only administrators can create this entry'
                      : `Add new ${group.title} entry`
                  }
                >
                  <Plus className="h-3 w-3 text-gray-600" />
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-gray-200 rounded"
                onClick={() => toggleGroup(group.key)}
              >
                <ChevronDown className={`h-3 w-3 text-gray-600 transition-transform ${timelineGroupExpandedForView(group.key) ? 'rotate-180' : ''
                  }`} />
              </Button>
            </div>
          </div>

          {timelineGroupExpandedForView(group.key) && (
            <div className="ml-2">
              {group.key === 'notification' && notificationMerged && items.length > 0 ? (
                ['user', 'department', 'crew']
                  .map((b) => ({ b, list: items.filter((it) => notificationBucket(it) === b) }))
                  .filter(({ list }) => list.length > 0)
                  .map(({ b, list }) => {
                    const open = !!expandedMergeBuckets[b];
                    return (
                      <div key={b} className="mb-3">
                        <button
                          data-testid={`notification-merge-${b}`}
                          onClick={() => toggleMergeBucket(b)}
                          className="w-full flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 hover:bg-amber-100 transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                            <span className="text-base">🔔</span>
                            {list.length} {NOTIFICATION_BUCKET_LABEL[b]} notification{list.length > 1 ? 's' : ''}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-amber-600 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                        {open && (
                          <div className="mt-2 pl-2 border-l-2 border-amber-100">
                            {list.map((item, index) => (
                              <div
                                key={item.id}
                                id={`inject-${item.id}`}
                                data-testid={`timeline-focus-target-${item.id}`}
                                data-focus-highlight={timelineItemIsFocused(item.id) ? 'true' : 'false'}
                                className={timelineItemFocusClass(item.id)}
                              >
                              <TimelineItem
                                item={item}
                                incident={incident}
                                groupColor={group.color}
                                group={group}
                                isLatest={index === list.length - 1}
                                allTimelineItems={effectiveTimelineData}
                                onEdit={handleCardEdit}
                                onTakeover={handleTakeover}
                              />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
              ) : items.length > 0 ? (
                items.map((item, index) =>
                  renderInjectNode(item, group, group.color, index === items.length - 1)
                )
              ) : (
                <div className="text-sm text-gray-500 italic py-4 pl-6">
                  No {group.title.toLowerCase()} entries
                </div>
              )}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className="h-full">
      {/* Sticky Header with Controls */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <h3 className="text-lg font-semibold text-gray-900">{incident?.is_exercise || incident?.is_scenario ? 'Injector Manager' : 'Incident Tracking Timeline'}</h3>
          </div>
          {pendingResponderActivity.activity && (
            <button
              type="button"
              data-testid="timeline-new-activity"
              onClick={focusPendingResponderActivity}
              title={[
                pendingResponderActivity.activity.actor?.name,
                pendingResponderActivity.activity.summary,
              ].filter(Boolean).join(' · ') || 'Focus the newest app-user activity'}
              className="inline-flex max-w-[260px] items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-left text-[11px] font-bold text-red-700 shadow-sm transition hover:bg-red-100"
            >
              <Bell className="h-3.5 w-3.5 shrink-0 animate-pulse" />
              <span className="truncate">
                {pendingResponderActivity.count > 1 ? `${pendingResponderActivity.count} new activities` : 'New activity'}
              </span>
            </button>
          )}
        </div>

        {staffingStatus && staffingStatus.total > 0 && (
          staffingStatus.unassigned > 0 ? (
            <div
              data-testid="staffing-banner-unassigned"
              className="mb-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              {staffingStatus.unassigned} of {staffingStatus.total} inject{staffingStatus.total > 1 ? 's' : ''} still need real personnel assigned to their roles before activation.
            </div>
          ) : (
            <div
              data-testid="staffing-banner-complete"
              className="mb-3 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800"
            >
              <CheckCircle className="h-4 w-4 shrink-0" />
              All {staffingStatus.total} role-based inject{staffingStatus.total > 1 ? 's are' : ' is'} fully staffed.
            </div>
          )
        )}

        {/* Controls Row */}
        <div className="flex items-center space-x-3">
          <Select value={filter} onValueChange={(value) => setFilter(value)}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="timeline-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {timelineFilterOptions.map((group) => (
                <SelectItem key={group.key} value={group.key}>{group.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
            <Input
              placeholder="Search timeline..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-7 pr-3 py-1 h-8 text-xs border-gray-300 rounded"
              data-testid="timeline-search"
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="text-xs px-2 h-8"
            onClick={() => {
              // Toggle all groups
              const allExpanded = Object.values(expandedGroups).every(v => v);
              const newState = Object.keys(expandedGroups).reduce((acc, key) => ({
                ...acc,
                [key]: !allExpanded
              }), {});
              setExpandedGroups(newState);
              localStorage.setItem(`timeline-groups-${incident.id}`, JSON.stringify(newState));
            }}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Timeline Groups */}
      <div className="p-4 space-y-6">
        {showTimelineSearchEmptyState && (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
            No timeline entries match “{searchQuery.trim()}”.
          </div>
        )}

        {visiblePrimaryTimelineGroups.map(renderTimelineGroupSection)}

      </div>

      {/* Persistent incident-readiness history sits immediately above Call Logs. */}
      <div className="mt-6 border-t border-gray-200 pt-6 px-6 pb-8">
        {/* Readiness checks exist for drills & real incidents only — never in the scenario env */}
        {filter === 'all' && !normalizedTimelineSearchQuery && !incident?.is_scenario && (
          <IncidentReadinessLog incident={incident} />
        )}
        <div className="mb-6">
          {visibleCallLogGroups.map((group) => {
            const items = getTimelineGroupItems(group);

            return (
              <div key={group.key} className="space-y-2">
                {/* Group Header */}
                <div
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                >
                  <div
                    className="flex items-center space-x-2 flex-1 cursor-pointer"
                    onClick={() => toggleGroup(group.key)}
                  >
                    <span className="text-2xl">{group.icon}</span>
                    <h4 className="text-sm font-semibold text-gray-800">{group.title}</h4>
                    <SectionTooltip content={group.tooltip} />
                    <span className="text-xs text-gray-500">({items.length})</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-gray-200 rounded"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <ChevronDown className={`h-3 w-3 text-gray-600 transition-transform ${timelineGroupExpandedForView(group.key) ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                </div>

                {/* Group Items */}
                {timelineGroupExpandedForView(group.key) && (
                  <div className="ml-2">
                    {items.length > 0 ? (
                      items.map((item, index) => (
                        <div
                          key={item.id}
                          id={`inject-${item.id}`}
                          data-testid={`timeline-focus-target-${item.id}`}
                          data-focus-highlight={timelineItemIsFocused(item.id) ? 'true' : 'false'}
                          className={timelineItemFocusClass(item.id)}
                        >
                        <TimelineItem
                          item={item}
                          incident={incident}
                          groupColor={group.color}
                          group={group}
                          isLatest={index === items.length - 1}
                          allTimelineItems={effectiveTimelineData}
                          onEdit={(editItem) => {
                            setEditingItem(editItem);
                            setActiveGroup(group.key);
                            setShowTimelineModal(true);
                          }}
                        />
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 italic py-4 pl-6">
                        No {group.title.toLowerCase()} entries
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {children}
      </div>
      {showResourceModal && editingResource && (
        <ResourceFormModal
          draft={editingResource}
          entities={resourceEntities}
          incident={incident}
          onSave={handleSaveResource}
          onClose={() => { setShowResourceModal(false); setEditingResource(null); }}
        />
      )}
    </div>
  );
};

export default IncidentTimeline
